// Firebase Collections Structure
import { collection, doc, setDoc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, writeBatch, runTransaction, increment, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db, auth } from './config';
import { queryWithFallback } from '../utils/firebaseIndexes';
import { getFileDownloadURL } from './r2storage';
import { convertFileKeysToUrls, convertFileKeysToUrlsInArray } from './urlConverter';

// Map to store active listeners
const listeners = new Map();

// Utility function to generate unique IDs
const generateId = () => {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
};

export const COLLECTIONS = {
  USERS: 'users',
  TESTS: 'tests',
  QUESTIONS: 'questions',
  TEST_RESULTS: 'testResults',
  TEST_STATISTICS: 'testStatistics',
  USER_PROGRESS: 'userProgress',
  NOTES: 'notes',
  PRACTICE_SESSIONS: 'practiceSessions',
  REVISION_DATA: 'revisionData',
  EXAM_DATA: 'examData',
  ADMIN_SETTINGS: 'adminSettings',
  MOCK_TESTS: 'mockTests',
  EXAM_SUBJECTS: 'examSubjects',
  QUESTION_BANK: 'questionBank',

  DOUBTS: 'doubts',
  DOUBT_RESPONSES: 'doubtResponses',
  EXPERT_PROFILES: 'expertProfiles',
  LIVE_SESSIONS: 'liveSessions',
  SESSION_REGISTRATIONS: 'sessionRegistrations',
  USER_SESSIONS: 'userSessions',
  
  // Course Management Collections
  COURSES: 'courses',
  LECTURES: 'lectures',
  INSTRUCTORS: 'instructors',
  USER_COURSES: 'userCourses',
  COURSE_DISCUSSIONS: 'courseDiscussions',
  LECTURE_DISCUSSIONS: 'lectureDiscussions',
  COURSE_ASSIGNMENTS: 'courseAssignments',
  COURSE_SUBMISSIONS: 'courseSubmissions',
  COURSE_CERTIFICATES: 'courseCertificates',
  COURSE_MATERIALS: 'courseMaterials',
  COURSE_LIVE_CLASSES: 'courseLiveClasses',
  COURSE_NOTIFICATIONS: 'courseNotifications',
  COURSE_CONTENT: 'courseContent',
  
  // Polls and Voting System
  POLLS: 'polls',
  POLL_VOTES: 'pollVotes',
  POLL_ANALYTICS: 'pollAnalytics',
  
  // Activity Registration System
  ACTIVITY_REGISTRATIONS: 'activityRegistrations',
  ACTIVITY_PARTICIPANTS: 'activityParticipants',
  ACTIVITY_LEADERBOARDS: 'activityLeaderboards',
  
  // Vacancy and Exam Management Collections
  VACANCIES: 'vacancies',
  EXAM_RESULTS: 'examResults',
  EXAM_NOTIFICATIONS: 'examNotifications',
  APPLICATION_FORMS: 'applicationForms',

  // Resource Management Collections
  RESOURCES: 'resources',

  // Test Management Collections
  TESTS: 'tests',

  // Content Management Collections
  QUESTION_BANK: 'questionBank',

  // Live Class Management Collections
  LIVE_CLASSES: 'liveClasses',

  // Live Class Registration Collections
  LIVE_CLASS_REGISTRATIONS: 'liveClassRegistrations',

  // Bookmark Management
  BOOKMARKS: 'bookmarks',
  
  // Question Bookmark Management
  QUESTION_BOOKMARKS: 'questionBookmarks',

  // Payment Management Collections
  PAYMENTS: 'payments',
  PAYMENT_TRANSACTIONS: 'paymentTransactions',
  PAYMENT_REFUNDS: 'paymentRefunds',
  TEACHER_HIRING_PAYMENTS: 'teacherHiringPayments',
  COURSE_ENROLLMENT_PAYMENTS: 'courseEnrollmentPayments',
  COACHING_SUBSCRIPTION_PAYMENTS: 'coachingSubscriptionPayments',

  // Batch Management Collections
  BATCHES: 'batches',
  STORE_ITEMS: 'storeItems',
  STORE_ORDERS: 'storeOrders',
  BATCH_CLASSES: 'batchClasses',
  BATCH_ENROLLMENTS: 'batchEnrollments',
  BATCH_STUDY_MATERIALS: 'batchStudyMaterials',
  BATCH_ANNOUNCEMENTS: 'batchAnnouncements',
  BATCH_ASSIGNMENTS: 'batchAssignments',
  ASSIGNMENT_SUBMISSIONS: 'assignmentSubmissions',

  // Fee Management Collections
  STUDENT_FEES: 'studentFees',
  FEE_PAYMENTS: 'feePayments',
  FEE_RECEIPTS: 'feeReceipts',
  FEE_REMINDERS: 'feeReminders',

  // Report Cards and Certificates
  REPORT_CARDS: 'reportCards',
  CERTIFICATES: 'certificates',

  // Support Tickets Collection
  SUPPORT_TICKETS: 'supportTickets',
  TICKET_RESPONSES: 'ticketResponses',

  // Admissions Collections
  ADMISSIONS_INSTITUTIONS: 'admissionsInstitutions',
  ADMISSIONS_APPLICATIONS: 'admissionsApplications',
  ADMISSIONS_PAYMENTS: 'admissionsPayments',
  ADMISSIONS_CUSTOMIZATIONS: 'admissionsCustomizations',
  ADMISSIONS_CURRICULUM: 'admissionsCurriculum',
  ADMISSIONS_MATERIALS: 'admissionsMaterials',
  ADMISSIONS_FACULTY: 'admissionsFaculty',
  ADMISSIONS_FEE_STRUCTURE: 'admissionsFeeStructure',
  ADMISSIONS_RESULTS: 'admissionsResults',
  ADMISSIONS_TEST_CONFIG: 'admissionsTestConfig',
  ADMISSIONS_TEST_REGISTRATIONS: 'admissionsTestRegistrations',

  // Team Management Collections
  TEAM_MEMBERS: 'teamMembers',

  // Timetable Management Collections
  TIMETABLES: 'timetables',

  // Student Results and Admit Cards Collections
  STUDENT_RESULTS: 'studentResults',
  STUDENT_ADMIT_CARDS: 'studentAdmitCards',

  // Batch Chat Collections
  BATCH_CHAT_MESSAGES: 'batchChatMessages',

  // Batch Syllabus Collection
  BATCH_SYLLABUS: 'batchSyllabus',

  // Admin Permissions Management
  ADMIN_PERMISSIONS: 'adminPermissions',
  COACHING_ADMIN_PERMISSIONS: 'coachingAdminPermissions',
  ADMIN_LOGS: 'adminActions',

  // Reviews Collection
  REVIEWS: 'reviews',

  // ID Card Management Collections
  ID_CARDS: 'idCards',

  // Blog Management Collections
  BLOGS: 'blogs'
};

// User Management Functions
export const createUserProfile = async (userData) => {
  try {
    console.log('📝 Creating user profile:', {
      uid: userData.uid,
      email: userData.email,
      role: userData.role,
      userType: userData.userType
    });
    
    await setDoc(doc(db, COLLECTIONS.USERS, userData.uid), {
      ...userData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    console.log('✅ User profile created successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error creating user profile:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    return { success: false, error };
  }
};

export const getUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: users };
  } catch (error) {
    return { success: false, error };
  }
};

export const addUser = async (userData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.USERS), {
      ...userData,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateUser = async (userId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      ...updateData,
      updatedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteUser = async (userId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.USERS, userId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserProfile = async (userId) => {
  try {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const dataWithUrls = await convertFileKeysToUrls(data);
      return { success: true, data: dataWithUrls };
    } else {
      return { success: false, error: 'User not found' };
    }
  } catch (error) {
    // Error getting user profile
    return { success: false, error };
  }
};

export const updateUserProfile = async (userId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    // Error updating user profile
    return { success: false, error };
  }
};

export const deleteUserProfile = async (userId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.USERS, userId));
    return { success: true };
  } catch (error) {
    // Error deleting user profile
    return { success: false, error };
  }
};

// Deep recursive function to remove all undefined values from objects and arrays
const removeUndefinedValues = (obj) => {
  if (obj === undefined) {
    return undefined; // Will be filtered out
  }
  
  if (obj === null) {
    return null; // Keep null values as they are valid
  }
  
  if (Array.isArray(obj)) {
    return obj
      .map(item => removeUndefinedValues(item))
      .filter(item => item !== undefined); // Only remove undefined, keep null
  }
  
  if (typeof obj === 'object' && obj.constructor === Object) {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        const cleanedValue = removeUndefinedValues(value);
        // Only add if the cleaned value is not undefined
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue;
        }
      }
    }
    return cleaned;
  }
  
  return obj;
};

// Test Results Management
export const saveTestResult = async (testResultData) => {
  try {
    // Deep clean: Remove all undefined values recursively from nested objects and arrays
    const cleanedData = removeUndefinedValues(testResultData);

    const testResult = {
      ...cleanedData,
      createdAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Final check: Ensure no undefined values remain at top level
    const finalCleanData = Object.fromEntries(
      Object.entries(testResult).filter(([key, value]) => value !== undefined)
    );

    // Save to main testResults collection
    const docRef = await addDoc(collection(db, COLLECTIONS.TEST_RESULTS), finalCleanData);
    
    // Also save to user's subcollection for consistency
    if (testResultData.userId) {
      try {
        const subcollectionData = removeUndefinedValues({
          ...finalCleanData,
          mainResultId: docRef.id // Reference to main collection
        });
        await addDoc(collection(db, 'users', testResultData.userId, 'testResults'), subcollectionData);
      } catch (subcollectionError) {
        // Failed to save to user subcollection
        // Don't fail the main save if subcollection save fails
      }
    }

    // Test result saved successfully
    return { success: true, id: docRef.id };
  } catch (error) {
    // Error saving test result
    return { success: false, error: error.message };
  }
};

export const getUserTestResults = async (userId, limitCount = 10) => {
  try {
    // Remove orderBy to avoid requiring a composite index
    const q = query(
      collection(db, COLLECTIONS.TEST_RESULTS),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const results = [];
    querySnapshot.forEach((doc) => {
      results.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort in memory by completion date (newest first) and limit
    results.sort((a, b) => {
      const aTime = new Date(a.completedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.completedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });
    
    return { success: true, data: results.slice(0, limitCount) };
  } catch (error) {
    // Error getting user test results
    return { success: false, error };
  }
};

// Get user courses
export const getUserCourses = async (userId, limitCount = 10) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.USER_COURSES),
      where('userId', '==', userId),
      orderBy('enrolledAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const courses = [];
    querySnapshot.forEach((doc) => {
      courses.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: courses };
  } catch (error) {
    // Error getting user courses
    return { success: false, error };
  }
};

// Get user activity
export const getUserActivity = async (userId, limitCount = 10) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.USER_PROGRESS),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const activities = [];
    querySnapshot.forEach((doc) => {
      activities.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: activities };
  } catch (error) {
    // Error getting user activity
    return { success: false, error };
  }
};

// Get a single test result by document ID
export const getTestResultById = async (resultId) => {
  try {
    const docRef = doc(db, COLLECTIONS.TEST_RESULTS, resultId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: 'Test result not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Get all test results for a specific test
export const getTestResultsByTestId = async (testId) => {
  try {
    // Decode testId to handle spaces and special characters
    const decodedTestId = testId ? (() => {
      try {
        return decodeURIComponent(testId);
      } catch (e) {
        return testId; // Use as-is if already decoded
      }
    })() : testId;
    
    // Remove orderBy to avoid requiring a composite index
    const q = query(
      collection(db, COLLECTIONS.TEST_RESULTS),
      where('testId', '==', decodedTestId)
    );
    
    const querySnapshot = await getDocs(q);
    const results = [];
    querySnapshot.forEach((doc) => {
      results.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort in memory if needed (by score descending for ranking purposes)
    results.sort((a, b) => (b.score || 0) - (a.score || 0));
    
    return { success: true, data: results };
  } catch (error) {
    return { success: false, error };
  }
};

// Get test statistics
export const getTestStatistics = async (testId) => {
  try {
    const docRef = doc(db, COLLECTIONS.TEST_STATISTICS, testId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, error: 'Test statistics not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Save or update test statistics
export const saveTestStatistics = async (testId, statistics) => {
  try {
    await setDoc(doc(db, COLLECTIONS.TEST_STATISTICS, testId), {
      ...statistics,
      testId,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Store test result with comprehensive analytics
export const storeTestResultWithAnalytics = async (testResult) => {
  try {
    // Validate required fields
    if (!testResult || !testResult.testId || !testResult.userId) {
      throw new Error('Missing required fields: testId and userId are required');
    }

    // 1. Save individual test result
    const saveResult = await saveTestResult(testResult);
    
    if (!saveResult.success) {
      throw new Error(`Failed to save test result: ${saveResult.error}`);
    }

    // 2. Get all existing results for this test
    const allResultsResult = await getTestResultsByTestId(testResult.testId);
    const allResults = allResultsResult.success ? allResultsResult.data : [];

    // 3. Calculate comprehensive analytics
    const analytics = calculateComprehensiveAnalytics(allResults, testResult);

    // 4. Save analytics to test statistics collection
    const statsResult = await saveTestStatistics(testResult.testId, analytics);
    if (!statsResult.success) {
      // Failed to save test statistics, but test result was saved
    }

    return {
      success: true,
      resultId: saveResult.id,
      analytics: analytics
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Calculate comprehensive analytics for a test
const calculateComprehensiveAnalytics = (allResults, newResult) => {
  const results = [...allResults, newResult];
  
  if (results.length === 0) {
    return {
      totalAttempts: 0,
      lastUpdated: new Date(),
      basicStats: {
        averageScore: 0,
        medianScore: 0,
        highestScore: 0,
        lowestScore: 0,
        averageAccuracy: 0,
        averageTime: 0,
        averageCorrect: 0,
        averageWrong: 0,
        averageUnattempted: 0
      },
      marksDistribution: [],
      sectionalStats: [],
      percentileRanges: [],
      timeAnalysis: {
        averageTimePerQuestion: 0,
        fastestCompletion: 0,
        slowestCompletion: 0,
        timeDistribution: []
      }
    };
  }

  // Basic statistics
  const scores = results.map(r => r.score || 0);
  const accuracies = results.map(r => r.accuracy || 0);
  const times = results.map(r => r.totalTimeSpent || 0);
  const correctAnswers = results.map(r => r.correctAnswers || 0);
  const wrongAnswers = results.map(r => r.wrongAnswers || 0);
  const unattempted = results.map(r => r.unattempted || 0);

  // Calculate median
  const sortedScores = [...scores].sort((a, b) => a - b);
  const medianScore = sortedScores.length % 2 === 0 
    ? (sortedScores[sortedScores.length / 2 - 1] + sortedScores[sortedScores.length / 2]) / 2
    : sortedScores[Math.floor(sortedScores.length / 2)];

  // Basic stats
  const basicStats = {
    averageScore: Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length),
    medianScore: Math.round(medianScore),
    highestScore: Math.max(...scores),
    lowestScore: Math.min(...scores),
    averageAccuracy: Math.round(accuracies.reduce((sum, a) => sum + a, 0) / accuracies.length),
    averageTime: Math.round(times.reduce((sum, t) => sum + t, 0) / times.length),
    averageCorrect: Math.round(correctAnswers.reduce((sum, c) => sum + c, 0) / correctAnswers.length),
    averageWrong: Math.round(wrongAnswers.reduce((sum, w) => sum + w, 0) / wrongAnswers.length),
    averageUnattempted: Math.round(unattempted.reduce((sum, u) => sum + u, 0) / unattempted.length)
  };

  // Extract totalMarks from results (use the most common value or default to 100)
  const totalMarksValues = results
    .map(r => r.totalMarks)
    .filter(m => m !== undefined && m !== null);
  const totalMarks = totalMarksValues.length > 0 
    ? Math.max(...totalMarksValues) // Use the maximum total marks found
    : 100; // Default to 100 if not available

  // Marks distribution
  const marksDistribution = calculateMarksDistribution(scores, totalMarks);

  // Sectional statistics
  const sectionalStats = calculateSectionalStatistics(results);

  // Percentile ranges
  const percentileRanges = calculatePercentileRanges(scores);

  // Time analysis
  const timeAnalysis = calculateTimeAnalysis(results);

  return {
    totalAttempts: results.length,
    lastUpdated: new Date(),
    basicStats,
    marksDistribution,
    sectionalStats,
    percentileRanges,
    timeAnalysis,
    testId: newResult.testId,
    testName: newResult.testName
  };
};

// Calculate marks distribution
const calculateMarksDistribution = (scores, totalMarks = 100) => {
  if (!scores || scores.length === 0) {
    return [];
  }
  
  // Convert all scores to numbers and filter out invalid values
  // Keep original array with duplicates to count actual students
  const numericScores = scores.map(s => Number(s)).filter(s => !isNaN(s));
  
  if (numericScores.length === 0) {
    return [];
  }
  
  // Find actual min and max scores from data
  const actualMinScore = Math.min(...numericScores);
  const actualMaxScore = Math.max(...numericScores);
  const maxScore = Math.max(totalMarks, actualMaxScore);
  
  // Calculate minimum possible score (negative marks possible)
  // Estimate worst case based on total marks
  const estimatedMinScore = Math.floor(-maxScore / 5);
  const minScore = actualMinScore < 0 
    ? Math.min(estimatedMinScore, Math.floor(actualMinScore / 10) * 10 - 10)
    : estimatedMinScore;
  
  const ranges = [];
  
  // Negative ranges (2 ranges below 0) - ensure no overlap
  if (minScore < 0) {
    const negativeRangeSize = Math.max(10, Math.ceil(Math.abs(minScore) / 2));
    let negativeStart = minScore;
    for (let i = 0; i < 2; i++) {
      const start = negativeStart;
      const end = i === 1 ? -1 : Math.min(-1, start + negativeRangeSize - 1);
      if (start <= end) {
        ranges.push({
          min: start,
          max: end,
          label: `${start}-${end}`
        });
        negativeStart = end + 1;
        if (negativeStart >= 0) break;
      }
    }
  }
  
  // Positive ranges (10 ranges from 0 to maxScore) - ensure no overlap
  const positiveRangeSize = Math.max(1, Math.ceil(maxScore / 10));
  for (let i = 0; i < 10; i++) {
    const start = i * positiveRangeSize;
    const end = i === 9 ? maxScore : Math.min(maxScore, (i + 1) * positiveRangeSize - 1);
    if (start <= end) {
      ranges.push({
        min: start,
        max: end,
        label: `${start}-${end}`
      });
    }
  }

  // Count students in each range - ensure each score occurrence is counted only once
  // Use a map to track which score index has been assigned to a range
  const scoreToRangeMap = new Map();
  
  // First pass: assign each score occurrence (by index) to exactly one range (the first matching range)
  numericScores.forEach((score, index) => {
    // Only process if this score index hasn't been assigned yet
    if (!scoreToRangeMap.has(index)) {
      for (let j = 0; j < ranges.length; j++) {
        const range = ranges[j];
        if (score >= range.min && score <= range.max) {
          scoreToRangeMap.set(index, j);
          break; // Assign to first matching range only, then stop
        }
      }
    }
  });
  
  // Count students per range
  const rangeCounts = new Array(ranges.length).fill(0);
  scoreToRangeMap.forEach((rangeIndex) => {
    rangeCounts[rangeIndex]++;
  });

  return ranges.map((range, index) => ({
    range: range.label,
    students: rangeCounts[index] || 0,
    percentage: numericScores.length > 0 ? Math.round((rangeCounts[index] / numericScores.length) * 100) : 0
  }));
};

// Calculate sectional statistics
const calculateSectionalStatistics = (results) => {
  // Get all unique sections from results
  const allSections = new Set();
  results.forEach(result => {
    if (result.sectionalResults) {
      result.sectionalResults.forEach(section => {
        allSections.add(section.name);
      });
    }
  });

  return Array.from(allSections).map(sectionName => {
    const sectionResults = results
      .filter(result => result.sectionalResults)
      .map(result => result.sectionalResults.find(s => s.name === sectionName))
      .filter(Boolean);

    if (sectionResults.length === 0) return null;

    const scores = sectionResults.map(s => s.score);
    const accuracies = sectionResults.map(s => s.accuracy);
    const times = sectionResults.map(s => s.timeSpent);
    const correctAnswers = sectionResults.map(s => s.correctAnswers);
    const wrongAnswers = sectionResults.map(s => s.wrongAnswers);

    // Calculate median for section
    const sortedScores = [...scores].sort((a, b) => a - b);
    const medianScore = sortedScores.length % 2 === 0 
      ? (sortedScores[sortedScores.length / 2 - 1] + sortedScores[sortedScores.length / 2]) / 2
      : sortedScores[Math.floor(sortedScores.length / 2)];

    return {
      name: sectionName,
      totalAttempts: sectionResults.length,
      averageScore: Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length),
      medianScore: Math.round(medianScore),
      highestScore: Math.max(...scores),
      lowestScore: Math.min(...scores),
      averageAccuracy: Math.round(accuracies.reduce((sum, a) => sum + a, 0) / accuracies.length),
      averageTime: Math.round(times.reduce((sum, t) => sum + t, 0) / times.length),
      averageCorrect: Math.round(correctAnswers.reduce((sum, c) => sum + c, 0) / correctAnswers.length),
      averageWrong: Math.round(wrongAnswers.reduce((sum, w) => sum + w, 0) / wrongAnswers.length)
    };
  }).filter(Boolean);
};

// Calculate percentile ranges
const calculatePercentileRanges = (scores) => {
  const sortedScores = [...scores].sort((a, b) => a - b);
  const totalScores = sortedScores.length;

  return [
    { percentile: 10, score: sortedScores[Math.floor(totalScores * 0.1)] || 0 },
    { percentile: 25, score: sortedScores[Math.floor(totalScores * 0.25)] || 0 },
    { percentile: 50, score: sortedScores[Math.floor(totalScores * 0.5)] || 0 },
    { percentile: 75, score: sortedScores[Math.floor(totalScores * 0.75)] || 0 },
    { percentile: 90, score: sortedScores[Math.floor(totalScores * 0.9)] || 0 },
    { percentile: 93, score: sortedScores[Math.floor(totalScores * 0.93)] || 0 },
    { percentile: 95, score: sortedScores[Math.floor(totalScores * 0.95)] || 0 },
    { percentile: 99, score: sortedScores[Math.floor(totalScores * 0.99)] || 0 }
  ];
};

// Calculate time analysis
const calculateTimeAnalysis = (results) => {
  // Clean and validate time data
  const times = results.map(r => {
    let time = r.totalTimeSpent || r.timeSpent || 0;
    
    // Convert milliseconds to seconds if needed (for legacy data)
    if (time > 10000) { // If time is very large, likely in milliseconds
      time = time / 1000;
    }
    
    // Cap unrealistic times (more than 3 hours for a test)
    if (time > 3 * 60 * 60) {
      time = 3 * 60 * 60; // Cap at 3 hours
    }
    
    return time;
  }).filter(t => t > 0); // Remove zero times

  const questionTimes = results.flatMap(r => {
    if (r.detailedResults) {
      return r.detailedResults.map(dr => {
        let time = dr.timeSpent || 0;
        // Convert milliseconds to seconds if needed
        if (time > 100000) {
          time = time / 1000;
        }
        // Cap per-question time at 5 minutes
        if (time > 5 * 60) {
          time = 5 * 60;
        }
        return time;
      }).filter(t => t > 0);
    }
    return [];
  });

  // If no valid times, return default values
  if (times.length === 0) {
    return {
      averageTimePerQuestion: 0,
      fastestCompletion: 0,
      slowestCompletion: 0,
      averageCompletionTime: 0,
      timeDistribution: []
    };
  }

  const timeDistribution = [
    { range: '0-15 min', count: times.filter(t => t <= 15 * 60).length },
    { range: '15-30 min', count: times.filter(t => t > 15 * 60 && t <= 30 * 60).length },
    { range: '30-45 min', count: times.filter(t => t > 30 * 60 && t <= 45 * 60).length },
    { range: '45-60 min', count: times.filter(t => t > 45 * 60 && t <= 60 * 60).length },
    { range: '60+ min', count: times.filter(t => t > 60 * 60).length }
  ];

  const result = {
    averageTimePerQuestion: questionTimes.length > 0 ? Math.round(questionTimes.reduce((sum, t) => sum + t, 0) / questionTimes.length) : 0,
    fastestCompletion: times.length > 0 ? Math.min(...times) : 0,
    slowestCompletion: times.length > 0 ? Math.max(...times) : 0,
    averageCompletionTime: times.length > 0 ? Math.round(times.reduce((sum, t) => sum + t, 0) / times.length) : 0,
    timeDistribution
  };
  
  return result;
};

// Get user's rank and percentile for a specific test
export const getUserTestRank = async (testId, userId, currentAttemptScore = null) => {
  try {
    const allResultsResult = await getTestResultsByTestId(testId);
    if (!allResultsResult.success) {
      return { success: false, error: 'Failed to fetch test results' };
    }

    const allResults = allResultsResult.data;
    
    const userResult = allResults.find(r => r.userId === userId);
    
    if (!userResult && !currentAttemptScore) {
      return { success: false, error: 'User result not found' };
    }

    // Use current attempt's score if provided, otherwise use the found result's score
    const userScore = currentAttemptScore !== null ? currentAttemptScore : userResult.score;

    // Ensure user's score is included in the ranking calculation
    const scores = allResults.map(r => r.score);
    
    // Add user's score to the array if it's not already there (shouldn't happen, but safety check)
    if (!scores.includes(userScore)) {
      scores.push(userScore);
    }
    
    // Step 1: Sort scores in descending order (highest first)
    const sortedScores = scores.sort((a, b) => b - a);
    
    // Step 2: Create rank mapping - highest score gets rank 1, second gets rank 2, etc.
    const rankMapping = new Map();
    let currentRank = 1;
    
    for (let i = 0; i < sortedScores.length; i++) {
      const score = sortedScores[i];
      
      // If this is the first occurrence of this score, assign the current rank
      if (!rankMapping.has(score)) {
        rankMapping.set(score, currentRank);
      }
      
      // Move to next rank for the next unique score
      if (i < sortedScores.length - 1 && sortedScores[i + 1] < score) {
        currentRank = i + 2; // Next rank
      }
    }
    
    // Step 3: Get user's rank from the mapping
    const userRank = rankMapping.get(userScore);
    
    let finalRank;
    if (userRank === undefined) {
      // Count how many scores are higher than user's score
      const higherScores = sortedScores.filter(score => score > userScore);
      finalRank = higherScores.length + 1;
    } else {
      finalRank = userRank;
    }
    
    // Step 4: Calculate percentile based on the mapped rank
    const percentile = allResults.length === 1 
      ? 100 
      : Math.round(((allResults.length - finalRank) / (allResults.length - 1)) * 100);

    return {
      success: true,
      data: {
        rank: finalRank,
        totalParticipants: allResults.length,
        percentile,
        userScore,
        testId
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get comprehensive test analytics
export const getComprehensiveTestAnalytics = async (testId) => {
  try {
    // Get cached statistics first
    const statsResult = await getTestStatistics(testId);
    if (statsResult.success) {
      return statsResult;
    }

    // If no cached stats, calculate from all results
    const allResultsResult = await getTestResultsByTestId(testId);
    if (!allResultsResult.success) {
      return { success: false, error: 'Failed to fetch test results' };
    }

    const analytics = calculateComprehensiveAnalytics(allResultsResult.data, {});
    
    // Cache the calculated analytics
    await saveTestStatistics(testId, analytics);
    
    return { success: true, data: analytics };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get top performers for a test
export const getTopPerformers = async (testId, limit = 10) => {
  try {
    // Remove orderBy to avoid requiring a composite index
    const q = query(
      collection(db, COLLECTIONS.TEST_RESULTS),
      where('testId', '==', testId)
    );
    
    const querySnapshot = await getDocs(q);
    const topPerformers = [];
    querySnapshot.forEach((doc) => {
      topPerformers.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort in memory by score (highest first) and limit
    topPerformers.sort((a, b) => (b.score || 0) - (a.score || 0));
    
    return { success: true, data: topPerformers.slice(0, limit) };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get test performance comparison data
export const getTestPerformanceComparison = async (testId, userId, currentAttemptScore = null) => {
  try {
    const [analyticsResult, userRankResult, topPerformersResult] = await Promise.all([
      getComprehensiveTestAnalytics(testId),
      getUserTestRank(testId, userId, currentAttemptScore),
      getTopPerformers(testId, 5)
    ]);

    if (!analyticsResult.success) {
      return { success: false, error: 'Failed to fetch analytics' };
    }

    return {
      success: true,
      data: {
        analytics: analyticsResult.data,
        userRank: userRankResult.success ? userRankResult.data : null,
        topPerformers: topPerformersResult.success ? topPerformersResult.data : [],
        testId
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get user statistics
export const getUserStats = async (userId) => {
  try {
    // Get test results
    const testResults = await getUserTestResults(userId, 100);
    const totalTests = testResults.success ? testResults.data.length : 0;
    const averageAccuracy = testResults.success && testResults.data.length > 0 
      ? Math.round(testResults.data.reduce((sum, test) => sum + (test.percentageScore || 0), 0) / testResults.data.length)
      : 0;

    // Get courses
    const courses = await getUserCourses(userId, 100);
    const totalCourses = courses.success ? courses.data.length : 0;

    // Get notes
    const notes = await getUserNotes(userId, 100);
    const totalNotes = notes.success ? notes.data.length : 0;

    // Get doubts
    const doubts = await getUserDoubts(userId, 100);
    const totalDoubts = doubts.success ? doubts.data.length : 0;

    // Get user profile for study streak
    const userProfile = await getUserProfile(userId);
    const studyStreak = userProfile.success ? (userProfile.data.studyStreak || 0) : 0;

    // Get resources (placeholder)
    const totalResources = 0; // Resource tracking implementation pending

    return {
      success: true,
      data: {
        totalTests,
        totalCourses,
        averageAccuracy,
        studyStreak,
        totalNotes,
        totalDoubts,
        totalResources,
        recentActivity: []
      }
    };
  } catch (error) {
    // Error getting user stats
    return { success: false, error };
  }
};

// User Progress Management
export const saveUserProgress = async (progressData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.USER_PROGRESS), {
      ...progressData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserProgress = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.USER_PROGRESS),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const progress = [];
    querySnapshot.forEach((doc) => {
      progress.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: progress };
  } catch (error) {
    return { success: false, error };
  }
};

// Notes Management
export const saveNote = async (noteData) => {
  try {
    if (noteData.id) {
      // Update existing note
      await updateDoc(doc(db, COLLECTIONS.NOTES, noteData.id), {
        ...noteData,
        updatedAt: new Date()
      });
      return { success: true, id: noteData.id };
    } else {
      // Create new note
      const docRef = await addDoc(collection(db, COLLECTIONS.NOTES), {
        ...noteData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Practice Sessions Management

// Enhanced User Revision Data Management
export const saveUserRevisionData = async (userId, revisionData) => {
  try {
    // Use user ID as document ID for easy access
    const docRef = doc(db, COLLECTIONS.REVISION_DATA, userId);
    await setDoc(docRef, {
      userId,
      ...revisionData,
      updatedAt: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserRevisionDataByUserId = async (userId) => {
  try {
    const docRef = doc(db, COLLECTIONS.REVISION_DATA, userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: true, data: null };
    }
  } catch (error) {
    return { success: false, error };
  }
};

export const updateUserRevisionData = async (userId, updateData) => {
  try {
    const docRef = doc(db, COLLECTIONS.REVISION_DATA, userId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Practice Session Management
export const savePracticeSession = async (userId, sessionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.PRACTICE_SESSIONS), {
      userId,
      ...sessionData,
      createdAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserPracticeSessions = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.PRACTICE_SESSIONS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const sessions = [];
    querySnapshot.forEach((doc) => {
      sessions.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: sessions };
  } catch (error) {
    return { success: false, error };
  }
};

// Wrong Questions Management
export const saveWrongQuestion = async (userId, questionData) => {
  try {
    const dataToSave = {
      userId,
      type: 'wrongQuestion',
      ...questionData,
      // Use server timestamp for consistency
      createdAt: new Date()
    };
    
    
    const docRef = await addDoc(collection(db, COLLECTIONS.REVISION_DATA), dataToSave);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

// Get user's wrong and skipped questions for revision
export const getUserWrongQuestions = async (userId, limitCount = 500) => {
  try {
    console.log(`getUserWrongQuestions: Fetching for userId=${userId}, limit=${limitCount}`);
    
    // First try with type filter
    let q = query(
      collection(db, COLLECTIONS.REVISION_DATA),
      where('userId', '==', userId),
      where('type', '==', 'wrongQuestion'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    let querySnapshot;
    try {
      querySnapshot = await getDocs(q);
      console.log(`getUserWrongQuestions: Found ${querySnapshot.size} questions with type filter`);
    } catch (indexError) {
      console.log('getUserWrongQuestions: Index error with orderBy, trying without...');
      // If index error, try without orderBy
      q = query(
        collection(db, COLLECTIONS.REVISION_DATA),
        where('userId', '==', userId),
        where('type', '==', 'wrongQuestion')
      );
      querySnapshot = await getDocs(q);
      console.log(`getUserWrongQuestions: Found ${querySnapshot.size} questions without orderBy`);
    }
    
    const questions = [];
    querySnapshot.forEach((doc) => {
      questions.push({ id: doc.id, ...doc.data() });
    });
    
    console.log(`getUserWrongQuestions: Got ${questions.length} questions with type='wrongQuestion'`);
    
    // ALWAYS try without type filter to get ALL revision data
    // This ensures we don't miss questions that might have different field structures
    console.log(`getUserWrongQuestions: Fetching ALL revision data to ensure completeness...`);
    
    try {
      q = query(
        collection(db, COLLECTIONS.REVISION_DATA),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount * 2) // Fetch more to account for filtering
      );
      querySnapshot = await getDocs(q);
      console.log(`getUserWrongQuestions: Found ${querySnapshot.size} total documents`);
    } catch (indexError2) {
      console.log('getUserWrongQuestions: Index error, trying without orderBy...');
      // If still index error, try without orderBy
      q = query(
        collection(db, COLLECTIONS.REVISION_DATA),
        where('userId', '==', userId)
      );
      querySnapshot = await getDocs(q);
      console.log(`getUserWrongQuestions: Found ${querySnapshot.size} total documents (no orderBy)`);
    }
    
    const allQuestions = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter for wrong/skipped questions in memory
      // Accept multiple field variations for compatibility
      if (data.status === 'wrong' || data.status === 'skipped' || 
          data.isWrong || data.isSkipped || 
          data.type === 'wrongQuestion') {
        allQuestions.push({ id: doc.id, ...data });
      }
    });
    
    console.log(`getUserWrongQuestions: Filtered to ${allQuestions.length} wrong/skipped questions`);
    
    // Sort by createdAt in memory (newest first)
    allQuestions.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });
    
    // Return all questions (up to limit)
    const finalQuestions = allQuestions.slice(0, limitCount);
    console.log(`getUserWrongQuestions: Returning ${finalQuestions.length} questions`);
    
    return { success: true, data: finalQuestions };
  } catch (error) {
    return { success: false, error };
  }
};

// Update wrong question metadata
export const updateWrongQuestion = async (questionId, updateData) => {
  try {
    const questionRef = doc(db, COLLECTIONS.REVISION_DATA, questionId);
    await updateDoc(questionRef, {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Questions Management
export const saveQuestion = async (questionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.QUESTIONS), {
      ...questionData,
      createdAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getQuestionsBySubject = async (subject, limitCount = 10) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.QUESTIONS),
      where('subject', '==', subject),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const questions = [];
    querySnapshot.forEach((doc) => {
      questions.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: questions };
  } catch (error) {
    return { success: false, error };
  }
};

export const getQuestionsByTopic = async (topic, limitCount = 10) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.QUESTIONS),
      where('topic', '==', topic),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const questions = [];
    querySnapshot.forEach((doc) => {
      questions.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: questions };
  } catch (error) {
    return { success: false, error };
  }
};

// Admin Settings Management
export const saveAdminSetting = async (settingData) => {
  try {
    await setDoc(doc(db, COLLECTIONS.ADMIN_SETTINGS, settingData.key), {
      ...settingData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getAdminSetting = async (key) => {
  try {
    const docRef = doc(db, COLLECTIONS.ADMIN_SETTINGS, key);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, error: 'Setting not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// NEW: Exam Data Management
export const saveExamData = async (examId, examData) => {
  try {
    // Preserve coachingId if provided (for multi-tenancy)
    await setDoc(doc(db, COLLECTIONS.EXAM_DATA, examId), {
      ...examData,
      examId,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getExamData = async (examId) => {
  try {
    const docRef = doc(db, COLLECTIONS.EXAM_DATA, examId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, error: 'Exam data not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

export const getAllExamData = async (coachingId = null) => {
  try {
    let q = collection(db, COLLECTIONS.EXAM_DATA);
    
    // Multi-tenancy: Filter by coachingId if provided
    if (coachingId) {
      q = query(q, where('coachingId', '==', coachingId));
    }
    
    const querySnapshot = await getDocs(q);
    const examData = {};
    querySnapshot.forEach((doc) => {
      examData[doc.id] = doc.data();
    });
    return { success: true, data: examData };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteExamData = async (examId, coachingId = null) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.EXAM_DATA, examId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// NEW: Mock Tests Management
export const saveMockTest = async (testData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.MOCK_TESTS), {
      ...testData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getMockTests = async (examId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.MOCK_TESTS),
      where('examId', '==', examId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const tests = [];
    querySnapshot.forEach((doc) => {
      tests.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: tests };
  } catch (error) {
    return { success: false, error };
  }
};

// Get single mock test by ID - Enhanced to handle both collections
export const getMockTest = async (testId) => {
  try {
    if (!testId) {
      return { success: false, error: 'Test ID is required' };
    }
    
    // React Router automatically decodes URL params, so testId should already be decoded
    // Try both the testId as-is and a decoded version (in case it's still encoded)
    const testIdsToTry = [testId];
    
    // If testId contains encoded characters, try decoding it
    if (testId.includes('%')) {
      try {
        const decoded = decodeURIComponent(testId);
        testIdsToTry.unshift(decoded); // Try decoded version first
      } catch (e) {
        // If decode fails, just use original
      }
    }
    
    // Try each testId variant
    for (const testIdToTry of testIdsToTry) {
      // First, try to find in MOCK_TESTS collection
      const mockTestRef = doc(db, COLLECTIONS.MOCK_TESTS, testIdToTry);
      const mockTestSnap = await getDoc(mockTestRef);
      
      if (mockTestSnap.exists()) {
        return { success: true, data: { id: mockTestSnap.id, ...mockTestSnap.data() } };
      }
      
      // If not found in MOCK_TESTS, try TESTS collection
      const testRef = doc(db, COLLECTIONS.TESTS, testIdToTry);
      const testSnap = await getDoc(testRef);
      
      if (testSnap.exists()) {
        const testData = testSnap.data();
        
        // Convert test format to mock test format
        const mockTestData = {
          id: testSnap.id,
          name: testData.title,
          description: testData.description || `Mock test for ${testData.exam}`,
          duration: testData.duration * 60, // Convert minutes to seconds
          questions: testData.totalQuestions || (testData.questions ? testData.questions.length : 0),
          examId: testData.exam?.toLowerCase() || 'cat',
          type: 'full', // Default type
          difficulty: testData.difficulty?.toLowerCase() || 'medium',
          subjects: [testData.subject || 'General'], // Use assigned subject or default to General
          attempts: 0,
          rating: 4.5,
          isCompleted: false,
          isNew: false,
          isLive: false,
          status: testData.status || 'published',
          createdAt: testData.createdAt || new Date(),
          updatedAt: testData.updatedAt || new Date(),
          // Include sections and useDynamicSections from test data
          sections: testData.sections || [],
          useDynamicSections: testData.useDynamicSections || false,
          // Include original test data for compatibility
          originalTestData: testData
        };
        
        return { success: true, data: mockTestData };
      }
    }
    
    return { success: false, error: `Test not found with ID: ${testId}` };
  } catch (error) {
    return { success: false, error: error.message || 'Unknown error' };
  }
};

export const getAllMockTests = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.MOCK_TESTS));
    const tests = [];
    querySnapshot.forEach((doc) => {
      tests.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: tests };
  } catch (error) {
    return { success: false, error };
  }
};

// NEW: Question Bank Management
export const saveQuestionToBank = async (questionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.QUESTION_BANK), {
      ...questionData,
      createdAt: new Date(),
      updatedAt: new Date(),
      usageCount: 0,
      averageAccuracy: 0,
      totalAttempts: 0,
      status: questionData.status || 'active'
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getQuestionsFromBank = async (filters = {}) => {
  try {
    // Use the enhanced query utility with automatic fallback
    const result = await queryWithFallback(COLLECTIONS.QUESTION_BANK, filters, {
      orderBy: { field: 'createdAt', direction: 'desc' },
      limit: filters.limit,
      useFallback: true,
      maxRetries: 3
    });
    
    if (result.success) {
      return result;
    } else {
      // Check if it's an index error
      if (result.error.code === 'failed-precondition' || result.error.message.includes('index')) {
        // Index error - return empty result instead of failing
        return { success: true, data: [], indexError: true };
      }
      
      return { success: false, error: result.error };
    }
  } catch (error) {
    return { success: false, error };
  }
};

export const getQuestionsByTopicFromBank = async (topic, limit = 10) => {
  return getQuestionsFromBank({ topic, limit });
};

export const getQuestionsBySubjectFromBank = async (subject, limit = 10) => {
  return getQuestionsFromBank({ subject, limit });
};

export const getMixedQuestionsFromBank = async (limit = 20) => {
  return getQuestionsFromBank({ limit });
};

// Batch Operations
export const batchWrite = async (operations) => {
  try {
    const batch = writeBatch(db);
    
    operations.forEach(operation => {
      const { type, collection: collectionName, docId, data } = operation;
      const docRef = doc(db, collectionName, docId);
      
      switch (type) {
        case 'set':
          batch.set(docRef, data);
          break;
        case 'update':
          batch.update(docRef, data);
          break;
        case 'delete':
          batch.delete(docRef);
          break;
        default:
          throw new Error(`Unknown operation type: ${type}`);
      }
    });
    
    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Index Creation (Firestore handles this automatically, but we can track it)
export const createIndex = async (collectionName, fields) => {
  try {
    // In Firestore, indexes are created automatically when queries are made
    // This function is for tracking and documentation purposes
    return { success: true, message: 'Index will be created automatically by Firestore' };
  } catch (error) {
    return { success: false, error };
  }
};

// Optimized batch save for questions
export const batchSaveQuestions = async (questions, batchSize = 500) => {
  try {
    const batches = [];
    for (let i = 0; i < questions.length; i += batchSize) {
      batches.push(questions.slice(i, i + batchSize));
    }
    
    const results = [];
    for (const batch of batches) {
      const batchOperations = batch.map(question => ({
        type: 'set',
        collection: COLLECTIONS.QUESTION_BANK,
        docId: question.id || question.firebaseId,
        data: {
          ...question,
          createdAt: new Date()
        }
      }));
      
      const result = await batchWrite(batchOperations);
      results.push(result);
      
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return { success: true, results };
  } catch (error) {
    return { success: false, error };
  }
};

// Optimized batch save for mock tests
export const batchSaveMockTests = async (tests, batchSize = 100) => {
  try {
    const batches = [];
    for (let i = 0; i < tests.length; i += batchSize) {
      batches.push(tests.slice(i, i + batchSize));
    }
    
    const results = [];
    for (const batch of batches) {
      const batchOperations = batch.map(test => ({
        type: 'set',
        collection: COLLECTIONS.MOCK_TESTS,
        docId: test.id || test.firebaseId,
        data: {
          ...test,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: test.status || 'draft',
          totalAttempts: 0,
          averageScore: 0,
          totalQuestions: test.questions ? test.questions.length : 0
        }
      }));
      
      const result = await batchWrite(batchOperations);
      results.push(result);
      
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return { success: true, results };
  } catch (error) {
    return { success: false, error };
  }
};

// Pagination support for questions
export const getQuestionsWithPagination = async (filters = {}, page = 1, pageSize = 20) => {
  try {
    let q = collection(db, COLLECTIONS.QUESTION_BANK);
    
    // Apply filters
    if (filters.subject) {
      q = query(q, where('subject', '==', filters.subject));
    }
    if (filters.topic) {
      q = query(q, where('topic', '==', filters.topic));
    }
    if (filters.difficulty) {
      q = query(q, where('difficulty', '==', filters.difficulty));
    }
    
    // Apply pagination
    const offset = (page - 1) * pageSize;
    q = query(q, limit(pageSize));
    
    const querySnapshot = await getDocs(q);
    const questions = [];
    querySnapshot.forEach((doc) => {
      questions.push({ id: doc.id, ...doc.data() });
    });
    
    return { 
      success: true, 
      data: questions,
      page,
      pageSize,
      hasMore: questions.length === pageSize
    };
  } catch (error) {
    return { success: false, error };
  }
};

// Cache management for frequently accessed data
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const getCachedData = async (key, fetcher, ttl = CACHE_TTL) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < ttl) {
    return cached.data;
  }
  
  const data = await fetcher();
  cache.set(key, { data, timestamp: Date.now() });
  return data;
};

export const clearCache = () => {
  cache.clear();
};

// Performance monitoring
export const getCollectionStats = async (collectionName) => {
  try {
    // For public stats, try to get from publicStats collection first
    if (collectionName === 'users' || collectionName === 'mockTests' || collectionName === 'tests') {
      try {
        const statsDoc = await getDoc(doc(db, 'publicStats', 'homePageStats'));
        if (statsDoc.exists()) {
          const stats = statsDoc.data();
          const count = stats[collectionName] || stats[`${collectionName}Count`] || 0;
          if (count > 0) {
            return {
              success: true,
              count: count,
              collection: collectionName,
              fromCache: true
            };
          }
        }
      } catch (statsError) {
        // If publicStats doesn't exist or fails, fall through to direct query
        console.log('Public stats not available, trying direct query');
      }
    }
    
    // Try direct query (works for collections that allow authenticated read)
    const querySnapshot = await getDocs(collection(db, collectionName));
    return {
      success: true,
      count: querySnapshot.size,
      collection: collectionName
    };
  } catch (error) {
    // If permission denied, return 0 (will show as "0+" in UI)
    if (error.code === 'permission-denied' || error.message?.includes('permission')) {
      console.warn(`Permission denied for ${collectionName}, returning 0`);
      return { success: false, error: 'permission-denied', count: 0 };
    }
    return { success: false, error };
  }
};

// ===== COLLABORATION FEATURES =====

// Doubts Management
export const createDoubt = async (doubtData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.DOUBTS), {
      ...doubtData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'open',
      viewCount: 0,
      responseCount: 0,
      isResolved: false
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getDoubts = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.DOUBTS);
    
    if (filters.authorId) {
      q = query(q, where('authorId', '==', filters.authorId));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.subject) {
      q = query(q, where('subject', '==', filters.subject));
    }
    if (filters.topic) {
      q = query(q, where('topic', '==', filters.topic));
    }
    if (filters.examId) {
      q = query(q, where('examId', '==', filters.examId));
    }
    if (filters.isResolved !== undefined) {
      q = query(q, where('isResolved', '==', filters.isResolved));
    }
    
    q = query(q, orderBy('createdAt', 'desc'));
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }
    
    const querySnapshot = await getDocs(q);
    const doubts = [];
    querySnapshot.forEach((doc) => {
      doubts.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs (for attachments and image arrays)
    const doubtsWithUrls = await Promise.all(
      doubts.map(async (doubt) => {
        // Handle questionImages array
        if (doubt.questionImages && Array.isArray(doubt.questionImages)) {
          doubt.questionImages = await Promise.all(
            doubt.questionImages.map(async (imageUrl) => {
              if (imageUrl && typeof imageUrl === 'string') {
                if (!imageUrl.startsWith('http') || imageUrl.includes('X-Amz-Expires')) {
                  let fileKey = imageUrl;
                  if (imageUrl.startsWith('http')) {
                    try {
                      const urlObj = new URL(imageUrl);
                      fileKey = decodeURIComponent(urlObj.pathname.substring(1));
                    } catch (e) {
                      console.warn('Failed to parse URL:', imageUrl);
                    }
                  }
                  try {
                    const urlResult = await getFileDownloadURL(fileKey);
                    if (urlResult.success) {
                      return urlResult.url;
                    }
                  } catch (error) {
                    console.warn('Failed to generate URL for image:', fileKey, error);
                  }
                }
              }
              return imageUrl;
            })
          );
        }
        
        // Handle answerImages array
        if (doubt.answerImages && Array.isArray(doubt.answerImages)) {
          doubt.answerImages = await Promise.all(
            doubt.answerImages.map(async (imageUrl) => {
              if (imageUrl && typeof imageUrl === 'string') {
                if (!imageUrl.startsWith('http') || imageUrl.includes('X-Amz-Expires')) {
                  let fileKey = imageUrl;
                  if (imageUrl.startsWith('http')) {
                    try {
                      const urlObj = new URL(imageUrl);
                      fileKey = decodeURIComponent(urlObj.pathname.substring(1));
                    } catch (e) {
                      console.warn('Failed to parse URL:', imageUrl);
                    }
                  }
                  try {
                    const urlResult = await getFileDownloadURL(fileKey);
                    if (urlResult.success) {
                      return urlResult.url;
                    }
                  } catch (error) {
                    console.warn('Failed to generate URL for image:', fileKey, error);
                  }
                }
              }
              return imageUrl;
            })
          );
        }
        
        return doubt;
      })
    );
    
    // Return empty array if no doubts found (this is normal)
    return { success: true, data: doubtsWithUrls };
  } catch (error) {
    // Return empty array on error instead of failing
    return { success: false, error, data: [] };
  }
};

export const updateDoubt = async (doubtId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.DOUBTS, doubtId), {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Doubt Responses
export const addDoubtResponse = async (responseData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.DOUBT_RESPONSES), {
      ...responseData,
      createdAt: new Date(),
      isAccepted: false,
      likeCount: 0,
      isExpert: false
    });
    
    // Update doubt response count
    const doubtRef = doc(db, COLLECTIONS.DOUBTS, responseData.doubtId);
    await updateDoc(doubtRef, {
      responseCount: responseData.responseCount + 1,
      updatedAt: new Date()
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getDoubtResponses = async (doubtId, limit = 10) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.DOUBT_RESPONSES),
      where('doubtId', '==', doubtId),
      orderBy('createdAt', 'asc'),
      limit(limit)
    );
    const querySnapshot = await getDocs(q);
    const responses = [];
    querySnapshot.forEach((doc) => {
      responses.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs (for attachments and image arrays)
    const responsesWithUrls = await Promise.all(
      responses.map(async (response) => {
        // Handle answerImages array in responses
        if (response.answerImages && Array.isArray(response.answerImages)) {
          response.answerImages = await Promise.all(
            response.answerImages.map(async (imageUrl) => {
              if (imageUrl && typeof imageUrl === 'string') {
                if (!imageUrl.startsWith('http') || imageUrl.includes('X-Amz-Expires')) {
                  let fileKey = imageUrl;
                  if (imageUrl.startsWith('http')) {
                    try {
                      const urlObj = new URL(imageUrl);
                      fileKey = decodeURIComponent(urlObj.pathname.substring(1));
                    } catch (e) {
                      console.warn('Failed to parse URL:', imageUrl);
                    }
                  }
                  try {
                    const urlResult = await getFileDownloadURL(fileKey);
                    if (urlResult.success) {
                      return urlResult.url;
                    }
                  } catch (error) {
                    console.warn('Failed to generate URL for image:', fileKey, error);
                  }
                }
              }
              return imageUrl;
            })
          );
        }
        
        // Handle attachments array if present
        if (response.attachments && Array.isArray(response.attachments)) {
          response.attachments = await Promise.all(
            response.attachments.map(async (attachment) => {
              if (attachment.url && typeof attachment.url === 'string') {
                if (!attachment.url.startsWith('http') || attachment.url.includes('X-Amz-Expires')) {
                  let fileKey = attachment.url;
                  if (attachment.url.startsWith('http')) {
                    try {
                      const urlObj = new URL(attachment.url);
                      fileKey = decodeURIComponent(urlObj.pathname.substring(1));
                    } catch (e) {
                      console.warn('Failed to parse URL:', attachment.url);
                    }
                  }
                  try {
                    const urlResult = await getFileDownloadURL(fileKey);
                    if (urlResult.success) {
                      attachment.url = urlResult.url;
                    }
                  } catch (error) {
                    console.warn('Failed to generate URL for attachment:', fileKey, error);
                  }
                }
              }
              return attachment;
            })
          );
        }
        
        return response;
      })
    );
    
    return { success: true, data: responsesWithUrls };
  } catch (error) {
    return { success: false, error };
  }
};

export const acceptDoubtResponse = async (responseId, doubtId) => {
  try {
    const batch = writeBatch(db);
    
    // Mark response as accepted
    batch.update(doc(db, COLLECTIONS.DOUBT_RESPONSES, responseId), {
      isAccepted: true,
      acceptedAt: new Date()
    });
    
    // Mark doubt as resolved
    batch.update(doc(db, COLLECTIONS.DOUBTS, doubtId), {
      isResolved: true,
      status: 'resolved',
      updatedAt: new Date()
    });
    
    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Expert Profiles
export const createExpertProfile = async (expertData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.EXPERT_PROFILES), {
      ...expertData,
      createdAt: new Date(),
      updatedAt: new Date(),
      isVerified: false,
      rating: 0,
      responseCount: 0,
      isActive: true
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getExpertProfiles = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.EXPERT_PROFILES);
    
    if (filters.subject) {
      q = query(q, where('subjects', 'array-contains', filters.subject));
    }
    if (filters.examId) {
      q = query(q, where('expertise', 'array-contains', filters.examId.toUpperCase()));
    }
    if (filters.isVerified !== undefined) {
      q = query(q, where('isVerified', '==', filters.isVerified));
    }
    if (filters.isActive !== undefined) {
      q = query(q, where('isActive', '==', filters.isActive));
    }
    
    q = query(q, orderBy('rating', 'desc'));
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }
    
    const querySnapshot = await getDocs(q);
    const experts = [];
    querySnapshot.forEach((doc) => {
      experts.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs (for profile pictures/documents)
    const expertsWithUrls = await convertFileKeysToUrlsInArray(experts);
    
    // Return empty array if no experts found (this is normal)
    return { success: true, data: expertsWithUrls };
  } catch (error) {
    // Return empty array on error instead of failing
    return { success: false, error, data: [] };
  }
};

// Live Sessions
export const createLiveSession = async (sessionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.LIVE_SESSIONS), {
      ...sessionData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'scheduled',
      participantCount: 0,
      isActive: false
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getLiveSessions = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.LIVE_SESSIONS);
    
    if (filters.expertId) {
      q = query(q, where('expertId', '==', filters.expertId));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.subject) {
      q = query(q, where('subject', '==', filters.subject));
    }
    if (filters.examId) {
      q = query(q, where('examId', '==', filters.examId));
    }
    
    q = query(q, orderBy('scheduledAt', 'asc'));
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }
    
    const querySnapshot = await getDocs(q);
    const sessions = [];
    querySnapshot.forEach((doc) => {
      sessions.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs (for session materials/recordings)
    const sessionsWithUrls = await convertFileKeysToUrlsInArray(sessions);
    
    // Return empty array if no sessions found (this is normal)
    return { success: true, data: sessionsWithUrls };
  } catch (error) {
    // Return empty array on error instead of failing
    return { success: false, error, data: [] };
  }
};

export const registerForSession = async (sessionId, userId) => {
  try {
    const registrationRef = doc(db, COLLECTIONS.SESSION_REGISTRATIONS, `${sessionId}_${userId}`);
    
    await setDoc(registrationRef, {
      sessionId,
      userId,
      registeredAt: new Date(),
      status: 'registered'
    });
    
    // Update session participant count
    const sessionRef = doc(db, COLLECTIONS.LIVE_SESSIONS, sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      await updateDoc(sessionRef, {
        participantCount: sessionData.participantCount + 1,
        updatedAt: new Date()
      });
    }
    
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getSessionRegistrations = async (sessionId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SESSION_REGISTRATIONS),
      where('sessionId', '==', sessionId),
      orderBy('registeredAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const registrations = [];
    querySnapshot.forEach((doc) => {
      registrations.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: registrations };
  } catch (error) {
    return { success: false, error };
  }
};

export const initializeDoubtsCollection = async () => {
  try {
    const sampleDoubts = [
      {
        id: 'sample-doubt-1',
        title: 'Help with Physics Mechanics',
        description: 'I am having trouble understanding Newton\'s laws of motion. Can someone explain?',
        subject: 'Physics',
        topic: 'Mechanics',
        authorId: 'sample-user-1',
        authorName: 'Sample User',
        examId: 'cat',
        status: 'open',
        isResolved: false,
        responseCount: 2,
        viewCount: 15,
        createdAt: new Date(Date.now() - 3600000), // 1 hour ago
        priority: 'medium'
      },
      {
        id: 'sample-doubt-2',
        title: 'Chemistry Organic Reactions',
        description: 'Need help with organic chemistry reactions and mechanisms',
        subject: 'Chemistry',
        topic: 'Organic Chemistry',
        authorId: 'sample-user-2',
        authorName: 'Another User',
        examId: 'cat',
        status: 'open',
        isResolved: false,
        responseCount: 1,
        viewCount: 8,
        createdAt: new Date(Date.now() - 7200000), // 2 hours ago
        priority: 'high'
      }
    ];

    const batch = writeBatch(db);
    sampleDoubts.forEach(doubt => {
      const docRef = doc(db, COLLECTIONS.DOUBTS, doubt.id);
      batch.set(docRef, doubt);
    });
    await batch.commit();
    
    return { success: true, message: 'Doubts collection initialized' };
  } catch (error) {
    return { success: false, error };
  }
};

export const initializeExpertProfilesCollection = async () => {
  try {
    const sampleExperts = [
      {
        id: 'sample-expert-1',
        name: 'Dr. Priya Sharma',
        bio: 'Experienced educator with 10+ years in exam preparation',
        subjects: ['Physics', 'Mathematics'],
        expertise: ['CAT'],
        experience: '10 years',
        hourlyRate: 1500,
        rating: 4.8,
        isVerified: true,
        isActive: true,
        responseCount: 45,
        availability: ['Mon', 'Wed', 'Fri'],
        createdAt: new Date(Date.now() - 2592000000), // 30 days ago
        updatedAt: new Date()
      },
      {
        id: 'sample-expert-2',
        name: 'Prof. Rajesh Kumar',
        bio: 'Subject matter expert in Chemistry and Biology',
        subjects: ['Chemistry', 'Biology'],
        expertise: ['CAT'],
        experience: '8 years',
        hourlyRate: 1200,
        rating: 4.6,
        isVerified: true,
        isActive: true,
        responseCount: 32,
        availability: ['Tue', 'Thu', 'Sat'],
        createdAt: new Date(Date.now() - 2592000000), // 30 days ago
        updatedAt: new Date()
      }
    ];

    const batch = writeBatch(db);
    sampleExperts.forEach(expert => {
      const docRef = doc(db, COLLECTIONS.EXPERT_PROFILES, expert.id);
      batch.set(docRef, expert);
    });
    await batch.commit();
    
    return { success: true, message: 'Expert profiles collection initialized' };
  } catch (error) {
    return { success: false, error };
  }
};

export const initializeLiveSessionsCollection = async () => {
  try {
    const sampleSessions = [
      {
        id: 'sample-session-1',
        title: 'Physics Problem Solving',
        description: 'Live session on solving complex physics problems',
        subject: 'Physics',
        expertId: 'sample-expert-1',
        expertName: 'Dr. Priya Sharma',
        scheduledAt: new Date(Date.now() + 86400000), // Tomorrow
        duration: 60,
        maxParticipants: 20,
        participantCount: 8,
        price: 500,
        status: 'scheduled',
        examId: 'cat',
        isActive: false,
        createdAt: new Date(Date.now() - 86400000), // 1 day ago
        updatedAt: new Date()
      }
    ];

    const batch = writeBatch(db);
    sampleSessions.forEach(session => {
      const docRef = doc(db, COLLECTIONS.LIVE_SESSIONS, session.id);
      batch.set(docRef, session);
    });
    await batch.commit();
    
    return { success: true, message: 'Live sessions collection initialized' };
  } catch (error) {
    return { success: false, error };
  }
};

// Check if collection is empty and initialize if needed
export const ensureCollectionHasData = async (collectionName, initializeFunction) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    if (querySnapshot.empty) {
      return await initializeFunction();
    }
    return { success: true, message: `${collectionName} collection already has data` };
  } catch (error) {
    return { success: false, error };
  }
};

// User-Specific Data Management
export const saveUserActivity = async (userId, activityData) => {
  try {
    const docRef = await addDoc(collection(db, 'userActivities'), {
      userId,
      ...activityData,
      timestamp: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserActivities = async (userId, limit = 100) => {
  try {
    const q = query(
      collection(db, 'userActivities'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    const querySnapshot = await getDocs(q);
    const activities = [];
    querySnapshot.forEach((doc) => {
      activities.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: activities };
  } catch (error) {
    return { success: false, error };
  }
};

// User Dashboard Data Management
export const saveUserDashboardData = async (userId, dashboardData) => {
  try {
    const docRef = doc(db, 'userDashboards', userId);
    await setDoc(docRef, {
      userId,
      ...dashboardData,
      lastUpdated: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserDashboardData = async (userId) => {
  try {
    const docRef = doc(db, 'userDashboards', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: true, data: null };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// User Study Progress Management
export const saveUserStudyProgress = async (userId, progressData) => {
  try {
    const docRef = doc(db, 'userStudyProgress', userId);
    await setDoc(docRef, {
      userId,
      ...progressData,
      lastUpdated: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserStudyProgress = async (userId) => {
  try {
    const docRef = doc(db, 'userStudyProgress', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: true, data: null };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// User Performance Analytics
export const saveUserPerformance = async (userId, performanceData) => {
  try {
    const docRef = doc(db, 'userPerformance', userId);
    await setDoc(docRef, {
      userId,
      ...performanceData,
      lastUpdated: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserPerformance = async (userId) => {
  try {
    const docRef = doc(db, 'userPerformance', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: true, data: null };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// User Enrolled Courses Management
export const saveUserEnrolledCourse = async (userId, courseData) => {
  try {
    const docRef = await addDoc(collection(db, 'userEnrolledCourses'), {
      userId,
      ...courseData,
      enrolledAt: new Date(),
      lastAccessed: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserEnrolledCourses = async (userId) => {
  try {
    const q = query(
      collection(db, 'userEnrolledCourses'),
      where('userId', '==', userId),
      orderBy('lastAccessed', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const courses = [];
    querySnapshot.forEach((doc) => {
      courses.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: courses };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateUserCourseProgress = async (courseId, progressData) => {
  try {
    const docRef = doc(db, 'userEnrolledCourses', courseId);
    await updateDoc(docRef, {
      ...progressData,
      lastAccessed: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// User Notes Management
export const saveUserNote = async (userId, noteData) => {
  try {
    const docRef = await addDoc(collection(db, 'userNotes'), {
      userId,
      ...noteData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserNotes = async (userId, limitCount = 100) => {
  try {
    const q = query(
      collection(db, 'userNotes'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const notes = [];
    querySnapshot.forEach((doc) => {
      notes.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs
    const notesWithUrls = await convertFileKeysToUrlsInArray(notes);
    
    return { success: true, data: notesWithUrls };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateUserNote = async (noteId, updateData) => {
  try {
    const docRef = doc(db, 'userNotes', noteId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteUserNote = async (noteId) => {
  try {
    await deleteDoc(doc(db, 'userNotes', noteId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Batch Syllabus Management
export const getBatchSyllabus = async (batchId) => {
  try {
    const q = query(collection(db, COLLECTIONS.BATCH_SYLLABUS), where('batchId', '==', batchId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { success: true, data: { id: doc.id, ...doc.data() } };
    }
    return { success: true, data: null };
  } catch (error) {
    console.error('Error getting batch syllabus:', error);
    return { success: false, error };
  }
};

export const saveBatchSyllabus = async (batchId, syllabusData) => {
  try {
    const q = query(collection(db, COLLECTIONS.BATCH_SYLLABUS), where('batchId', '==', batchId));
    const querySnapshot = await getDocs(q);
    
    // Ensure we handle both cases: syllabusData being the subjects array or an object containing it
    const subjects = Array.isArray(syllabusData) ? syllabusData : (syllabusData.subjects || []);

    const data = {
      batchId,
      subjects,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid
    };

    if (!querySnapshot.empty) {
      const docRef = doc(db, COLLECTIONS.BATCH_SYLLABUS, querySnapshot.docs[0].id);
      await updateDoc(docRef, data);
      return { success: true, id: docRef.id };
    } else {
      data.createdAt = serverTimestamp();
      const docRef = await addDoc(collection(db, COLLECTIONS.BATCH_SYLLABUS), data);
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    console.error('Error saving batch syllabus:', error);
    return { success: false, error };
  }
};

export const updateSyllabusProgress = async (syllabusId, subjects) => {
  try {
    const docRef = doc(db, COLLECTIONS.BATCH_SYLLABUS, syllabusId);
    await updateDoc(docRef, {
      subjects,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating syllabus progress:', error);
    return { success: false, error };
  }
};

// User Doubts Management
export const saveUserDoubt = async (userId, doubtData) => {
  try {
    const docRef = await addDoc(collection(db, 'userDoubts'), {
      userId,
      ...doubtData,
      createdAt: new Date(),
      status: 'pending'
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserDoubts = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'userDoubts'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const doubts = [];
    querySnapshot.forEach((doc) => {
      doubts.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs (for attachments)
    const doubtsWithUrls = await convertFileKeysToUrlsInArray(doubts);
    
    return { success: true, data: doubtsWithUrls };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateUserDoubt = async (doubtId, updateData) => {
  try {
    const docRef = doc(db, 'userDoubts', doubtId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Comprehensive User Data Sync
export const syncUserData = async (userId) => {
  try {
    // Get all user data from different collections
    const [
      dashboardResult,
      progressResult,
      performanceResult,
      coursesResult,
      notesResult,
      doubtsResult,
      groupsResult,
      activitiesResult
    ] = await Promise.all([
      getUserDashboardData(userId),
      getUserStudyProgress(userId),
      getUserPerformance(userId),
      getUserEnrolledCourses(userId),
      getUserNotes(userId),
      getUserDoubts(userId),

      getUserActivities(userId, 50)
    ]);

    // Combine all data into a comprehensive user profile
    const userData = {
      dashboard: dashboardResult.success ? dashboardResult.data : null,
      progress: progressResult.success ? progressResult.data : null,
      performance: performanceResult.success ? performanceResult.data : null,
      courses: coursesResult.success ? coursesResult.data : [],
      notes: notesResult.success ? notesResult.data : [],
      doubts: doubtsResult.success ? doubtsResult.data : [],

      activities: activitiesResult.success ? activitiesResult.data : [],
      lastSync: new Date().toISOString()
    };

    // Save combined data for quick access
    await saveUserDashboardData(userId, {
      comprehensiveData: userData,
      lastSync: new Date().toISOString()
    });

    return { success: true, data: userData };
  } catch (error) {
    return { success: false, error };
  }
};

// User Analytics and Insights
export const generateUserInsights = async (userId) => {
  try {
    const userData = await syncUserData(userId);
    if (!userData.success) {
      return { success: false, error: 'Failed to sync user data' };
    }

    const data = userData.data;
    
    // Calculate insights
    const insights = {
      totalStudyTime: data.activities.reduce((sum, activity) => 
        sum + (activity.duration || 0), 0),
      totalPracticeSessions: data.activities.filter(a => 
        a.type === 'practice_session').length,
      totalTestsTaken: data.activities.filter(a => 
        a.type === 'test_completion').length,
      averageAccuracy: data.performance?.averageAccuracy || 0,
      weakTopics: data.performance?.weakTopics || [],
      strongTopics: data.performance?.strongTopics || [],
      studyStreak: calculateStudyStreak(data.activities),
      recommendedActions: generateRecommendations(data),
      progressTrend: calculateProgressTrend(data.activities),
      nextMilestones: generateMilestones(data)
    };

    // Save insights
    await saveUserDashboardData(userId, {
      insights: insights,
      lastInsightsUpdate: new Date().toISOString()
    });

    return { success: true, data: insights };
  } catch (error) {
    return { success: false, error };
  }
};

// Helper functions for insights
const calculateStudyStreak = (activities) => {
  // Implementation for calculating study streak
  return 7; // Placeholder
};

const generateRecommendations = (data) => {
  const recommendations = [];
  
  if (data.performance?.weakTopics?.length > 0) {
    recommendations.push({
      type: 'weak_topic',
      message: `Focus on ${data.performance.weakTopics[0].name}`,
      priority: 'high'
    });
  }
  
  if (data.activities.length < 10) {
    recommendations.push({
      type: 'activity',
      message: 'Start practicing more regularly',
      priority: 'medium'
    });
  }
  
  return recommendations;
};

const calculateProgressTrend = (activities) => {
  // Implementation for calculating progress trend
  return 'improving'; // Placeholder
};

const generateMilestones = (data) => {
  // Implementation for generating milestones
  return [
    { type: 'accuracy', target: 80, current: data.performance?.averageAccuracy || 0 },
    { type: 'sessions', target: 50, current: data.activities.length }
  ];
};

// User Exam Preferences Management
export const saveUserExamPreferences = async (userId, examPreferences) => {
  try {
    const docRef = doc(db, 'userExamPreferences', userId);
    await setDoc(docRef, {
      userId,
      ...examPreferences,
      lastUpdated: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserExamPreferences = async (userId) => {
  try {
    const docRef = doc(db, 'userExamPreferences', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: true, data: null };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// User App Settings Management
export const saveUserAppSettings = async (userId, settings) => {
  try {
    const docRef = doc(db, 'userAppSettings', userId);
    await setDoc(docRef, {
      userId,
      ...settings,
      lastUpdated: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserAppSettings = async (userId) => {
  try {
    const docRef = doc(db, 'userAppSettings', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: true, data: null };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// User Target Score Management
export const saveUserTargetScore = async (userId, targetScore) => {
  try {
    const docRef = doc(db, 'userTargetScores', userId);
    await setDoc(docRef, {
      userId,
      targetScore,
      lastUpdated: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserTargetScore = async (userId) => {
  try {
    const docRef = doc(db, 'userTargetScores', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: true, data: { targetScore: 95 } }; // Default target score
    }
  } catch (error) {
    return { success: false, error };
  }
};

// ===== COURSE MANAGEMENT FUNCTIONS =====

// User Courses Management
export const saveUserCourseData = async (userId, courseData) => {
  try {
    const docRef = doc(db, COLLECTIONS.USER_COURSES, userId);
    
    // Ensure the data is properly structured
    const dataToSave = {
      userId,
      ...courseData,
      lastUpdated: new Date().toISOString()
    };
    
    // If comprehensiveCourseData is provided, ensure it has all required fields
    if (dataToSave.comprehensiveCourseData) {
      const comprehensive = dataToSave.comprehensiveCourseData;
      dataToSave.comprehensiveCourseData = {
        stats: comprehensive.stats || {
          enrolledCourses: 0,
          completedCourses: 0,
          totalHours: 0,
          averageScore: 0,
          certificates: 0
        },
        enrolledCourses: comprehensive.enrolledCourses || [],
        assignments: comprehensive.assignments || [],
        certificates: comprehensive.certificates || [],
        studyMaterials: comprehensive.studyMaterials || [],
        liveClasses: comprehensive.liveClasses || [],
        notifications: comprehensive.notifications || [],
        courseDiscussions: comprehensive.courseDiscussions || {}
      };
    }
    
    await setDoc(docRef, dataToSave, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserCourseData = async (userId) => {
  try {
    const docRef = doc(db, COLLECTIONS.USER_COURSES, userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      // Check if data needs migration to new format
      if (data && !data.comprehensiveCourseData && (data.stats || data.enrolledCourses)) {
        // Migrate old format to new format
        const migratedData = {
          ...data,
          comprehensiveCourseData: {
            stats: data.stats || {
              enrolledCourses: 0,
              completedCourses: 0,
              totalHours: 0,
              averageScore: 0,
              certificates: 0
            },
            enrolledCourses: data.enrolledCourses || [],
            assignments: data.assignments || [],
            certificates: data.certificates || [],
            studyMaterials: data.studyMaterials || [],
            liveClasses: data.liveClasses || [],
            notifications: data.notifications || [],
            courseDiscussions: data.courseDiscussions || {}
          }
        };
        
        // Save migrated data back to Firebase
        await setDoc(docRef, migratedData, { merge: true });
        return { success: true, data: migratedData };
      }
      
      return { success: true, data: data };
    } else {
      return { success: true, data: null };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Course Discussions Management
export const saveCourseDiscussion = async (courseId, discussionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.COURSE_DISCUSSIONS), {
      courseId,
      ...discussionData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCourseDiscussions = async (courseId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COURSE_DISCUSSIONS),
      where('courseId', '==', courseId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const discussions = [];
    querySnapshot.forEach((doc) => {
      discussions.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs (for attached files)
    const discussionsWithUrls = await convertFileKeysToUrlsInArray(discussions);
    
    return { success: true, data: discussionsWithUrls };
  } catch (error) {
    return { success: false, error };
  }
};

// Lecture Discussions Management
export const saveLectureDiscussion = async (lectureId, discussionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.LECTURE_DISCUSSIONS), {
      lectureId,
      ...discussionData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getLectureDiscussions = async (lectureId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.LECTURE_DISCUSSIONS),
      where('lectureId', '==', lectureId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const discussions = [];
    querySnapshot.forEach((doc) => {
      discussions.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs (for attached files)
    const discussionsWithUrls = await convertFileKeysToUrlsInArray(discussions);
    
    return { success: true, data: discussionsWithUrls };
  } catch (error) {
    // Check if it's an index error
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      // Fallback to simple query without ordering
      try {
        const fallbackQuery = query(
          collection(db, COLLECTIONS.LECTURE_DISCUSSIONS),
          where('lectureId', '==', lectureId)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        const discussions = [];
        fallbackSnapshot.forEach((doc) => {
          discussions.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort in memory by createdAt
        discussions.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        
        // Convert fileKeys to URLs
        const discussionsWithUrls = await convertFileKeysToUrlsInArray(discussions);
        
        return { success: true, data: discussionsWithUrls, usedFallback: true };
      } catch (fallbackError) {
        return { success: false, error: fallbackError };
      }
    }
    return { success: false, error };
  }
};

export const updateLectureDiscussion = async (discussionId, updateData) => {
  try {
    const docRef = doc(db, COLLECTIONS.LECTURE_DISCUSSIONS, discussionId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteLectureDiscussion = async (discussionId) => {
  try {
    const docRef = doc(db, COLLECTIONS.LECTURE_DISCUSSIONS, discussionId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Assignments Management
export const saveCourseAssignment = async (assignmentData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.COURSE_ASSIGNMENTS), {
      ...assignmentData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCourseAssignments = async (courseId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COURSE_ASSIGNMENTS),
      where('courseId', '==', courseId),
      orderBy('dueDate', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const assignments = [];
    querySnapshot.forEach((doc) => {
      assignments.push({ id: doc.id, ...doc.data() });
    });
    
    // Convert fileKeys to URLs (for assignment files)
    const assignmentsWithUrls = await convertFileKeysToUrlsInArray(assignments);
    
    return { success: true, data: assignmentsWithUrls };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Submissions Management
export const saveCourseSubmission = async (submissionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.COURSE_SUBMISSIONS), {
      ...submissionData,
      submittedAt: new Date(),
      status: 'submitted'
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserSubmissions = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COURSE_SUBMISSIONS),
      where('userId', '==', userId),
      orderBy('submittedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const submissions = [];
    querySnapshot.forEach((doc) => {
      submissions.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: submissions };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Certificates Management
export const saveCourseCertificate = async (certificateData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.COURSE_CERTIFICATES), {
      ...certificateData,
      issuedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserCertificates = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COURSE_CERTIFICATES),
      where('userId', '==', userId),
      orderBy('issuedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const certificates = [];
    querySnapshot.forEach((doc) => {
      certificates.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: certificates };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Materials Management
export const saveCourseMaterial = async (materialData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.COURSE_MATERIALS), {
      ...materialData,
      createdAt: new Date(),
      downloadCount: 0
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCourseMaterials = async (courseId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COURSE_MATERIALS),
      where('courseId', '==', courseId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const materials = [];
    querySnapshot.forEach((doc) => {
      materials.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: materials };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Live Classes Management
export const saveCourseLiveClass = async (liveClassData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.COURSE_LIVE_CLASSES), {
      ...liveClassData,
      createdAt: new Date(),
      participantCount: 0
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCourseLiveClasses = async (courseId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COURSE_LIVE_CLASSES),
      where('courseId', '==', courseId),
      orderBy('startTime', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const liveClasses = [];
    querySnapshot.forEach((doc) => {
      liveClasses.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: liveClasses };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Notifications Management
export const saveCourseNotification = async (notificationData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.COURSE_NOTIFICATIONS), {
      ...notificationData,
      createdAt: new Date(),
      read: false
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserCourseNotifications = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COURSE_NOTIFICATIONS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const notifications = [];
    querySnapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: notifications };
  } catch (error) {
    return { success: false, error };
  }
};

// Comprehensive Course Data Sync
export const syncUserCourseData = async (userId) => {
  try {
    // Get all course-related data for the user
    const [
      courseDataResult,
      discussionsResult,
      assignmentsResult,
      submissionsResult,
      certificatesResult,
      materialsResult,
      liveClassesResult,
      notificationsResult
    ] = await Promise.all([
      getUserCourseData(userId),
      getCourseDiscussions(userId),
      getCourseAssignments(userId),
      getUserSubmissions(userId),
      getUserCertificates(userId),
      getCourseMaterials(userId),
      getCourseLiveClasses(userId),
      getUserCourseNotifications(userId)
    ]);

    // Combine all course data
    const courseData = {
      courses: courseDataResult.success ? courseDataResult.data : null,
      discussions: discussionsResult.success ? discussionsResult.data : [],
      assignments: assignmentsResult.success ? assignmentsResult.data : [],
      submissions: submissionsResult.success ? submissionsResult.data : [],
      certificates: certificatesResult.success ? certificatesResult.data : [],
      materials: materialsResult.success ? materialsResult.data : [],
      liveClasses: liveClassesResult.success ? liveClassesResult.data : [],
      notifications: notificationsResult.success ? notificationsResult.data : [],
      lastSync: new Date().toISOString()
    };

    // Save combined data for quick access
    await saveUserCourseData(userId, {
      comprehensiveCourseData: courseData,
      lastSync: new Date().toISOString()
    });

    return { success: true, data: courseData };
  } catch (error) {
    return { success: false, error };
  }
};

// Initialize default course data for new users
export const initializeUserCourseData = async (userId) => {
  try {
    const defaultCourseData = {
      comprehensiveCourseData: {
        stats: {
          enrolledCourses: 0,
          completedCourses: 0,
          totalHours: 0,
          averageScore: 0,
          certificates: 0
        },
        enrolledCourses: [],
        assignments: [],
        certificates: [],
        studyMaterials: [],
        liveClasses: [],
        notifications: [],
        courseDiscussions: {}
      },
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    };

    await saveUserCourseData(userId, defaultCourseData);
    return { success: true, data: defaultCourseData };
  } catch (error) {
    return { success: false, error };
  }
};

// Vacancy and Exam Management Functions

// Save or update vacancy
export const saveVacancy = async (vacancyData) => {
  try {
    if (vacancyData.id) {
      // Update existing vacancy
      await updateDoc(doc(db, COLLECTIONS.VACANCIES, vacancyData.id), {
        ...vacancyData,
        updatedAt: new Date()
      });
      return { success: true, id: vacancyData.id };
    } else {
      // Create new vacancy
      const docRef = await addDoc(collection(db, COLLECTIONS.VACANCIES), {
        ...vacancyData,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'active'
      });
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Get all vacancies with optional filtering
export const getVacancies = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.VACANCIES);
    
    if (filters.category) {
      q = query(q, where('category', '==', filters.category));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.organization) {
      q = query(q, where('organization', '==', filters.organization));
    }
    
    q = query(q, orderBy('createdAt', 'desc'));
    
    const querySnapshot = await getDocs(q);
    const vacancies = [];
    querySnapshot.forEach((doc) => {
      vacancies.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: vacancies };
  } catch (error) {
    return { success: false, error };
  }
};

// Get single vacancy by ID
export const getVacancyById = async (vacancyId) => {
  try {
    const docRef = doc(db, COLLECTIONS.VACANCIES, vacancyId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: 'Vacancy not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Update vacancy
export const updateVacancy = async (vacancyId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.VACANCIES, vacancyId), {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Delete vacancy
export const deleteVacancy = async (vacancyId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.VACANCIES, vacancyId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Save exam result
export const saveExamResult = async (resultData) => {
  try {
    const resultRef = doc(db, 'examResults', resultData.id || generateId());
    const resultWithMetadata = {
      ...resultData,
      id: resultRef.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: resultData.status || 'published'
    };
    
    await setDoc(resultRef, resultWithMetadata);
    return { success: true, data: resultWithMetadata };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getExamResults = async (filters = {}) => {
  try {
    let resultsQuery = collection(db, 'examResults');
    
    if (filters.status) {
      resultsQuery = query(resultsQuery, where('status', '==', filters.status));
    }
    if (filters.organization) {
      resultsQuery = query(resultsQuery, where('organization', '==', filters.organization));
    }
    
    const snapshot = await getDocs(resultsQuery);
    const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return { success: true, data: results };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateExamResult = async (resultId, updates) => {
  try {
    const resultRef = doc(db, 'examResults', resultId);
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(resultRef, updateData);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteExamResult = async (resultId) => {
  try {
    const resultRef = doc(db, 'examResults', resultId);
    await deleteDoc(resultRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Save exam notification
export const saveExamNotification = async (notificationData) => {
  try {
    const notificationRef = doc(db, 'examNotifications', notificationData.id || generateId());
    const notificationWithMetadata = {
      ...notificationData,
      id: notificationRef.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: notificationData.status || 'active'
    };
    
    await setDoc(notificationRef, notificationWithMetadata);
    return { success: true, data: notificationWithMetadata };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getExamNotifications = async (filters = {}) => {
  try {
    let notificationsQuery = collection(db, 'examNotifications');
    
    if (filters.status) {
      notificationsQuery = query(notificationsQuery, where('status', '==', filters.status));
    }
    if (filters.type) {
      notificationsQuery = query(notificationsQuery, where('type', '==', filters.type));
    }
    if (filters.organization) {
      notificationsQuery = query(notificationsQuery, where('organization', '==', filters.organization));
    }
    
    const snapshot = await getDocs(notificationsQuery);
    const notifications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return { success: true, data: notifications };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateExamNotification = async (notificationId, updates) => {
  try {
    const notificationRef = doc(db, 'examNotifications', notificationId);
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(notificationRef, updateData);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteExamNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'examNotifications', notificationId);
    await deleteDoc(notificationRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Save application form
export const saveApplicationForm = async (formData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.APPLICATION_FORMS), {
      ...formData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

// Get application forms
export const getApplicationForms = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.APPLICATION_FORMS);
    
    if (filters.vacancyId) {
      q = query(q, where('vacancyId', '==', filters.vacancyId));
    }
    
    q = query(q, orderBy('createdAt', 'desc'));
    
    const querySnapshot = await getDocs(q);
    const forms = [];
    querySnapshot.forEach((doc) => {
      forms.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: forms };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Management Functions
export const saveCourse = async (courseData) => {
  try {
    const courseRef = doc(db, COLLECTIONS.COURSES, courseData.id || generateId());
    const courseWithMetadata = {
      ...courseData,
      id: courseRef.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: courseData.status || 'draft',
      enrollmentCount: courseData.enrollmentCount || 0,
      rating: courseData.rating || 0,
      totalRatings: courseData.totalRatings || 0
    };
    
    await setDoc(courseRef, courseWithMetadata);
    return { success: true, data: courseWithMetadata };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCourses = async (filters = {}) => {
  try {
    let coursesQuery = collection(db, COLLECTIONS.COURSES);
    
    // Apply filters
    if (filters.status) {
      coursesQuery = query(coursesQuery, where('status', '==', filters.status));
    }
    if (filters.instructorId) {
      coursesQuery = query(coursesQuery, where('instructorId', '==', filters.instructorId));
    }
    if (filters.category) {
      coursesQuery = query(coursesQuery, where('category', '==', filters.category));
    }
    
    const snapshot = await getDocs(coursesQuery);
    const courses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return { success: true, data: courses };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCourseById = async (courseId) => {
  try {
    const courseRef = doc(db, COLLECTIONS.COURSES, courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (courseDoc.exists()) {
      return { success: true, data: { id: courseDoc.id, ...courseDoc.data() } };
    } else {
      return { success: false, error: 'Course not found' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateCourse = async (courseId, updates) => {
  try {
    const courseRef = doc(db, COLLECTIONS.COURSES, courseId);
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(courseRef, updateData);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteCourse = async (courseId) => {
  try {
    const courseRef = doc(db, COLLECTIONS.COURSES, courseId);
    await deleteDoc(courseRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Instructor Management Functions
export const saveInstructor = async (instructorData) => {
  try {
    const instructorRef = doc(db, COLLECTIONS.INSTRUCTORS, instructorData.id || generateId());
    const instructorWithMetadata = {
      ...instructorData,
      id: instructorRef.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: instructorData.status || 'pending',
      totalCourses: instructorData.totalCourses || 0,
      totalStudents: instructorData.totalStudents || 0,
      rating: instructorData.rating || 0,
      totalRatings: instructorData.totalRatings || 0
    };
    
    await setDoc(instructorRef, instructorWithMetadata);
    return { success: true, data: instructorWithMetadata };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getInstructors = async (filters = {}) => {
  try {
    let instructorsQuery = collection(db, COLLECTIONS.INSTRUCTORS);
    
    if (filters.status) {
      instructorsQuery = query(instructorsQuery, where('status', '==', filters.status));
    }
    
    const snapshot = await getDocs(instructorsQuery);
    const instructors = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return { success: true, data: instructors };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteInstructor = async (instructorId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.INSTRUCTORS, instructorId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Course Enrollment Functions
export const enrollUserInCourse = async (userId, courseId, enrollmentData = {}) => {
  try {
    const enrollmentRef = doc(db, 'enrollments', generateId());
    const enrollment = {
      id: enrollmentRef.id,
      userId,
      courseId,
      enrolledAt: new Date().toISOString(),
      status: 'active',
      progress: 0,
      completedLessons: 0,
      totalLessons: enrollmentData.totalLessons || 0,
      lastAccessed: new Date().toISOString(),
      ...enrollmentData
    };
    
    await setDoc(enrollmentRef, enrollment);
    
    // Update course enrollment count
    const courseRef = doc(db, 'courses', courseId);
    await updateDoc(courseRef, {
      enrollmentCount: increment(1)
    });
    
    return { success: true, data: enrollment };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserEnrollments = async (userId) => {
  try {
    const enrollmentsQuery = query(
      collection(db, 'enrollments'),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(enrollmentsQuery);
    const enrollments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return { success: true, data: enrollments };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateEnrollmentProgress = async (enrollmentId, progressData) => {
  try {
    const enrollmentRef = doc(db, 'enrollments', enrollmentId);
    const updateData = {
      ...progressData,
      lastAccessed: new Date().toISOString()
    };
    
    await updateDoc(enrollmentRef, updateData);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Course Analytics Functions
export const getCourseAnalytics = async (courseId) => {
  try {
    // Get enrollments for this course
    const enrollmentsQuery = query(
      collection(db, 'enrollments'),
      where('courseId', '==', courseId)
    );
    
    const snapshot = await getDocs(enrollmentsQuery);
    const enrollments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Calculate analytics
    const totalEnrollments = enrollments.length;
    const activeEnrollments = enrollments.filter(e => e.status === 'active').length;
    const completedEnrollments = enrollments.filter(e => e.status === 'completed').length;
    const averageProgress = enrollments.length > 0 
      ? enrollments.reduce((sum, e) => sum + (e.progress || 0), 0) / enrollments.length 
      : 0;
    
    const analytics = {
      totalEnrollments,
      activeEnrollments,
      completedEnrollments,
      averageProgress: Math.round(averageProgress * 100) / 100,
      completionRate: totalEnrollments > 0 ? (completedEnrollments / totalEnrollments) * 100 : 0
    };
    
    return { success: true, data: analytics };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Course Content Management
export const saveCourseContent = async (courseId, contentData) => {
  try {
    const contentRef = doc(db, COLLECTIONS.COURSE_CONTENT, generateId());
    const content = {
      id: contentRef.id,
      courseId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...contentData
    };
    
    await setDoc(contentRef, content);
    return { success: true, data: content };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCourseContent = async (courseId) => {
  try {
    const contentQuery = query(
      collection(db, COLLECTIONS.COURSE_CONTENT),
      where('courseId', '==', courseId)
    );
    
    const snapshot = await getDocs(contentQuery);
    const content = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return { success: true, data: content };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Lecture Management Functions
export const saveLecture = async (courseId, lectureData) => {
  try {
    if (!courseId) {
      throw new Error('Course ID is required');
    }
    
    const lectureRef = doc(db, COLLECTIONS.LECTURES, generateId());
    const lecture = {
      id: lectureRef.id,
      courseId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      order: lectureData.order || 0,
      status: lectureData.status || 'draft',
      views: 0,
      duration: lectureData.duration || 0,
      ...lectureData
    };
    
    await setDoc(lectureRef, lecture);
    
    // Update course total lessons count
    try {
      const courseRef = doc(db, COLLECTIONS.COURSES, courseId);
      await updateDoc(courseRef, {
        totalLessons: increment(1),
        updatedAt: new Date().toISOString()
      });
    } catch (courseUpdateError) {
      // Don't fail the entire operation if course update fails
    }
    
    return { success: true, data: lecture };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getLectures = async (courseId) => {
  try {
    // Use the enhanced query utility with automatic fallback
    const result = await queryWithFallback(COLLECTIONS.LECTURES, { courseId }, {
      orderBy: { field: 'order', direction: 'asc' },
      useFallback: true,
      maxRetries: 3
    });
    
    if (result.success) {
      return result;
    } else {
      // Check if it's an index error
      if (result.error.code === 'failed-precondition' || result.error.message.includes('index')) {
        // Index error detected, returning empty result
        
        // Return empty result instead of failing
        return { success: true, data: [], indexError: true };
      }
      
      return { success: false, error: result.error };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateLecture = async (lectureId, updates) => {
  try {
    const lectureRef = doc(db, COLLECTIONS.LECTURES, lectureId);
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(lectureRef, updateData);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteLecture = async (lectureId, courseId) => {
  try {
    if (!lectureId) {
      throw new Error('Lecture ID is required');
    }
    
    const lectureRef = doc(db, COLLECTIONS.LECTURES, lectureId);
    await deleteDoc(lectureRef);
    
    // Update course total lessons count
    if (courseId) {
      try {
        const courseRef = doc(db, COLLECTIONS.COURSES, courseId);
        await updateDoc(courseRef, {
          totalLessons: increment(-1),
          updatedAt: new Date().toISOString()
        });
      } catch (courseUpdateError) {
        // Don't fail the entire operation if course update fails
      }
    }
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getLectureById = async (lectureId) => {
  try {
    const lectureRef = doc(db, COLLECTIONS.LECTURES, lectureId);
    const lectureDoc = await getDoc(lectureRef);
    
    if (lectureDoc.exists()) {
      return { success: true, data: { id: lectureDoc.id, ...lectureDoc.data() } };
    } else {
      return { success: false, error: 'Lecture not found' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== RESOURCE MANAGEMENT FUNCTIONS =====

// Save or update resource
export const saveResource = async (resourceData) => {
  try {
    // Remove undefined values to prevent Firestore errors
    // Keep null values for updates (they're allowed), but remove them for new documents
    const dataToSave = Object.fromEntries(
      Object.entries(resourceData).filter(([_, value]) => value !== undefined)
    );
    
    // For new documents, remove null coachingId (Firestore doesn't allow null in addDoc)
    // For updates, null is allowed, so we keep it
    if (!resourceData.id && dataToSave.coachingId === null) {
      delete dataToSave.coachingId;
    }
    
    if (resourceData.id) {
      // Update existing resource
      await updateDoc(doc(db, COLLECTIONS.RESOURCES, resourceData.id), {
        ...dataToSave,
        updatedAt: new Date()
      });
      return { success: true, id: resourceData.id };
    } else {
      // Create new resource
      const resourceToSave = {
        ...dataToSave,
        createdAt: new Date(),
        updatedAt: new Date(),
        downloads: 0,
        views: 0,
        rating: 0,
        totalRatings: 0,
        status: resourceData.status || 'active'
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.RESOURCES), resourceToSave);
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get all resources with optional filtering
export const getResources = async (coachingIdOrFilters = {}) => {
  try {
    // Handle both old format (filters object) and new format (coachingId string)
    const filters = typeof coachingIdOrFilters === 'string' 
      ? { coachingId: coachingIdOrFilters }
      : coachingIdOrFilters;
    
    let q = collection(db, COLLECTIONS.RESOURCES);
    
    // Multi-tenancy: Filter by coachingId if provided
    if (filters.coachingId) {
      q = query(q, where('coachingId', '==', filters.coachingId));
    }
    
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }
    if (filters.category) {
      q = query(q, where('category', '==', filters.category));
    }
    if (filters.subject) {
      q = query(q, where('subject', '==', filters.subject));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.accessLevel) {
      q = query(q, where('accessLevel', '==', filters.accessLevel));
    }
    
    // Try with orderBy first, fallback to without if index error
    try {
      q = query(q, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const resources = [];
      querySnapshot.forEach((doc) => {
        resources.push({ id: doc.id, ...doc.data() });
      });
      // Sort client-side: admin resources first, then by createdAt
      resources.sort((a, b) => {
        // Prioritize admin resources (coachingId is null/undefined) over coaching resources
        const aIsAdmin = !a.coachingId;
        const bIsAdmin = !b.coachingId;
        
        if (aIsAdmin && !bIsAdmin) return -1; // Admin resources first
        if (!aIsAdmin && bIsAdmin) return 1;  // Coaching resources after admin
        
        // If both are same type, sort by createdAt descending
        const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
        return bTime - aTime; // Descending
      });
      return { success: true, data: resources };
    } catch (orderByError) {
      // If orderBy fails (likely missing index), try without it
      if (orderByError.code === 'failed-precondition' || orderByError.message?.includes('index')) {
        // Remove orderBy and try again
        let q2 = collection(db, COLLECTIONS.RESOURCES);
        if (filters.coachingId) {
          q2 = query(q2, where('coachingId', '==', filters.coachingId));
        }
        if (filters.type) {
          q2 = query(q2, where('type', '==', filters.type));
        }
        if (filters.category) {
          q2 = query(q2, where('category', '==', filters.category));
        }
        if (filters.subject) {
          q2 = query(q2, where('subject', '==', filters.subject));
        }
        if (filters.status) {
          q2 = query(q2, where('status', '==', filters.status));
        }
        if (filters.accessLevel) {
          q2 = query(q2, where('accessLevel', '==', filters.accessLevel));
        }
        
        const querySnapshot = await getDocs(q2);
        const resources = [];
        querySnapshot.forEach((doc) => {
          resources.push({ id: doc.id, ...doc.data() });
        });
        // Sort client-side: admin resources first, then by createdAt
        resources.sort((a, b) => {
          // Prioritize admin resources (coachingId is null/undefined) over coaching resources
          const aIsAdmin = !a.coachingId;
          const bIsAdmin = !b.coachingId;
          
          if (aIsAdmin && !bIsAdmin) return -1; // Admin resources first
          if (!aIsAdmin && bIsAdmin) return 1;  // Coaching resources after admin
          
          // If both are same type, sort by createdAt descending
          const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
          const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
          return bTime - aTime; // Descending
        });
        
        // Convert fileKeys to URLs
        const resourcesWithUrls = await convertFileKeysToUrlsInArray(resources);
        
        return { success: true, data: resourcesWithUrls };
      }
      throw orderByError; // Re-throw if it's not an index error
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get resources for students - filters by coaching and visibility
export const getResourcesForStudent = async (studentEmail, studentCoachingId) => {
  try {
    // Get all public admin resources (coachingId is null)
    const adminResourcesQuery = query(
      collection(db, COLLECTIONS.RESOURCES),
      where('coachingId', '==', null),
      where('isPublic', '==', true)
    );
    
    // Get coaching resources (either public OR private if student belongs to that coaching)
    let coachingResourcesQuery;
    if (studentCoachingId) {
      coachingResourcesQuery = query(
        collection(db, COLLECTIONS.RESOURCES),
        where('coachingId', '==', studentCoachingId)
      );
    }
    
    const [adminSnapshot, coachingSnapshot] = await Promise.all([
      getDocs(adminResourcesQuery),
      studentCoachingId ? getDocs(coachingResourcesQuery) : Promise.resolve({ docs: [] })
    ]);
    
    const resources = [];
    
    // Add admin resources (always public)
    adminSnapshot.forEach((doc) => {
      resources.push({ id: doc.id, ...doc.data() });
    });
    
    // Add coaching resources (all resources from student's coaching)
    coachingSnapshot.docs?.forEach((doc) => {
      resources.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort: admin resources first, then by createdAt
    resources.sort((a, b) => {
      const aIsAdmin = !a.coachingId;
      const bIsAdmin = !b.coachingId;
      
      if (aIsAdmin && !bIsAdmin) return -1; // Admin resources first
      if (!aIsAdmin && bIsAdmin) return 1;  // Coaching resources after admin
      
      // If both are same type, sort by createdAt descending
      const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
      const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
      return bTime - aTime; // Descending
    });
    
    // Convert fileKeys to URLs
    const resourcesWithUrls = await convertFileKeysToUrlsInArray(resources);
    
    return { success: true, data: resourcesWithUrls };
  } catch (error) {
    console.error('Error getting resources for student:', error);
    return { success: false, error: error.message || error };
  }
};

// Get single resource by ID
export const getResourceById = async (resourceId) => {
  try {
    const docRef = doc(db, COLLECTIONS.RESOURCES, resourceId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = { id: docSnap.id, ...docSnap.data() };
      const dataWithUrls = await convertFileKeysToUrls(data);
      return { success: true, data: dataWithUrls };
    } else {
      return { success: false, error: 'Resource not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Update resource
export const updateResource = async (resourceId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.RESOURCES, resourceId), {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Delete resource
export const deleteResource = async (resourceId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.RESOURCES, resourceId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Increment resource download count
export const incrementResourceDownloads = async (resourceId) => {
  try {
    const docRef = doc(db, COLLECTIONS.RESOURCES, resourceId);
    await updateDoc(docRef, {
      downloads: increment(1),
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Increment resource view count
export const incrementResourceViews = async (resourceId) => {
  try {
    const docRef = doc(db, COLLECTIONS.RESOURCES, resourceId);
    await updateDoc(docRef, {
      views: increment(1),
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Rate resource
export const rateResource = async (resourceId, rating, userId) => {
  try {
    const docRef = doc(db, COLLECTIONS.RESOURCES, resourceId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const resourceData = docSnap.data();
      const currentRating = resourceData.rating || 0;
      const totalRatings = resourceData.totalRatings || 0;
      
      // Calculate new average rating
      const newTotalRatings = totalRatings + 1;
      const newAverageRating = ((currentRating * totalRatings) + rating) / newTotalRatings;
      
      await updateDoc(docRef, {
        rating: Math.round(newAverageRating * 10) / 10, // Round to 1 decimal place
        totalRatings: newTotalRatings,
        updatedAt: new Date()
      });
      
      return { success: true };
    } else {
      return { success: false, error: 'Resource not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// ===== TEST MANAGEMENT FUNCTIONS =====

// Save new test
export const saveTest = async (testData) => {
  try {
    // Filter out undefined values to prevent Firebase errors
    const cleanData = Object.fromEntries(
      Object.entries(testData || {}).filter(([, value]) => value !== undefined)
    );
    
    // Preserve coachingId if provided (for multi-tenancy)
    const docRef = await addDoc(collection(db, COLLECTIONS.TESTS), {
      ...cleanData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: testData?.status || 'draft',
      totalAttempts: 0,
      averageScore: 0,
      totalQuestions: testData?.questions ? testData.questions.length : 0
    });
    
    // Auto-increment test count in publicStats for homepage
    try {
      const { incrementTestCount } = await import('../utils/realStats');
      await incrementTestCount();
    } catch (statsError) {
      console.warn('Failed to update test count stats:', statsError);
      // Don't fail the test creation if stats update fails
    }
    
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

// Get all tests with optional filtering
export const getTests = async (coachingIdOrFilters = {}) => {
  try {
    // Handle both old format (filters object) and new format (coachingId string)
    const filters = typeof coachingIdOrFilters === 'string' 
      ? { coachingId: coachingIdOrFilters }
      : coachingIdOrFilters;
    
    let q = collection(db, COLLECTIONS.TESTS);
    
    // Multi-tenancy: Always filter by coachingId
    // If coachingId is provided, filter for that specific coaching
    // If coachingId is null, filter for platform-level tests only (coachingId == null)
    if (filters.coachingId !== undefined) {
      q = query(q, where('coachingId', '==', filters.coachingId));
    }
    
    if (filters.exam) {
      q = query(q, where('exam', '==', filters.exam));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.difficulty) {
      q = query(q, where('difficulty', '==', filters.difficulty));
    }
    
    // Try with orderBy first, fallback to without if index error
    try {
      q = query(q, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const tests = [];
      querySnapshot.forEach((doc) => {
        tests.push({ id: doc.id, ...doc.data() });
      });
      // Sort client-side if needed (already sorted by Firestore)
      return { success: true, data: tests };
    } catch (orderByError) {
      // If orderBy fails (likely missing index), try without it
      if (orderByError.code === 'failed-precondition' || orderByError.message?.includes('index')) {
        // Remove orderBy and try again
        let q2 = collection(db, COLLECTIONS.TESTS);
        if (filters.coachingId !== undefined) {
          q2 = query(q2, where('coachingId', '==', filters.coachingId));
        }
        if (filters.exam) {
          q2 = query(q2, where('exam', '==', filters.exam));
        }
        if (filters.status) {
          q2 = query(q2, where('status', '==', filters.status));
        }
        if (filters.difficulty) {
          q2 = query(q2, where('difficulty', '==', filters.difficulty));
        }
        
        const querySnapshot = await getDocs(q2);
        const tests = [];
        querySnapshot.forEach((doc) => {
          tests.push({ id: doc.id, ...doc.data() });
        });
        // Sort client-side by createdAt
        tests.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
          const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
          return bTime - aTime; // Descending
        });
        return { success: true, data: tests };
      }
      throw orderByError; // Re-throw if it's not an index error
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get single test by ID
export const getTestById = async (testId) => {
  try {
    if (!testId) {
      return { success: false, error: 'Test ID is required' };
    }
    // React Router decodes URL params automatically, so testId should already be decoded
    // Try both as-is and decoded (in case it's still encoded)
    const testIdsToTry = [testId];
    if (testId.includes('%')) {
      try {
        testIdsToTry.unshift(decodeURIComponent(testId));
      } catch (e) {
        // Ignore decode errors
      }
    }
    
    for (const testIdToTry of testIdsToTry) {
      const docRef = doc(db, COLLECTIONS.TESTS, testIdToTry);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { success: true, data: { ...docSnap.data(), id: docSnap.id } };
      }
    }
    return { success: false, error: `Test not found with ID: ${testId}` };
  } catch (error) {
    return { success: false, error: error.message || 'Unknown error' };
  }
};

// Update test
export const updateTest = async (testId, updateData) => {
  try {
    // Avoid writing undefined fields (which can cause permission issues)
    const cleaned = Object.fromEntries(
      Object.entries(updateData || {}).filter(([, v]) => v !== undefined)
    );
    if (cleaned.questions) {
      cleaned.totalQuestions = cleaned.questions.length;
    }
    cleaned.updatedAt = new Date();
    await updateDoc(doc(db, COLLECTIONS.TESTS, testId), cleaned);
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Delete test
export const deleteTest = async (testId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.TESTS, testId));
    
    // Auto-decrement test count in publicStats for homepage
    try {
      const { decrementTestCount } = await import('../utils/realStats');
      await decrementTestCount();
    } catch (statsError) {
      console.warn('Failed to update test count stats:', statsError);
      // Don't fail the test deletion if stats update fails
    }
    
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== CONTENT MANAGEMENT FUNCTIONS =====

// Note: saveQuestionToBank and getQuestionsFromBank functions are already defined above
// Additional content management functions below:

// Get single question by ID
export const getQuestionById = async (questionId) => {
  try {
    const docRef = doc(db, COLLECTIONS.QUESTION_BANK, questionId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: 'Question not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Update question
export const updateQuestion = async (questionId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.QUESTION_BANK, questionId), {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Delete question
export const deleteQuestion = async (questionId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.QUESTION_BANK, questionId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== LIVE CLASS MANAGEMENT FUNCTIONS =====

// Save new live class
export const saveLiveClass = async (liveClassData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.LIVE_CLASSES), {
      ...liveClassData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: liveClassData.status || 'scheduled',
      participantCount: 0,
      maxParticipants: liveClassData.maxParticipants || 50,
      isActive: false
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

// Get all live classes with optional filtering
export const getLiveClasses = async (filters = {}) => {
  try {
    // Use queryWithFallback to handle index errors gracefully
    const queryFilters = {};
    if (filters.status) {
      queryFilters.status = filters.status;
    }
    if (filters.exam) {
      queryFilters.exam = filters.exam;
    }
    if (filters.subject) {
      queryFilters.subject = filters.subject;
    }
    if (filters.instructorId) {
      queryFilters.instructorId = filters.instructorId;
    }
    
    const result = await queryWithFallback(COLLECTIONS.LIVE_CLASSES, queryFilters, {
      orderBy: { field: 'scheduledAt', direction: 'asc' },
      useFallback: true,
      maxRetries: 3
    });
    
    if (result.success) {
      return result;
    } else {
      // Check if it's an index error
      if (result.error && (result.error.code === 'failed-precondition' || result.error.message?.includes('index'))) {
        // Index error - return empty result instead of failing
        return { success: true, data: [], indexError: true };
      }
      return { success: false, error: result.error };
    }
  } catch (error) {
    // If index error, return empty array instead of failing
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      return { success: true, data: [], indexError: true };
    }
    return { success: false, error };
  }
};

// Get single live class by ID
export const getLiveClassById = async (liveClassId) => {
  try {
    const docRef = doc(db, COLLECTIONS.LIVE_CLASSES, liveClassId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: 'Live class not found' };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Update live class
export const updateLiveClass = async (liveClassId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.LIVE_CLASSES, liveClassId), {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Delete live class
export const deleteLiveClass = async (liveClassId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.LIVE_CLASSES, liveClassId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Register user for live class
export const registerForLiveClass = async (liveClassId, userId, userData) => {
  try {
    const registrationRef = doc(db, COLLECTIONS.LIVE_CLASS_REGISTRATIONS, `${liveClassId}_${userId}`);
    
    await setDoc(registrationRef, {
      liveClassId,
      userId,
      userName: userData.name || 'Anonymous',
      userEmail: userData.email || '',
      registeredAt: new Date(),
      status: 'registered'
    });
    
    // Update live class participant count
    const liveClassRef = doc(db, COLLECTIONS.LIVE_CLASSES, liveClassId);
    const liveClassDoc = await getDoc(liveClassRef);
    
    if (liveClassDoc.exists()) {
      const liveClassData = liveClassDoc.data();
      await updateDoc(liveClassRef, {
        participantCount: liveClassData.participantCount + 1,
        updatedAt: new Date()
      });
    }
    
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Get live class registrations
export const getLiveClassRegistrations = async (liveClassId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.LIVE_CLASS_REGISTRATIONS),
      where('liveClassId', '==', liveClassId),
      orderBy('registeredAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const registrations = [];
    querySnapshot.forEach((doc) => {
      registrations.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: registrations };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== REAL-TIME LISTENERS =====

// Real-time listener for resources
export const subscribeToResources = (callback, filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.RESOURCES);
    
    // Multi-tenancy: Filter by coachingId if provided
    if (filters.coachingId) {
      q = query(q, where('coachingId', '==', filters.coachingId));
    }
    
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    // Try with orderBy, fallback if index error
    let finalQuery = q;
    try {
      finalQuery = query(q, orderBy('createdAt', 'desc'));
    } catch (orderByError) {
      finalQuery = q;
    }
    
    return onSnapshot(finalQuery, (snapshot) => {
      const resources = [];
      snapshot.forEach((doc) => {
        resources.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort client-side: admin resources first, then by createdAt
      resources.sort((a, b) => {
        // Prioritize admin resources (coachingId is null/undefined) over coaching resources
        const aIsAdmin = !a.coachingId;
        const bIsAdmin = !b.coachingId;
        
        if (aIsAdmin && !bIsAdmin) return -1; // Admin resources first
        if (!aIsAdmin && bIsAdmin) return 1;  // Coaching resources after admin
        
        // If both are same type, sort by createdAt descending
        const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
        return bTime - aTime; // Descending
      });
      
      callback({ success: true, data: resources });
    }, (error) => {
      // If error is about missing index, try without orderBy
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        let q2 = collection(db, COLLECTIONS.RESOURCES);
        if (filters.coachingId) {
          q2 = query(q2, where('coachingId', '==', filters.coachingId));
        }
        if (filters.type) {
          q2 = query(q2, where('type', '==', filters.type));
        }
        if (filters.status) {
          q2 = query(q2, where('status', '==', filters.status));
        }
        
        return onSnapshot(q2, (snapshot) => {
          const resources = [];
          snapshot.forEach((doc) => {
            resources.push({ id: doc.id, ...doc.data() });
          });
          // Sort client-side: admin resources first, then by createdAt
          resources.sort((a, b) => {
            // Prioritize admin resources (coachingId is null/undefined) over coaching resources
            const aIsAdmin = !a.coachingId;
            const bIsAdmin = !b.coachingId;
            
            if (aIsAdmin && !bIsAdmin) return -1; // Admin resources first
            if (!aIsAdmin && bIsAdmin) return 1;  // Coaching resources after admin
            
            // If both are same type, sort by createdAt descending
            const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
            const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
            return bTime - aTime;
          });
          callback({ success: true, data: resources });
        }, (retryError) => {
          callback({ success: false, error: retryError });
        });
      }
      callback({ success: false, error });
    });
  } catch (error) {
    return null;
  }
};

// Real-time listener for tests
export const subscribeToTests = (callback, filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.TESTS);
    
    // Multi-tenancy: Filter by coachingId if provided
    if (filters.coachingId) {
      q = query(q, where('coachingId', '==', filters.coachingId));
    }
    
    if (filters.exam) {
      q = query(q, where('exam', '==', filters.exam));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    // Try with orderBy first
    let finalQuery = q;
    try {
      finalQuery = query(q, orderBy('createdAt', 'desc'));
    } catch (orderByError) {
      // If orderBy fails at query construction, use without it
      finalQuery = q;
    }
    
    return onSnapshot(finalQuery, (snapshot) => {
      const tests = [];
      snapshot.forEach((doc) => {
        tests.push({ ...doc.data(), id: doc.id });
      });
      
      // Always sort client-side to ensure correct order
      tests.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
        return bTime - aTime; // Descending
      });
      
      callback({ success: true, data: tests });
    }, (error) => {
      // If error is about missing index, try without orderBy
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        // Set up fallback query without orderBy
        let q2 = collection(db, COLLECTIONS.TESTS);
        if (filters.coachingId) {
          q2 = query(q2, where('coachingId', '==', filters.coachingId));
        }
        if (filters.exam) {
          q2 = query(q2, where('exam', '==', filters.exam));
        }
        if (filters.status) {
          q2 = query(q2, where('status', '==', filters.status));
        }
        
        // Return the fallback subscription
        const fallbackUnsubscribe = onSnapshot(q2, (snapshot) => {
          const tests = [];
          snapshot.forEach((doc) => {
            tests.push({ ...doc.data(), id: doc.id });
          });
          // Sort client-side
          tests.sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
            const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
            return bTime - aTime;
          });
          callback({ success: true, data: tests });
        }, (retryError) => {
          callback({ success: false, error: retryError });
        });
        
        // Return the fallback unsubscribe function
        return fallbackUnsubscribe;
      }
      callback({ success: false, error });
    });
  } catch (error) {
    callback({ success: false, error: error.message || error });
    return null;
  }
};

// Real-time listener for questions
export const subscribeToQuestions = (callback, filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.QUESTION_BANK);
    
    if (filters.exam) {
      q = query(q, where('exam', '==', filters.exam));
    }
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    q = query(q, orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
      const questions = [];
      snapshot.forEach((doc) => {
        questions.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: questions });
    }, (error) => {
      callback({ success: false, error });
    });
  } catch (error) {
    return null;
  }
};

// Real-time listener for live classes
export const subscribeToLiveClasses = (callback, filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.LIVE_CLASSES);
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.exam) {
      q = query(q, where('exam', '==', filters.exam));
    }
    
    // Try with orderBy, but handle index errors
    try {
      q = query(q, orderBy('scheduledAt', 'asc'));
    } catch (error) {
      // If orderBy fails, continue without it
      console.warn('OrderBy failed for live classes, continuing without ordering:', error);
    }
    
    return onSnapshot(q, (snapshot) => {
      const liveClasses = [];
      snapshot.forEach((doc) => {
        liveClasses.push({ id: doc.id, ...doc.data() });
      });
      // Sort in memory if orderBy wasn't used
      if (!filters.status && !filters.exam) {
        liveClasses.sort((a, b) => {
          const aTime = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0;
          const bTime = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0;
          return aTime - bTime;
        });
      }
      callback({ success: true, data: liveClasses });
    }, (error) => {
      // If index error, return empty array
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        callback({ success: true, data: [] });
      } else {
        callback({ success: false, error });
      }
    });
  } catch (error) {
    // Return empty array on error
    callback({ success: true, data: [] });
    return null;
  }
};

// ===== BULK OPERATIONS =====

// Bulk save resources
export const bulkSaveResources = async (resources, batchSize = 500) => {
  try {
    const batches = [];
    for (let i = 0; i < resources.length; i += batchSize) {
      batches.push(resources.slice(i, i + batchSize));
    }
    
    const results = [];
    for (const batch of batches) {
      const batchOperations = batch.map(resource => ({
        type: 'set',
        collection: COLLECTIONS.RESOURCES,
        docId: resource.id || generateId(),
        data: {
          ...resource,
          createdAt: new Date(),
          updatedAt: new Date(),
          downloads: 0,
          views: 0,
          rating: 0,
          totalRatings: 0,
          status: resource.status || 'active'
        }
      }));
      
      const result = await batchWrite(batchOperations);
      results.push(result);
      
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return { success: true, results };
  } catch (error) {
    return { success: false, error };
  }
};

// Bulk save tests
export const bulkSaveTests = async (tests, batchSize = 100) => {
  try {
    const batches = [];
    for (let i = 0; i < tests.length; i += batchSize) {
      batches.push(tests.slice(i, i + batchSize));
    }
    
    const results = [];
    let totalSaved = 0;
    
    for (const batch of batches) {
      const batchOperations = batch.map(test => ({
        type: 'set',
        collection: COLLECTIONS.TESTS,
        docId: test.id || generateId(),
        data: {
          ...test,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: test.status || 'draft',
          totalAttempts: 0,
          averageScore: 0,
          totalQuestions: test.questions ? test.questions.length : 0
        }
      }));
      
      const result = await batchWrite(batchOperations);
      results.push(result);
      
      if (result.success) {
        totalSaved += batch.length;
      }
      
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Auto-increment test count in publicStats for homepage
    if (totalSaved > 0) {
      try {
        const statsRef = doc(db, 'publicStats', 'homePageStats');
        const statsDoc = await getDoc(statsRef);
        
        if (statsDoc.exists()) {
          await setDoc(statsRef, {
            totalTestCount: increment(totalSaved),
            lastUpdated: new Date()
          }, { merge: true });
        } else {
          await setDoc(statsRef, {
            totalTestCount: totalSaved,
            users: 0,
            userCount: 0,
            lastUpdated: new Date()
          });
        }
      } catch (statsError) {
        console.warn('Failed to update test count stats:', statsError);
      }
    }
    
    return { success: true, results };
  } catch (error) {
    return { success: false, error };
  }
};

// Bulk save questions
export const bulkSaveQuestions = async (questions, batchSize = 500) => {
  try {
    const batches = [];
    for (let i = 0; i < questions.length; i += batchSize) {
      batches.push(questions.slice(i, i + batchSize));
    }
    
    const results = [];
    for (const batch of batches) {
      const batchOperations = batch.map(question => ({
        type: 'set',
        collection: COLLECTIONS.QUESTION_BANK,
        docId: question.id || generateId(),
        data: {
          ...question,
          createdAt: new Date(),
          updatedAt: new Date(),
          usageCount: 0,
          averageAccuracy: 0,
          totalAttempts: 0,
          status: question.status || 'active'
        }
      }));
      
      const result = await batchWrite(batchOperations);
      results.push(result);
      
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return { success: true, results };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== DATA VALIDATION AND SANITIZATION =====

// Validate resource data
export const validateResourceData = (resourceData) => {
  const errors = [];
  
  if (!resourceData.title || resourceData.title.trim().length === 0) {
    errors.push('Title is required');
  }
  
  if (!resourceData.type || !['document', 'video', 'image', 'link'].includes(resourceData.type)) {
    errors.push('Valid type is required (document, video, image, link)');
  }
  
  if (!resourceData.fileUrl || resourceData.fileUrl.trim().length === 0) {
    errors.push('File URL is required');
  }
  
  if (!resourceData.category || resourceData.category.trim().length === 0) {
    errors.push('Category is required');
  }
  
  if (!resourceData.subject || resourceData.subject.trim().length === 0) {
    errors.push('Subject is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate test data
export const validateTestData = (testData) => {
  const errors = [];
  
  if (!testData.title || testData.title.trim().length === 0) {
    errors.push('Title is required');
  }
  
  if (!testData.exam || testData.exam.trim().length === 0) {
    errors.push('Exam is required');
  }
  
  if (!testData.duration || testData.duration === '' || testData.duration <= 0) {
    errors.push('Valid duration is required');
  }
  
  if (!testData.questions || testData.questions.length === 0) {
    errors.push('At least one question is required');
  }
  
  // Validate section configuration if dynamic sections are enabled
  if (testData.useDynamicSections && testData.sections) {
    const totalQuestions = testData.questions ? testData.questions.length : 0;
    let totalCovered = 0;
    const usedRanges = new Set();
    
    testData.sections.forEach((section, index) => {
      if (!section.name || section.name.trim().length === 0) {
        errors.push(`Section ${index + 1}: Name is required`);
      }
      
      if (!section.startQuestion || !section.endQuestion) {
        errors.push(`Section ${index + 1}: Question range is required`);
      } else {
        const start = parseInt(section.startQuestion);
        const end = parseInt(section.endQuestion);
        
        if (start > end) {
          errors.push(`Section ${index + 1}: Start question must be less than end question`);
        }
        if (start < 1 || end > totalQuestions) {
          errors.push(`Section ${index + 1}: Range must be between 1 and ${totalQuestions}`);
        }
        
        // Check for overlapping ranges
        const rangeKey = `${start}-${end}`;
        if (usedRanges.has(rangeKey)) {
          errors.push(`Section ${index + 1}: Question ranges cannot overlap`);
        }
        usedRanges.add(rangeKey);
        
        totalCovered += (end - start + 1);
      }
    });
    
    if (totalCovered !== totalQuestions) {
      errors.push(`Sections must cover exactly ${totalQuestions} questions. Currently covering ${totalCovered} questions.`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate question data
export const validateQuestionData = (questionData) => {
  const errors = [];
  
  // Allow questions with only images for certain question types
  const hasQuestionText = questionData.question && questionData.question.trim().length > 0;
  const hasQuestionImage = questionData.questionImage || questionData.questionImageUrl;
  
  if (!hasQuestionText && !hasQuestionImage) {
    errors.push('Question text or question image is required');
  }
  
  if (!questionData.type || !['multiple-choice', 'multiple-choice-multi', 'integer', 'floating-point', 'subjective', 'matrix-match', 'assertion-reason'].includes(questionData.type)) {
    errors.push('Valid question type is required');
  }
  
  if (!questionData.exam || questionData.exam.trim().length === 0) {
    errors.push('Exam is required');
  }
  
  if (['multiple-choice', 'multiple-choice-multi'].includes(questionData.type)) {
    if (!questionData.options || questionData.options.length < 2) {
      errors.push('At least 2 options are required for multiple choice questions');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Sanitize resource data
export const sanitizeResourceData = (resourceData) => {
  return {
    ...resourceData,
    title: resourceData.title ? resourceData.title.trim() : '',
    description: resourceData.description ? resourceData.description.trim() : '',
    category: resourceData.category ? resourceData.category.trim() : '',
    subject: resourceData.subject ? resourceData.subject.trim() : '',
    tags: resourceData.tags ? resourceData.tags.trim() : '',
    fileUrl: resourceData.fileUrl ? resourceData.fileUrl.trim() : '',
    thumbnailUrl: resourceData.thumbnailUrl ? resourceData.thumbnailUrl.trim() : '',
    accessLevel: resourceData.accessLevel || 'public',
    status: resourceData.status || 'active'
  };
};

// Sanitize test data
export const sanitizeTestData = (testData) => {
  return {
    ...testData,
    title: testData.title ? testData.title.trim() : '',
    description: testData.description ? testData.description.trim() : '',
    exam: testData.exam ? testData.exam.trim() : '',
    subject: testData.subject ? testData.subject.trim() : 'General',
    difficulty: testData.difficulty || 'Medium',
    status: testData.status || 'draft',
    duration: parseInt(testData.duration) || 60,
    totalQuestions: testData.questions ? testData.questions.length : 0,
    useDynamicSections: testData.useDynamicSections || false,
    sections: testData.sections || []
  };
};

// Sanitize question data
export const sanitizeQuestionData = (questionData) => {
  return {
    ...questionData,
    question: questionData.question ? questionData.question.trim() : '',
    explanation: questionData.explanation ? questionData.explanation.trim() : '',
    topic: questionData.topic ? questionData.topic.trim() : '',
    exam: questionData.exam ? questionData.exam.trim() : '',
    difficulty: questionData.difficulty || 'Medium',
    type: questionData.type || 'multiple-choice',
    status: questionData.status || 'active',
    options: questionData.options ? questionData.options.map(opt => opt.trim()) : [],
    markingScheme: questionData.markingScheme || {
      correct: 4,
      incorrect: -1,
      partial: 0
    }
  };
};

// Bulk update users
export const bulkUpdateUsers = async (userIds, updateData) => {
  try {
    const batch = writeBatch(db);
    
    userIds.forEach(userId => {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      batch.update(userRef, {
        ...updateData,
        updatedAt: new Date()
      });
    });
    
    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Export user data to CSV
export const exportUserData = async (users) => {
  try {
    const headers = [
      'ID', 'Email', 'Display Name', 'Role', 'Status', 'Created At', 
      'Last Login', 'Tests Taken', 'Average Score', 'Phone', 'Location'
    ];
    
    const csvData = users.map(user => [
      user.uid || user.id,
      user.email || '',
      user.displayName || '',
      user.role || '',
      user.isActive ? 'Active' : 'Inactive',
      user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '',
      user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : '',
      user.stats?.testsTaken || 0,
      user.stats?.averageScore || 0,
      user.profile?.phone || '',
      user.profile?.location || ''
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    return { success: true, data: csvContent };
  } catch (error) {
    return { success: false, error };
  }
};

// Import user data from CSV
export const importUserData = async (csvContent) => {
  try {
    const lines = csvContent.split('\n');
    const headers = lines[0].split(',').map(h => h.replace(/"/g, ''));
    const users = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.replace(/"/g, ''));
        const user = {};
        
        headers.forEach((header, index) => {
          user[header.toLowerCase().replace(/\s+/g, '')] = values[index];
        });
        
        users.push(user);
      }
    }
    
    const batch = writeBatch(db);
    const results = [];
    
    for (const user of users) {
      try {
        const userData = {
          email: user.email,
          displayName: user.displayname,
          role: user.role || 'user',
          isActive: user.status === 'Active',
          createdAt: new Date(),
          updatedAt: new Date(),
          profile: {
            phone: user.phone || '',
            location: user.location || ''
          },
          stats: {
            testsTaken: parseInt(user.teststaken) || 0,
            averageScore: parseFloat(user.averagescore) || 0
          }
        };
        
        const userRef = doc(db, COLLECTIONS.USERS, userData.email);
        batch.set(userRef, userData);
        results.push({ success: true, email: userData.email });
      } catch (error) {
        results.push({ success: false, email: user.email, error: error.message });
      }
    }
    
    await batch.commit();
    return { success: true, results };
  } catch (error) {
    return { success: false, error };
  }
};

// Get all users with pagination
export const getAllUsers = async (limit = 100, startAfter = null) => {
  try {
    let q = query(
      collection(db, COLLECTIONS.USERS),
      orderBy('createdAt', 'desc'),
      limit(limit)
    );
    
    if (startAfter) {
      q = query(q, startAfter(startAfter));
    }
    
    const querySnapshot = await getDocs(q);
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: users };
  } catch (error) {
    return { success: false, error };
  }
};

// Get user statistics
export const getUserStatistics = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
    const testResultsSnapshot = await getDocs(collection(db, COLLECTIONS.TEST_RESULTS));
    
    const totalUsers = usersSnapshot.size;
    const activeUsers = usersSnapshot.docs.filter(doc => doc.data().isActive).length;
    const totalTests = testResultsSnapshot.size;
    
    let totalScore = 0;
    let completedTests = 0;
    
    testResultsSnapshot.forEach(doc => {
      const result = doc.data();
      if (result.score !== undefined) {
        totalScore += result.score;
        completedTests++;
      }
    });
    
    const averageScore = completedTests > 0 ? (totalScore / completedTests).toFixed(2) : 0;
    
    return {
      success: true,
      data: {
        totalUsers,
        activeUsers,
        inactiveUsers: totalUsers - activeUsers,
        totalTests,
        averageScore,
        completionRate: totalUsers > 0 ? ((completedTests / totalUsers) * 100).toFixed(2) : 0
      }
    };
  } catch (error) {
    return { success: false, error };
  }
};

// Search users
export const searchUsers = async (searchTerm, filters = {}) => {
  try {
    let q = query(collection(db, COLLECTIONS.USERS));
    
    // Apply filters
    if (filters.role) {
      q = query(q, where('role', '==', filters.role));
    }
    
    if (filters.isActive !== undefined) {
      q = query(q, where('isActive', '==', filters.isActive));
    }
    
    const querySnapshot = await getDocs(q);
    const users = [];
    
    querySnapshot.forEach((doc) => {
      const userData = { id: doc.id, ...doc.data() };
      
      // Apply search term filter
      if (!searchTerm || 
          userData.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          userData.email?.toLowerCase().includes(searchTerm.toLowerCase())) {
        users.push(userData);
      }
    });
    
    return { success: true, data: users };
  } catch (error) {
    return { success: false, error };
  }
};

// Update user permissions
export const updateUserPermissions = async (userId, permissions) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      permissions,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Get user activity log
export const getUserActivityLog = async (userId, limit = 50) => {
  try {
    const q = query(
      collection(db, 'userActivities'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    const activities = [];
    querySnapshot.forEach((doc) => {
      activities.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: activities };
  } catch (error) {
    return { success: false, error };
  }
};

// Log user activity
export const logUserActivity = async (userId, activity) => {
  try {
    await addDoc(collection(db, 'userActivities'), {
      userId,
      ...activity,
      timestamp: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Get user performance analytics
export const getUserPerformanceAnalytics = async (userId) => {
  try {
    const testResultsSnapshot = await getDocs(
      query(
        collection(db, COLLECTIONS.TEST_RESULTS),
        where('userId', '==', userId),
        orderBy('completedAt', 'desc')
      )
    );
    
    const results = [];
    testResultsSnapshot.forEach((doc) => {
      results.push({ id: doc.id, ...doc.data() });
    });
    
    const analytics = {
      totalTests: results.length,
      averageScore: results.length > 0 ? 
        (results.reduce((sum, r) => sum + (r.score || 0), 0) / results.length).toFixed(2) : 0,
      bestScore: results.length > 0 ? Math.max(...results.map(r => r.score || 0)) : 0,
      worstScore: results.length > 0 ? Math.min(...results.map(r => r.score || 0)) : 0,
      totalTime: results.reduce((sum, r) => sum + (r.duration || 0), 0),
      recentTrend: results.slice(0, 5).map(r => r.score || 0)
    };
    
    return { success: true, data: analytics };
  } catch (error) {
    return { success: false, error };
  }
};

// Expert Profile Management
export const saveExpertProfile = async (expertData) => {
  try {
    const docRef = await addDoc(collection(db, 'expertProfiles'), {
      ...expertData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: expertData.status || 'active',
      verified: expertData.verified || false
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateExpertProfile = async (expertId, updateData) => {
  try {
    await updateDoc(doc(db, 'expertProfiles', expertId), {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteExpertProfile = async (expertId) => {
  try {
    await deleteDoc(doc(db, 'expertProfiles', expertId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Live Session Management
export const saveLiveSession = async (sessionData) => {
  try {
    const docRef = await addDoc(collection(db, 'liveSessions'), {
      ...sessionData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: sessionData.status || 'scheduled',
      participants: sessionData.participants || []
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateLiveSession = async (sessionId, updateData) => {
  try {
    await updateDoc(doc(db, 'liveSessions', sessionId), {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteLiveSession = async (sessionId) => {
  try {
    await deleteDoc(doc(db, 'liveSessions', sessionId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Doubt Resolution Management
export const saveDoubt = async (doubtData) => {
  try {
    if (doubtData.id) {
      // Update existing doubt
      await updateDoc(doc(db, 'doubts', doubtData.id), {
        ...doubtData,
        updatedAt: new Date()
      });
      return { success: true, id: doubtData.id };
    } else {
      // Create new doubt
      const docRef = await addDoc(collection(db, 'doubts'), {
        ...doubtData,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: doubtData.status || 'pending',
        responses: doubtData.responses || []
      });
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteDoubt = async (doubtId) => {
  try {
    await deleteDoc(doc(db, 'doubts', doubtId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Doubt Response Management
export const saveDoubtResponse = async (doubtId, responseData) => {
  try {
    const docRef = await addDoc(collection(db, 'doubtResponses'), {
      doubtId,
      ...responseData,
      createdAt: new Date()
    });
    
    // Update the doubt with the response
    await updateDoc(doc(db, 'doubts', doubtId), {
      updatedAt: new Date(),
      status: 'answered'
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

// Session Registration Management
export const saveSessionRegistration = async (registrationData) => {
  try {
    const docRef = await addDoc(collection(db, 'sessionRegistrations'), {
      ...registrationData,
      createdAt: new Date(),
      status: registrationData.status || 'registered'
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

// Collaboration Analytics
export const getCollaborationAnalytics = async () => {
  try {
    const expertsSnapshot = await getDocs(collection(db, 'expertProfiles'));
    const sessionsSnapshot = await getDocs(collection(db, 'liveSessions'));
    const doubtsSnapshot = await getDocs(collection(db, 'doubts'));
    
    const totalExperts = expertsSnapshot.size;
    const activeExperts = expertsSnapshot.docs.filter(doc => doc.data().status === 'active').length;
    const totalSessions = sessionsSnapshot.size;
    const completedSessions = sessionsSnapshot.docs.filter(doc => doc.data().status === 'completed').length;
    const totalDoubts = doubtsSnapshot.size;
    const resolvedDoubts = doubtsSnapshot.docs.filter(doc => doc.data().status === 'resolved').length;
    
    return {
      success: true,
      data: {
        totalExperts,
        activeExperts,
        inactiveExperts: totalExperts - activeExperts,
        totalSessions,
        completedSessions,
        pendingSessions: totalSessions - completedSessions,
        totalDoubts,
        resolvedDoubts,
        pendingDoubts: totalDoubts - resolvedDoubts,
        resolutionRate: totalDoubts > 0 ? ((resolvedDoubts / totalDoubts) * 100).toFixed(2) : 0,
        completionRate: totalSessions > 0 ? ((completedSessions / totalSessions) * 100).toFixed(2) : 0
      }
    };
  } catch (error) {
    return { success: false, error };
  }
};

// Real-time listeners for collaboration
export const subscribeToExpertProfiles = (callback, filters = {}) => {
  try {
    let q = query(collection(db, 'expertProfiles'), orderBy('createdAt', 'desc'));
    
    if (filters.status && filters.status !== 'all') {
      q = query(q, where('status', '==', filters.status));
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const experts = [];
      snapshot.forEach((doc) => {
        experts.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: experts });
    }, (error) => {
      callback({ success: false, error });
    });
    
    return unsubscribe;
  } catch (error) {
    return () => {};
  }
};

export const subscribeToLiveSessions = (callback, filters = {}) => {
  try {
    let q = query(collection(db, 'liveSessions'), orderBy('scheduledAt', 'desc'));
    
    if (filters.status && filters.status !== 'all') {
      q = query(q, where('status', '==', filters.status));
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const sessions = [];
      snapshot.forEach((doc) => {
        sessions.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: sessions });
    }, (error) => {
      callback({ success: false, error });
    });
    
    return unsubscribe;
  } catch (error) {
    return () => {};
  }
};

export const subscribeToDoubts = (callback, filters = {}) => {
  try {
    let q = query(collection(db, 'doubts'), orderBy('createdAt', 'desc'));
    
    if (filters.status && filters.status !== 'all') {
      q = query(q, where('status', '==', filters.status));
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const doubts = [];
      snapshot.forEach((doc) => {
        doubts.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: doubts });
    }, (error) => {
      callback({ success: false, error });
    });
    
    return unsubscribe;
  } catch (error) {
    return () => {};
  }
};

// Real-time listeners for vacancy management
export const subscribeToVacancies = (callback, filters = {}) => {
  try {
    let q = query(collection(db, COLLECTIONS.VACANCIES), orderBy('createdAt', 'desc'));
    
    if (filters.status && filters.status !== 'all') {
      q = query(q, where('status', '==', filters.status));
    }
    
    if (filters.category && filters.category !== 'all') {
      q = query(q, where('category', '==', filters.category));
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const vacancies = [];
      snapshot.forEach((doc) => {
        vacancies.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: vacancies });
    }, (error) => {
      callback({ success: false, error });
    });
    
    return unsubscribe;
  } catch (error) {
    return () => {};
  }
};

export const subscribeToExamResults = (callback, filters = {}) => {
  try {
    let q = query(collection(db, COLLECTIONS.EXAM_RESULTS), orderBy('createdAt', 'desc'));
    
    if (filters.status && filters.status !== 'all') {
      q = query(q, where('status', '==', filters.status));
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = [];
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: results });
    }, (error) => {
      callback({ success: false, error });
    });
    
    return unsubscribe;
  } catch (error) {
    return () => {};
  }
};

export const subscribeToExamNotifications = (callback, filters = {}) => {
  try {
    let q = query(collection(db, COLLECTIONS.EXAM_NOTIFICATIONS), orderBy('createdAt', 'desc'));
    
    if (filters.type && filters.type !== 'all') {
      q = query(q, where('type', '==', filters.type));
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications = [];
      snapshot.forEach((doc) => {
        notifications.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: notifications });
    }, (error) => {
      callback({ success: false, error });
    });
    
    return unsubscribe;
  } catch (error) {
    return () => {};
  }
};

// Convert tests from tests collection to mockTests format
export const convertTestsToMockTests = async (examId) => {
  try {
    // Get tests from tests collection for the specific exam
    // Normalize exam identifiers to handle hyphens/underscores/case
    const normalize = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    const examIdNormalized = normalize(examId);
    
    // Get exam data to match by both ID and name
    let examData = null;
    let examNameNormalized = null;
    try {
      const examResult = await getAllExamData();
      if (examResult.success && examResult.data) {
        // Find the exam by ID
        const exam = examResult.data[examId];
        if (exam) {
          examData = exam;
          examNameNormalized = normalize(exam.name || examId);
        }
      }
        } catch (error) {
          // Could not fetch exam data for matching
        }
    
    // Build list of normalized values to match against
    const matchValues = [examIdNormalized];
    if (examNameNormalized && examNameNormalized !== examIdNormalized) {
      matchValues.push(examNameNormalized);
    }
    
    // Query for tests with the exam name (case-insensitive)
    // Include ALL published tests (both with and without coachingId) so users can see coaching tests
    let testsQuery = query(
      collection(db, COLLECTIONS.TESTS),
      where('status', '==', 'published')
    );
    
    let testsSnapshot;
    try {
      testsSnapshot = await getDocs(testsQuery);
    } catch (queryError) {
      // If query fails (likely index error), try without status filter
      if (queryError.code === 'failed-precondition' || queryError.message?.includes('index')) {
        testsQuery = collection(db, COLLECTIONS.TESTS);
        testsSnapshot = await getDocs(testsQuery);
      } else {
        throw queryError;
      }
    }
    
    const convertedTests = [];
    const now = new Date();
    
    for (const testDoc of testsSnapshot.docs) {
      const testData = testDoc.data();
      
      // Only include published tests (filter client-side if query didn't filter)
      if (testData.status !== 'published') {
        continue;
      }
      
      // Filter out coaching tests - they should only appear in Batch Schedule
      // Only show admin tests (coachingId is null) in Mock Tests page
      if (testData.coachingId) {
        continue; // Skip coaching tests in Mock Tests page
      }
      
      // Filter live tests based on timing
      if (testData.isLiveTest) {
        const startTime = testData.startTime ? new Date(testData.startTime) : null;
        const endTime = testData.endTime ? new Date(testData.endTime) : null;
        
        // If test has start/end time, check if current time is within range
        if (startTime && endTime) {
          if (now < startTime || now > endTime) {
            continue; // Skip tests that haven't started or have ended
          }
        } else if (endTime && now > endTime) {
          continue; // Skip tests that have ended (even if no start time)
        }
      }
      
      // Check if this test matches our exam (case-insensitive, ignoring separators)
      // Match by both exam ID and exam name
      const testExamNormalized = normalize(testData.exam);
      const matches = matchValues.some(matchValue => testExamNormalized === matchValue);
      
      if (testData.exam && matches) {
        // Convert test format to mock test format
        const mockTestData = {
          id: testDoc.id,
          name: testData.title,
          description: testData.description || `Mock test for ${testData.exam}`,
          duration: testData.duration * 60, // Convert minutes to seconds
          questions: testData.totalQuestions || (testData.questions ? testData.questions.length : 0),
          examId: examId.toLowerCase(),
          type: 'full', // Default type
          difficulty: testData.difficulty?.toLowerCase() || 'medium',
          subjects: [testData.subject || 'General'], // Use assigned subject or default to General
          attempts: 0,
          rating: 4.5,
          isCompleted: false,
          isNew: false,
          isLive: false,
          status: 'published',
          coachingId: testData.coachingId, // Preserve coachingId for reference (users can see coaching tests)
          createdAt: testData.createdAt || new Date(),
          updatedAt: testData.updatedAt || new Date()
        };
            
            convertedTests.push(mockTestData);
          }
        }
        
        return { success: true, data: convertedTests };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Enhanced getMockTests function that also includes converted tests
export const getMockTestsEnhanced = async (examId) => {
  try {
    let allTests = [];
    
    // First, get tests from mockTests collection
    const mockTestsResult = await getMockTests(examId);
    if (mockTestsResult.success) {
      allTests = [...allTests, ...mockTestsResult.data];
    }
    
    // Then, get and convert tests from tests collection
    const convertedTestsResult = await convertTestsToMockTests(examId);
    if (convertedTestsResult.success) {
      allTests = [...allTests, ...convertedTestsResult.data];
    }
    
    return { success: true, data: allTests };
  } catch (error) {
    return { success: false, error };
  }
};

export const getQuestionsFromTest = async (testId) => {
  try {
    // Decode testId to handle spaces and special characters in document names
    const decodedTestId = testId ? (() => {
      try {
        return decodeURIComponent(testId);
      } catch (e) {
        return testId; // Use as-is if already decoded
      }
    })() : testId;
    
    // Get the test document
    const testRef = doc(db, COLLECTIONS.TESTS, decodedTestId);
    const testDoc = await getDoc(testRef);
    
    if (!testDoc.exists()) {
      return { success: false, error: 'Test not found' };
    }
    
    const testData = testDoc.data();
    // Check if questions are embedded in the test document
    if (testData.questions && Array.isArray(testData.questions) && testData.questions.length > 0) {
      return { 
        success: true, 
        data: testData.questions.map((q, index) => ({
          id: q.id || `q-${index}`,
          ...q
        }))
      };
    }
    
    // Check if questions are in a subcollection
    try {
      const questionsRef = collection(db, COLLECTIONS.TESTS, decodedTestId, 'questions');
      const questionsSnapshot = await getDocs(questionsRef);
      
      if (!questionsSnapshot.empty) {
        const questions = questionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        return { success: true, data: questions };
      }
    } catch (subcollectionError) {
      }
    
    // If no questions found, return empty array
    return { success: true, data: [] };
    
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Note Voting Management
export const upvoteNote = async (noteId, userId) => {
  try {
    const noteRef = doc(db, COLLECTIONS.NOTES, noteId);
    const noteDoc = await getDoc(noteRef);
    
    if (!noteDoc.exists()) {
      return { success: false, error: 'Note not found' };
    }
    
    const noteData = noteDoc.data();
    const upvotes = noteData.upvotes || [];
    const downvotes = noteData.downvotes || [];
    
    // Check if user already upvoted
    if (upvotes.includes(userId)) {
      // Remove upvote
      const newUpvotes = upvotes.filter(id => id !== userId);
      await updateDoc(noteRef, {
        upvotes: newUpvotes,
        upvoteCount: newUpvotes.length,
        updatedAt: new Date().toISOString()
      });
      return { success: true, action: 'removed' };
    } else {
      // Add upvote and remove downvote if exists
      const newUpvotes = [...upvotes, userId];
      const newDownvotes = downvotes.filter(id => id !== userId);
      
      await updateDoc(noteRef, {
        upvotes: newUpvotes,
        downvotes: newDownvotes,
        upvoteCount: newUpvotes.length,
        downvoteCount: newDownvotes.length,
        updatedAt: new Date().toISOString()
      });
      return { success: true, action: 'added' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const downvoteNote = async (noteId, userId) => {
  try {
    const noteRef = doc(db, COLLECTIONS.NOTES, noteId);
    const noteDoc = await getDoc(noteRef);
    
    if (!noteDoc.exists()) {
      return { success: false, error: 'Note not found' };
    }
    
    const noteData = noteDoc.data();
    const upvotes = noteData.upvotes || [];
    const downvotes = noteData.downvotes || [];
    
    // Check if user already downvoted
    if (downvotes.includes(userId)) {
      // Remove downvote
      const newDownvotes = downvotes.filter(id => id !== userId);
      await updateDoc(noteRef, {
        downvotes: newDownvotes,
        downvoteCount: newDownvotes.length,
        updatedAt: new Date().toISOString()
      });
      return { success: true, action: 'removed' };
    } else {
      // Add downvote and remove upvote if exists
      const newDownvotes = [...downvotes, userId];
      const newUpvotes = upvotes.filter(id => id !== userId);
      
      await updateDoc(noteRef, {
        upvotes: newUpvotes,
        downvotes: newDownvotes,
        upvoteCount: newUpvotes.length,
        downvoteCount: newDownvotes.length,
        updatedAt: new Date().toISOString()
      });
      return { success: true, action: 'added' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserVoteStatus = async (noteId, userId) => {
  try {
    const noteRef = doc(db, COLLECTIONS.NOTES, noteId);
    const noteDoc = await getDoc(noteRef);
    
    if (!noteDoc.exists()) {
      return { success: false, error: 'Note not found' };
    }
    
    const noteData = noteDoc.data();
    const upvotes = noteData.upvotes || [];
    const downvotes = noteData.downvotes || [];
    
    if (upvotes.includes(userId)) {
      return { success: true, vote: 'upvote' };
    } else if (downvotes.includes(userId)) {
      return { success: true, vote: 'downvote' };
    } else {
      return { success: true, vote: 'none' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Bookmark Management
export const saveBookmark = async (userId, bookmarkData) => {
  try {
    const bookmark = {
      userId,
      type: bookmarkData.type, // 'resource' or 'note'
      itemId: bookmarkData.itemId,
      title: bookmarkData.title,
      description: bookmarkData.description,
      url: bookmarkData.url,
      thumbnail: bookmarkData.thumbnail || '',
      category: bookmarkData.category || 'General',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.BOOKMARKS), bookmark);
    
    return { success: true, data: { ...bookmark, id: docRef.id } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserBookmarks = async (userId, type = null) => {
  try {
    let q;
    if (type) {
      q = query(
        collection(db, COLLECTIONS.BOOKMARKS),
        where('userId', '==', userId),
        where('type', '==', type),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(
        collection(db, COLLECTIONS.BOOKMARKS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
    }
    
    const querySnapshot = await getDocs(q);
    const bookmarks = [];
    querySnapshot.forEach((doc) => {
      bookmarks.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: bookmarks };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const removeBookmark = async (bookmarkId) => {
  try {
    const bookmarkRef = doc(db, COLLECTIONS.BOOKMARKS, bookmarkId);
    await deleteDoc(bookmarkRef);
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const checkBookmarkStatus = async (userId, itemId, type) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.BOOKMARKS),
      where('userId', '==', userId),
      where('itemId', '==', itemId),
      where('type', '==', type)
    );
    
    const querySnapshot = await getDocs(q);
    const bookmark = querySnapshot.docs[0];
    
    const result = { 
      success: true, 
      isBookmarked: !querySnapshot.empty,
      bookmarkId: bookmark ? bookmark.id : null
    };
    
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Saved Questions Management
export const saveQuestionForUser = async (userId, questionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.REVISION_DATA), {
      userId,
      type: 'savedQuestion',
      ...questionData,
      createdAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserSavedQuestions = async (userId, limitCount = 200) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.REVISION_DATA),
      where('userId', '==', userId),
      where('type', '==', 'savedQuestion'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const savedQuestions = [];
    
    querySnapshot.forEach((doc) => {
      savedQuestions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, data: savedQuestions };
  } catch (error) {
    return { success: false, error };
  }
};

export const removeSavedQuestion = async (questionId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.REVISION_DATA, questionId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const checkIfQuestionSaved = async (userId, questionId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.REVISION_DATA),
      where('userId', '==', userId),
      where('type', '==', 'savedQuestion'),
      where('questionId', '==', questionId)
    );
    
    const querySnapshot = await getDocs(q);
    return { success: true, isSaved: !querySnapshot.empty, docId: querySnapshot.empty ? null : querySnapshot.docs[0].id };
  } catch (error) {
    return { success: false, error };
  }
};

// Listen to user saved questions in real-time
export const listenToUserSavedQuestions = (userId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.REVISION_DATA),
    where('userId', '==', userId),
    where('type', '==', 'savedQuestion'),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const savedQuestions = [];
    snapshot.forEach((doc) => {
      savedQuestions.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: savedQuestions });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-saved-questions-${userId}`, unsubscribe);
  return unsubscribe;
};

// Listen to user bookmarks in real-time
export const listenToUserBookmarks = (userId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.BOOKMARKS),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const bookmarks = [];
    snapshot.forEach((doc) => {
      const bookmarkData = { id: doc.id, ...doc.data() };
      bookmarks.push(bookmarkData);
    });
    callback({ success: true, data: bookmarks });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-bookmarks-${userId}`, unsubscribe);
  return unsubscribe;
};

// Clean up listeners
export const cleanupListeners = () => {
  listeners.forEach((unsubscribe) => {
    unsubscribe();
  });
  listeners.clear();
};

// Question Bookmark Management Functions
export const bookmarkQuestion = async (userId, questionData) => {
  try {
    const now = new Date();
    const bookmarkData = {
      userId,
      questionId: questionData.questionId,
      question: questionData.question,
      type: questionData.type || 'multiple-choice',
      options: questionData.options || [],
      correctAnswer: questionData.correctAnswer,
      explanation: questionData.explanation || '',
      topic: questionData.topic || questionData.subject || 'General',
      subject: questionData.subject || questionData.topic || 'General',
      difficulty: questionData.difficulty || 'medium',
      testId: questionData.testId || 'unknown',
      testName: questionData.testName || 'Unknown Test',
      markingScheme: questionData.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 },
      createdAt: now,
      updatedAt: now
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.QUESTION_BOOKMARKS), bookmarkData);
    
    return { success: true, data: { ...bookmarkData, id: docRef.id } };
  } catch (error) {
    // Error bookmarking question
    return { success: false, error: error.message };
  }
};

export const getUserQuestionBookmarks = async (userId, limitCount = 200) => {
  try {
    // First try with orderBy, if it fails, try without
    let q;
    try {
      q = query(
        collection(db, COLLECTIONS.QUESTION_BOOKMARKS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    } catch (indexError) {
      // Index not available, trying without orderBy
      q = query(
        collection(db, COLLECTIONS.QUESTION_BOOKMARKS),
        where('userId', '==', userId),
        limit(limitCount)
      );
    }
    
    const querySnapshot = await getDocs(q);
    const bookmarks = [];
    
    querySnapshot.forEach((doc) => {
      bookmarks.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort manually if we couldn't use orderBy
    if (!q._query.orderBy || q._query.orderBy.length === 0) {
      bookmarks.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
    }
    
    return { success: true, data: bookmarks };
  } catch (error) {
    // Error getting user question bookmarks
    return { success: false, error: error.message };
  }
};

export const removeQuestionBookmark = async (bookmarkId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.QUESTION_BOOKMARKS, bookmarkId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const checkQuestionBookmarkStatus = async (userId, questionId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.QUESTION_BOOKMARKS),
      where('userId', '==', userId),
      where('questionId', '==', questionId)
    );
    
    const querySnapshot = await getDocs(q);
    const bookmark = querySnapshot.docs[0];
    
    return { 
      success: true, 
      isBookmarked: !querySnapshot.empty,
      bookmarkId: bookmark ? bookmark.id : null 
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Listen to user question bookmarks in real-time
export const listenToUserQuestionBookmarks = (userId, callback) => {
  let q;
  
  try {
    q = query(
      collection(db, COLLECTIONS.QUESTION_BOOKMARKS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
  } catch (indexError) {
    // Index not available for real-time listener, using without orderBy
    q = query(
      collection(db, COLLECTIONS.QUESTION_BOOKMARKS),
      where('userId', '==', userId)
    );
  }

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const bookmarks = [];
    snapshot.forEach((doc) => {
      bookmarks.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort manually if we couldn't use orderBy
    if (!q._query.orderBy || q._query.orderBy.length === 0) {
      bookmarks.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
    }
    
    callback({ success: true, data: bookmarks });
  }, (error) => {
    // Error in question bookmarks listener
    
    // If it's an index error, try without orderBy
    if (error.code === 'failed-precondition' && error.message.includes('index')) {
      // Index error detected, retrying without orderBy
      
      const fallbackQuery = query(
        collection(db, COLLECTIONS.QUESTION_BOOKMARKS),
        where('userId', '==', userId)
      );
      
      const fallbackUnsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
        const bookmarks = [];
        snapshot.forEach((doc) => {
          bookmarks.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort manually
        bookmarks.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });
        
        callback({ success: true, data: bookmarks });
      }, (fallbackError) => {
        // Error in fallback question bookmarks listener
        callback({ success: false, error: fallbackError });
      });
      
      listeners.set(`user-question-bookmarks-${userId}`, fallbackUnsubscribe);
      return fallbackUnsubscribe;
    }
    
    callback({ success: false, error });
  });

  listeners.set(`user-question-bookmarks-${userId}`, unsubscribe);
  return unsubscribe;
};

// Test function for question bookmark functionality
export const testQuestionBookmarks = async (userId) => {
  try {
    const result = await getUserQuestionBookmarks(userId, 10);
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== BATCH MANAGEMENT FUNCTIONS =====

// Create a new batch
export const createBatch = async (batchData) => {
  try {
    // Batch limit validation for coaching institutes
    if (batchData.coachingId) {
      // Get coaching admin profile
      const coachingAdminQuery = query(
        collection(db, COLLECTIONS.USERS),
        where('coachingId', '==', batchData.coachingId),
        where('role', '==', 'coaching_admin'),
        limit(1)
      );
      const coachingAdminSnapshot = await getDocs(coachingAdminQuery);
      
      if (!coachingAdminSnapshot.empty) {
        const coachingProfile = coachingAdminSnapshot.docs[0].data();
        const category = coachingProfile.category || 'coaching';
        const studentRange = coachingProfile.studentRange || '0-500';
        const purchasedAddonPacks = coachingProfile.batchAddonPacks || 0;
        
        // Get current batch count
        const batchesQuery = query(
          collection(db, COLLECTIONS.BATCHES),
          where('coachingId', '==', batchData.coachingId)
        );
        const batchesSnapshot = await getDocs(batchesQuery);
        const currentBatchCount = batchesSnapshot.size;
        
        // Import batch limit functions
        const { checkBatchLimit } = await import('../utils/subscriptionPricing');
        const limitCheck = checkBatchLimit(currentBatchCount, category, studentRange, purchasedAddonPacks);
        
        if (!limitCheck.canCreate) {
          return {
            success: false,
            error: limitCheck.isAtMaximum
              ? `Maximum batch limit reached (${limitCheck.totalLimit} batches). Please delete some batches to create new ones.`
              : `Batch limit reached (${limitCheck.totalLimit} batches). Purchase addon packs to create more batches.`,
            limitReached: true,
            needsAddon: limitCheck.needsAddon,
            isAtMaximum: limitCheck.isAtMaximum,
            currentCount: currentBatchCount,
            totalLimit: limitCheck.totalLimit
          };
        }
      }
    }
    
    // Preserve coachingId if provided (for multi-tenancy)
    const batchRef = doc(collection(db, COLLECTIONS.BATCHES));
    const batchId = batchRef.id;
    
    const batchToSave = {
      ...batchData,
      id: batchId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(batchRef, batchToSave);
    
    return { success: true, batchId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get all batches
export const getBatches = async (coachingId = null) => {
  try {
    let q = collection(db, COLLECTIONS.BATCHES);
    
    // Multi-tenancy: Always filter by coachingId
    // If coachingId is provided, filter for that specific coaching
    // If coachingId is null, filter for platform-level batches only (coachingId == null)
    if (coachingId !== undefined) {
      q = query(q, where('coachingId', '==', coachingId));
    }
    
    // Try with orderBy first, fallback to without if index error
    try {
      q = query(q, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const batches = [];
      querySnapshot.forEach((doc) => {
        batches.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: batches };
    } catch (orderByError) {
      // If orderBy fails (likely missing index), try without it
      if (orderByError.code === 'failed-precondition' || orderByError.message?.includes('index')) {
        // Remove orderBy and try again
        let q2 = collection(db, COLLECTIONS.BATCHES);
        if (coachingId !== undefined) {
          q2 = query(q2, where('coachingId', '==', coachingId));
        }
        
        const querySnapshot = await getDocs(q2);
        const batches = [];
        querySnapshot.forEach((doc) => {
          batches.push({ id: doc.id, ...doc.data() });
        });
        // Sort client-side by createdAt
        batches.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
          const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
          return bTime - aTime; // Descending
        });
        return { success: true, data: batches };
      }
      throw orderByError; // Re-throw if it's not an index error
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get batch by ID
export const getBatchById = async (batchId) => {
  try {
    const docRef = doc(db, COLLECTIONS.BATCHES, batchId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    }
    return { success: false, error: 'Batch not found' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update batch
export const updateBatch = async (batchId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.BATCHES, batchId), {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete batch
export const deleteBatch = async (batchId, coachingId = null) => {
  try {
    console.log('🗑️ Attempting to delete batch:', { batchId, coachingId });
    // Validate batch deletion if coachingId is provided
    if (coachingId) {
      const { batchLimitService } = await import('../services/batchLimitService');
      const validationResult = await batchLimitService.validateBatchDeletion(batchId, coachingId);
      
      console.log('🛡️ Deletion validation result:', validationResult);

      if (!validationResult.canDelete) {
        return {
          success: false,
          error: validationResult.warning || validationResult.error,
          studentCount: validationResult.studentCount
        };
      }
    }

    // Get batch data before deletion for cleanup
    const batchDoc = await getDoc(doc(db, COLLECTIONS.BATCHES, batchId));
    const batchData = batchDoc.exists() ? batchDoc.data() : null;

    // Delete batch document
    await deleteDoc(doc(db, COLLECTIONS.BATCHES, batchId));

    // Clean up related data
    if (batchData) {
      // Delete batch schedules
      const schedulesQuery = query(
        collection(db, 'batchSchedules'),
        where('batchId', '==', batchId)
      );
      const schedulesSnapshot = await getDocs(schedulesQuery);
      const deleteSchedulePromises = schedulesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deleteSchedulePromises);

      // Delete batch classes
      const classesQuery = query(
        collection(db, COLLECTIONS.BATCH_CLASSES),
        where('batchId', '==', batchId)
      );
      const classesSnapshot = await getDocs(classesQuery);
      const deleteClassPromises = classesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deleteClassPromises);

      // Remove batch from teacher's profile
      if (batchData.teacherId) {
        const teacherRef = doc(db, COLLECTIONS.USERS, batchData.teacherId);
        const teacherDoc = await getDoc(teacherRef);
        
        if (teacherDoc.exists()) {
          const teacherData = teacherDoc.data();
          const currentBatches = teacherData.teacherProfile?.batches || [];
          const updatedBatches = currentBatches.filter(b => b.id !== batchId);
          
          await updateDoc(teacherRef, {
            'teacherProfile.batches': updatedBatches,
            updatedAt: serverTimestamp()
          });
        }
      }
    }

    return { 
      success: true,
      message: 'Batch deleted successfully. You can now create new batches.'
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete coaching account and associated data
export const deleteCoachingAccount = async (userId, coachingId) => {
  try {
    // Get user profile to find institutionId
    const userProfileResult = await getUserProfile(userId);
    const userProfile = userProfileResult.success ? userProfileResult.data : null;
    const institutionId = userProfile?.institutionId || userProfile?.coachingId || coachingId;

    // 1. Delete all exams with this coachingId
    const examsResult = await getAllExamData(coachingId);
    if (examsResult.success && examsResult.data) {
      const examIds = Object.keys(examsResult.data);
      for (const examId of examIds) {
        await deleteExamData(examId);
      }
    }

    // 2. Delete all batches with this coachingId
    const batchesResult = await getBatches(coachingId);
    if (batchesResult.success && batchesResult.data) {
      for (const batch of batchesResult.data) {
        if (batch.id) {
          await deleteBatch(batch.id);
        }
      }
    }

    // 3. Delete all resources with this coachingId
    const resourcesResult = await getResources({ coachingId });
    if (resourcesResult.success && resourcesResult.data) {
      for (const resource of resourcesResult.data) {
        if (resource.id) {
          await deleteResource(resource.id);
        }
      }
    }

    // 4. Remove coachingId from tests (keep them for admin)
    const testsResult = await getTests({ coachingId });
    if (testsResult.success && testsResult.data) {
      for (const test of testsResult.data) {
        if (test.id) {
          await updateDoc(doc(db, COLLECTIONS.TESTS, test.id), {
            coachingId: null,
            updatedAt: new Date()
          });
        }
      }
    }

    // 5. Delete all admission-related data if institutionId exists
    if (institutionId) {
      // 5a. Delete all admission applications for this institution
      try {
        const applicationsQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS),
          where('institutionId', '==', institutionId)
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        for (const appDoc of applicationsSnapshot.docs) {
          await deleteDoc(appDoc.ref);
        }
      } catch (error) {
        // Try fallback: get all and filter
        try {
          const allAppsSnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS));
          for (const appDoc of allAppsSnapshot.docs) {
            const appData = appDoc.data();
            if (appData.institutionId === institutionId) {
              await deleteDoc(appDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 5b. Delete all admission payments for this institution
      try {
        const paymentsQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_PAYMENTS),
          where('institutionId', '==', institutionId)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        for (const paymentDoc of paymentsSnapshot.docs) {
          await deleteDoc(paymentDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allPaymentsSnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_PAYMENTS));
          for (const paymentDoc of allPaymentsSnapshot.docs) {
            const paymentData = paymentDoc.data();
            if (paymentData.institutionId === institutionId) {
              await deleteDoc(paymentDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 5c. Delete institution customizations
      try {
        const customizationRef = doc(db, COLLECTIONS.ADMISSIONS_CUSTOMIZATIONS, institutionId);
        const customizationDoc = await getDoc(customizationRef);
        if (customizationDoc.exists()) {
          await deleteDoc(customizationRef);
        }
      } catch (error) {
      }

      // 5d. Delete all curriculum items for this institution
      try {
        const curriculumQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_CURRICULUM),
          where('institutionId', '==', institutionId)
        );
        const curriculumSnapshot = await getDocs(curriculumQuery);
        for (const curriculumDoc of curriculumSnapshot.docs) {
          await deleteDoc(curriculumDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allCurriculumSnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_CURRICULUM));
          for (const curriculumDoc of allCurriculumSnapshot.docs) {
            const curriculumData = curriculumDoc.data();
            if (curriculumData.institutionId === institutionId) {
              await deleteDoc(curriculumDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 5e. Delete all materials for this institution
      try {
        const materialsQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_MATERIALS),
          where('institutionId', '==', institutionId)
        );
        const materialsSnapshot = await getDocs(materialsQuery);
        for (const materialDoc of materialsSnapshot.docs) {
          await deleteDoc(materialDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allMaterialsSnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_MATERIALS));
          for (const materialDoc of allMaterialsSnapshot.docs) {
            const materialData = materialDoc.data();
            if (materialData.institutionId === institutionId) {
              await deleteDoc(materialDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 5f. Delete all faculty for this institution
      try {
        const facultyQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_FACULTY),
          where('institutionId', '==', institutionId)
        );
        const facultySnapshot = await getDocs(facultyQuery);
        for (const facultyDoc of facultySnapshot.docs) {
          await deleteDoc(facultyDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allFacultySnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_FACULTY));
          for (const facultyDoc of allFacultySnapshot.docs) {
            const facultyData = facultyDoc.data();
            if (facultyData.institutionId === institutionId) {
              await deleteDoc(facultyDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 5g. Delete institution profile from admissionsInstitutions
      try {
        const institutionRef = doc(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS, institutionId);
        const institutionDoc = await getDoc(institutionRef);
        if (institutionDoc.exists()) {
          await deleteDoc(institutionRef);
        }
      } catch (error) {
      }

      // 5h. Delete payment refunds related to this institution
      try {
        const refundsQuery = query(
          collection(db, COLLECTIONS.PAYMENT_REFUNDS),
          where('institutionId', '==', institutionId)
        );
        const refundsSnapshot = await getDocs(refundsQuery);
        for (const refundDoc of refundsSnapshot.docs) {
          await deleteDoc(refundDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allRefundsSnapshot = await getDocs(collection(db, COLLECTIONS.PAYMENT_REFUNDS));
          for (const refundDoc of allRefundsSnapshot.docs) {
            const refundData = refundDoc.data();
            if (refundData.institutionId === institutionId) {
              await deleteDoc(refundDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }
    }

    // 6. Delete user profile
    await deleteUserProfile(userId);

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Add class to batch
export const addBatchClass = async (classData) => {
  try {
    const classRef = doc(collection(db, COLLECTIONS.BATCH_CLASSES));
    const classId = classRef.id;
    
    await setDoc(classRef, {
      ...classData,
      id: classId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, classId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get classes for a batch
export const getBatchClasses = async (batchId) => {
  try {
    let q = query(
      collection(db, COLLECTIONS.BATCH_CLASSES),
      where('batchId', '==', batchId)
    );
    
    let querySnapshot;
    try {
      // Try with ordering (requires composite index)
      q = query(
        collection(db, COLLECTIONS.BATCH_CLASSES),
        where('batchId', '==', batchId),
        orderBy('date', 'asc'),
        orderBy('time', 'asc')
      );
      querySnapshot = await getDocs(q);
    } catch (indexError) {
      // Fallback without ordering if index doesn't exist
      q = query(
        collection(db, COLLECTIONS.BATCH_CLASSES),
        where('batchId', '==', batchId)
      );
      querySnapshot = await getDocs(q);
    }
    
    const classes = [];
    querySnapshot.forEach((doc) => {
      classes.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort manually if query didn't include ordering
    classes.sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      return (a.time || '').localeCompare(b.time || '');
    });
    
    // Convert fileKeys to URLs
    const classesWithUrls = await convertFileKeysToUrlsInArray(classes);
    
    return { success: true, data: classesWithUrls };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get class by ID
export const getBatchClassById = async (classId) => {
  try {
    const docRef = doc(db, COLLECTIONS.BATCH_CLASSES, classId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = { id: docSnap.id, ...docSnap.data() };
      
      // Convert fileKeys to URLs
      const dataWithUrls = await convertFileKeysToUrls(data);
      
      return { success: true, data: dataWithUrls };
    }
    return { success: false, error: 'Class not found' };
  } catch (error) {
    console.error('Error in getBatchClassById:', error);
    return { success: false, error: error.message };
  }
};

// Update batch class
export const updateBatchClass = async (classId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.BATCH_CLASSES, classId), {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete batch class
export const deleteBatchClass = async (classId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.BATCH_CLASSES, classId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Enroll student by email
export const enrollStudentInBatch = async (batchId, email) => {
  try {
    // Get batch data to retrieve coachingId
    const batchRef = doc(db, COLLECTIONS.BATCHES, batchId);
    const batchDoc = await getDoc(batchRef);
    
    if (!batchDoc.exists()) {
      return { success: false, error: 'Batch not found' };
    }
    
    const batchData = batchDoc.data();
    const coachingId = batchData.coachingId || null;
    
    // Check if student exists in the coaching's student management
    // First try with coachingId if available
    let studentQuery;
    if (coachingId) {
      studentQuery = query(
        collection(db, 'students'),
        where('email', '==', email.toLowerCase().trim()),
        where('coachingId', '==', coachingId)
      );
    } else {
      // If no coachingId, just search by email
      studentQuery = query(
        collection(db, 'students'),
        where('email', '==', email.toLowerCase().trim())
      );
    }
    
    const studentSnapshot = await getDocs(studentQuery);
    
    if (studentSnapshot.empty) {
      // Try to find student without coachingId filter to provide better error message
      const fallbackQuery = query(
        collection(db, 'students'),
        where('email', '==', email.toLowerCase().trim())
      );
      const fallbackSnapshot = await getDocs(fallbackQuery);
      
      if (fallbackSnapshot.empty) {
        return { success: false, error: 'Student not found in student management. Please add the student first.' };
      } else {
        const foundStudent = fallbackSnapshot.docs[0].data();
        return { success: false, error: `Student found but belongs to a different coaching (coachingId: ${foundStudent.coachingId}). Batch coachingId: ${coachingId}` };
      }
    }
    
    const studentDoc = studentSnapshot.docs[0];
    const studentId = studentDoc.id;
    const studentData = studentDoc.data();
    
    // Check if already enrolled in this specific batch
    const enrollmentQuery = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('batchId', '==', batchId),
      where('email', '==', email.toLowerCase().trim())
    );
    const enrollmentSnapshot = await getDocs(enrollmentQuery);
    
    if (!enrollmentSnapshot.empty) {
      return { success: false, error: 'Student already enrolled in this batch' };
    }
    
    // Create enrollment with coachingId and student data
    const enrollmentRef = doc(collection(db, COLLECTIONS.BATCH_ENROLLMENTS));
    await setDoc(enrollmentRef, {
      batchId,
      studentId,
      email: email.toLowerCase().trim(),
      name: studentData.name,
      phone: studentData.phone,
      coachingId,
      status: 'active',
      enrolledAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });
    
    return { success: true, enrollmentId: enrollmentRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get enrollments for a batch
export const getBatchEnrollments = async (batchId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('batchId', '==', batchId)
    );
    const querySnapshot = await getDocs(q);
    const enrollments = [];
    
    for (const docSnap of querySnapshot.docs) {
      const enrollmentData = { id: docSnap.id, ...docSnap.data() };
      // Get user details
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, enrollmentData.userId));
        if (userDoc.exists()) {
          enrollmentData.userData = userDoc.data();
        }
      } catch (err) {
        // User not found, skip
      }
      enrollments.push(enrollmentData);
    }
    
    return { success: true, data: enrollments };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get batches for a user (by userId)
// Uses multiple query strategies to handle different enrollment field variations
export const getUserBatches = async (userId) => {
  try {
    const allBatchIds = new Set();
    
    // Strategy 1: Query by userId field
    console.log('Fetching enrollments by userId...');
    let q = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('userId', '==', userId)
    );
    let querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      const batchId = doc.data().batchId;
      if (batchId) {
        allBatchIds.add(batchId);
      }
    });
    console.log(`Found ${querySnapshot.size} enrollments by userId`);
    
    // Strategy 2: Query by studentId field (some systems use this)
    console.log('Fetching enrollments by studentId...');
    q = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('studentId', '==', userId)
    );
    querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      const batchId = doc.data().batchId;
      if (batchId) {
        allBatchIds.add(batchId);
      }
    });
    console.log(`Found ${querySnapshot.size} enrollments by studentId`);
    
    // Strategy 3: Query by email (if user is authenticated)
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.email) {
      console.log('Fetching enrollments by email:', currentUser.email);
      q = query(
        collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
        where('email', '==', currentUser.email.toLowerCase().trim())
      );
      querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const batchId = doc.data().batchId;
        if (batchId) {
          allBatchIds.add(batchId);
        }
      });
      console.log(`Found ${querySnapshot.size} enrollments by email`);
    }
    
    console.log(`Total unique batch IDs found: ${allBatchIds.size}`);
    
    if (allBatchIds.size === 0) {
      return { success: true, data: [] };
    }
    
    // Get batch details for all found batch IDs
    const batches = [];
    for (const batchId of allBatchIds) {
      const batchResult = await getBatchById(batchId);
      if (batchResult.success && batchResult.data) {
        batches.push(batchResult.data);
      }
    }
    
    console.log(`Returning ${batches.length} batches`);
    return { success: true, data: batches };
  } catch (error) {
    console.error('Error in getUserBatches:', error);
    return { success: false, error: error.message };
  }
};

// Check if user is enrolled in batch
// Uses multiple query strategies to handle different enrollment field variations
export const checkBatchEnrollment = async (batchId, userId) => {
  try {
    // Strategy 1: Check by userId
    let q = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('batchId', '==', batchId),
      where('userId', '==', userId)
    );
    let querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return { success: true, enrolled: true };
    }
    
    // Strategy 2: Check by studentId
    q = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('batchId', '==', batchId),
      where('studentId', '==', userId)
    );
    querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return { success: true, enrolled: true };
    }
    
    // Strategy 3: Check by email
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.email) {
      q = query(
        collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
        where('batchId', '==', batchId),
        where('email', '==', currentUser.email.toLowerCase().trim())
      );
      querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        return { success: true, enrolled: true };
      }
    }
    
    return { success: true, enrolled: false };
  } catch (error) {
    console.error('Error in checkBatchEnrollment:', error);
    return { success: false, error: error.message };
  }
};

// Remove enrollment
// Uses multiple query strategies to find the enrollment to remove
export const removeBatchEnrollment = async (batchId, userId) => {
  try {
    let enrollmentDoc = null;
    
    // Strategy 1: Try by userId
    let q = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('batchId', '==', batchId),
      where('userId', '==', userId)
    );
    let querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      enrollmentDoc = querySnapshot.docs[0];
    } else {
      // Strategy 2: Try by studentId
      q = query(
        collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
        where('batchId', '==', batchId),
        where('studentId', '==', userId)
      );
      querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        enrollmentDoc = querySnapshot.docs[0];
      } else {
        // Strategy 3: Try by email
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.email) {
          q = query(
            collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
            where('batchId', '==', batchId),
            where('email', '==', currentUser.email.toLowerCase().trim())
          );
          querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            enrollmentDoc = querySnapshot.docs[0];
          }
        }
      }
    }
    
    if (!enrollmentDoc) {
      return { success: false, error: 'Enrollment not found' };
    }
    
    await deleteDoc(enrollmentDoc.ref);
    return { success: true };
  } catch (error) {
    console.error('Error in removeBatchEnrollment:', error);
    return { success: false, error: error.message };
  }
};

// Enroll student in batch with payment
export const enrollStudentInBatchWithPayment = async (batchId, email, paymentData) => {
  try {
    // Get batch data to retrieve coachingId
    const batchRef = doc(db, COLLECTIONS.BATCHES, batchId);
    const batchDoc = await getDoc(batchRef);
    
    if (!batchDoc.exists()) {
      return { success: false, error: 'Batch not found' };
    }
    
    const batchData = batchDoc.data();
    const coachingId = batchData.coachingId || null;
    
    // Check if student exists in the coaching's student management
    // First try with coachingId if available
    let studentQuery;
    if (coachingId) {
      studentQuery = query(
        collection(db, 'students'),
        where('email', '==', email.toLowerCase().trim()),
        where('coachingId', '==', coachingId)
      );
    } else {
      // If no coachingId, just search by email
      studentQuery = query(
        collection(db, 'students'),
        where('email', '==', email.toLowerCase().trim())
      );
    }
    
    const studentSnapshot = await getDocs(studentQuery);
    
    if (studentSnapshot.empty) {
      // Try to find student without coachingId filter to provide better error message
      const fallbackQuery = query(
        collection(db, 'students'),
        where('email', '==', email.toLowerCase().trim())
      );
      const fallbackSnapshot = await getDocs(fallbackQuery);
      
      if (fallbackSnapshot.empty) {
        return { success: false, error: 'Student not found in student management. Please add the student first.' };
      } else {
        const foundStudent = fallbackSnapshot.docs[0].data();
        return { success: false, error: `Student found but belongs to a different coaching (coachingId: ${foundStudent.coachingId}). Batch coachingId: ${coachingId}` };
      }
    }
    
    const studentDoc = studentSnapshot.docs[0];
    const studentId = studentDoc.id;
    const studentData = studentDoc.data();
    
    // Check if already enrolled in this specific batch
    const enrollmentQuery = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('batchId', '==', batchId),
      where('email', '==', email.toLowerCase().trim())
    );
    const enrollmentSnapshot = await getDocs(enrollmentQuery);
    
    if (!enrollmentSnapshot.empty) {
      return { success: false, error: 'Student already enrolled in this batch' };
    }
    
    // Create enrollment with payment info and coachingId
    const enrollmentRef = doc(collection(db, COLLECTIONS.BATCH_ENROLLMENTS));
    await setDoc(enrollmentRef, {
      batchId,
      studentId,
      email: email.toLowerCase().trim(),
      name: studentData.name,
      phone: studentData.phone,
      coachingId,
      status: 'active',
      paymentId: paymentData?.paymentId || null,
      orderId: paymentData?.orderId || null,
      paymentAmount: paymentData?.amount || 0,
      paymentCurrency: paymentData?.currency || 'INR',
      paymentStatus: paymentData?.paymentId ? 'completed' : 'pending',
      enrolledAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    // Save payment transaction if payment data exists
    if (paymentData?.paymentId && paymentData?.amount > 0) {
      const { paymentManagementService } = await import('./paymentManagement');
      await paymentManagementService.savePaymentTransaction({
        studentId,
        amount: paymentData.amount,
        currency: paymentData.currency || 'INR',
        paymentId: paymentData.paymentId,
        orderId: paymentData.orderId,
        status: 'completed',
        type: 'batch_enrollment',
        batchId: batchId,
        description: `Batch Enrollment Payment`,
        metadata: {
          batchId,
          email: email.toLowerCase().trim()
        }
      });
    }
    
    return { success: true, enrollmentId: enrollmentRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get batch assignments
export const getBatchAssignments = async (batchId) => {
  try {
    let q = query(
      collection(db, COLLECTIONS.BATCH_ASSIGNMENTS),
      where('batchId', '==', batchId)
    );
    
    let querySnapshot;
    try {
      // Try with ordering (requires composite index)
      q = query(
        collection(db, COLLECTIONS.BATCH_ASSIGNMENTS),
        where('batchId', '==', batchId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      querySnapshot = await getDocs(q);
    } catch (indexError) {
      // Fallback without ordering if index doesn't exist
      q = query(
        collection(db, COLLECTIONS.BATCH_ASSIGNMENTS),
        where('batchId', '==', batchId),
        limit(20)
      );
      querySnapshot = await getDocs(q);
    }
    
    const assignments = [];
    querySnapshot.forEach((doc) => {
      assignments.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort manually if query didn't include ordering
    assignments.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
      return dateB - dateA; // Descending order
    });
    
    // Convert fileKeys to URLs in assignment files
    const assignmentsWithUrls = await convertFileKeysToUrlsInArray(assignments);
    
    return { success: true, data: assignmentsWithUrls };
  } catch (error) {
    console.error('Error in getBatchAssignments:', error);
    return { success: false, error: error.message };
  }
};

// Get assignment submissions for a student
export const getStudentSubmissions = async (batchId, studentId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ASSIGNMENT_SUBMISSIONS),
      where('batchId', '==', batchId),
      where('studentId', '==', studentId),
      limit(50)
    );
    
    const querySnapshot = await getDocs(q);
    const submissions = {};
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Map by assignmentId for easy lookup
      submissions[data.assignmentId] = { id: doc.id, ...data };
    });
    
    // Convert fileKeys to URLs in submission files
    const submissionsArray = Object.values(submissions);
    const submissionsWithUrls = await Promise.all(
      submissionsArray.map(async (submission) => {
        if (submission.submissionFiles && Array.isArray(submission.submissionFiles)) {
          submission.submissionFiles = await Promise.all(
            submission.submissionFiles.map(async (file) => {
              if (file.url && typeof file.url === 'string') {
                if (!file.url.startsWith('http') || file.url.includes('X-Amz-Expires')) {
                  let fileKey = file.url;
                  if (file.url.startsWith('http')) {
                    try {
                      const urlObj = new URL(file.url);
                      fileKey = decodeURIComponent(urlObj.pathname.substring(1));
                    } catch (e) {
                      console.warn('Failed to parse URL:', file.url);
                    }
                  }
                  try {
                    const urlResult = await getFileDownloadURL(fileKey);
                    if (urlResult.success) {
                      file.url = urlResult.url;
                    }
                  } catch (error) {
                    console.warn('Failed to generate URL for file:', fileKey, error);
                  }
                }
              }
              return file;
            })
          );
        }
        return submission;
      })
    );
    
    // Convert back to map
    const submissionsMap = {};
    submissionsWithUrls.forEach(sub => {
      submissionsMap[sub.assignmentId] = sub;
    });
    
    return { success: true, data: submissionsMap };
  } catch (error) {
    console.error('Error in getStudentSubmissions:', error);
    return { success: false, error: error.message };
  }
};

// Get batch doubts with URL conversion
export const getBatchDoubts = async (batchId) => {
  try {
    const q = query(
      collection(db, 'batchDoubts'),
      where('batchId', '==', batchId)
    );
    
    const snapshot = await getDocs(q);
    const doubtsList = [];
    
    for (const docSnap of snapshot.docs) {
      const doubtData = docSnap.data();
      
      // Convert questionImages array
      const questionImages = doubtData.questionImages || [];
      const convertedQuestionImages = await Promise.all(
        questionImages.map(async (url) => {
          if (url && typeof url === 'string') {
            // Check if it's a fileKey or expired R2 URL
            if (!url.startsWith('http') || url.includes('X-Amz-Expires')) {
              let fileKey = url;
              
              // If it's an expired URL, extract the fileKey
              if (url.startsWith('http')) {
                try {
                  const urlObj = new URL(url);
                  fileKey = decodeURIComponent(urlObj.pathname.substring(1)); // Remove leading /
                } catch (e) {
                  console.warn('Failed to parse URL:', url);
                  return url;
                }
              }
              
              // Generate fresh URL
              try {
                const urlResult = await getFileDownloadURL(fileKey);
                if (urlResult.success) {
                  return urlResult.url;
                }
              } catch (error) {
                console.warn('Failed to generate URL for:', fileKey);
              }
            }
          }
          return url;
        })
      );
      
      // Convert answerImages array
      const answerImages = doubtData.answerImages || [];
      const convertedAnswerImages = await Promise.all(
        answerImages.map(async (url) => {
          if (url && typeof url === 'string') {
            // Check if it's a fileKey or expired R2 URL
            if (!url.startsWith('http') || url.includes('X-Amz-Expires')) {
              let fileKey = url;
              
              // If it's an expired URL, extract the fileKey
              if (url.startsWith('http')) {
                try {
                  const urlObj = new URL(url);
                  fileKey = decodeURIComponent(urlObj.pathname.substring(1)); // Remove leading /
                } catch (e) {
                  console.warn('Failed to parse URL:', url);
                  return url;
                }
              }
              
              // Generate fresh URL
              try {
                const urlResult = await getFileDownloadURL(fileKey);
                if (urlResult.success) {
                  return urlResult.url;
                }
              } catch (error) {
                console.warn('Failed to generate URL for:', fileKey);
              }
            }
          }
          return url;
        })
      );
      
      doubtsList.push({
        id: docSnap.id,
        ...doubtData,
        questionImages: convertedQuestionImages,
        answerImages: convertedAnswerImages
      });
    }
    
    // Sort by createdAt on client side (newest first)
    doubtsList.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return bTime - aTime;
    });
    
    return { success: true, data: doubtsList };
  } catch (error) {
    console.error('Error in getBatchDoubts:', error);
    return { success: false, error: error.message };
  }
};

// Add study material to class
export const addBatchStudyMaterial = async (classId, materialData) => {
  try {
    const materialRef = doc(collection(db, COLLECTIONS.BATCH_STUDY_MATERIALS));
    const materialId = materialRef.id;
    
    await setDoc(materialRef, {
      classId,
      ...materialData,
      id: materialId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, materialId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get study materials for a class
export const getBatchStudyMaterials = async (classId) => {
  try {
    let q = query(
      collection(db, COLLECTIONS.BATCH_STUDY_MATERIALS),
      where('classId', '==', classId)
    );
    
    let querySnapshot;
    try {
      // Try with ordering (requires composite index)
      q = query(
        collection(db, COLLECTIONS.BATCH_STUDY_MATERIALS),
        where('classId', '==', classId),
        orderBy('createdAt', 'desc')
      );
      querySnapshot = await getDocs(q);
    } catch (indexError) {
      // Fallback without ordering if index doesn't exist
      q = query(
        collection(db, COLLECTIONS.BATCH_STUDY_MATERIALS),
        where('classId', '==', classId)
      );
      querySnapshot = await getDocs(q);
    }
    
    const materials = [];
    querySnapshot.forEach((doc) => {
      materials.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort manually if query didn't include ordering
    materials.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
      return dateB - dateA; // Descending order
    });
    
    // Convert fileKeys to URLs
    const materialsWithUrls = await convertFileKeysToUrlsInArray(materials);
    
    return { success: true, data: materialsWithUrls };
  } catch (error) {
    console.error('Error in getBatchStudyMaterials:', error);
    return { success: false, error: error.message };
  }
};

// Delete study material
export const deleteBatchStudyMaterial = async (materialId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.BATCH_STUDY_MATERIALS, materialId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get batch announcements
export const getBatchAnnouncements = async (batchId) => {
  try {
    let q = query(
      collection(db, COLLECTIONS.BATCH_ANNOUNCEMENTS),
      where('batchId', '==', batchId)
    );
    
    let querySnapshot;
    try {
      // Try with ordering (requires composite index)
      q = query(
        collection(db, COLLECTIONS.BATCH_ANNOUNCEMENTS),
        where('batchId', '==', batchId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      querySnapshot = await getDocs(q);
    } catch (indexError) {
      // Fallback without ordering if index doesn't exist
      q = query(
        collection(db, COLLECTIONS.BATCH_ANNOUNCEMENTS),
        where('batchId', '==', batchId),
        limit(50)
      );
      querySnapshot = await getDocs(q);
    }
    
    const announcements = [];
    querySnapshot.forEach((doc) => {
      announcements.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort manually if query didn't include ordering
    announcements.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
      return dateB - dateA; // Descending order
    });
    
    // Convert fileKeys to URLs in announcement files array
    const announcementsWithUrls = await Promise.all(
      announcements.map(async (announcement) => {
        if (announcement.files && Array.isArray(announcement.files)) {
          // Convert URLs in each file object
          announcement.files = await Promise.all(
            announcement.files.map(async (file) => {
              if (file.url && typeof file.url === 'string') {
                // Check if it's a fileKey (doesn't start with http) or an expired URL
                if (!file.url.startsWith('http') || file.url.includes('X-Amz-Expires')) {
                  // Extract fileKey from URL or use as-is if it's already a fileKey
                  let fileKey = file.url;
                  if (file.url.startsWith('http')) {
                    // Extract fileKey from R2 URL
                    try {
                      const urlObj = new URL(file.url);
                      fileKey = decodeURIComponent(urlObj.pathname.substring(1)); // Remove leading /
                    } catch (e) {
                      console.warn('Failed to parse URL:', file.url);
                    }
                  }
                  
                  // Generate fresh URL
                  try {
                    const urlResult = await getFileDownloadURL(fileKey);
                    if (urlResult.success) {
                      file.url = urlResult.url;
                    }
                  } catch (error) {
                    console.warn('Failed to generate URL for file:', fileKey, error);
                  }
                }
              }
              return file;
            })
          );
        }
        return announcement;
      })
    );
    
    return { success: true, data: announcementsWithUrls };
  } catch (error) {
    console.error('Error in getBatchAnnouncements:', error);
    return { success: false, error: error.message };
  }
};

// Real-time listener for batch classes
export const subscribeToBatchClasses = (batchId, callback) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.BATCH_CLASSES),
      where('batchId', '==', batchId),
      orderBy('date', 'asc'),
      orderBy('time', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const classes = [];
      snapshot.forEach((doc) => {
        classes.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: classes });
    }, (error) => {
      callback({ success: false, error });
    });
  } catch (error) {
    return null;
  }
};

// ===== SUPPORT TICKETS MANAGEMENT =====

// Create a new support ticket
export const createSupportTicket = async (ticketData) => {
  try {
    const ticket = {
      ...ticketData,
      status: 'open',
      priority: ticketData.priority || 'medium',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.SUPPORT_TICKETS), ticket);
    return { success: true, ticketId: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get user's support tickets
export const getUserSupportTickets = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SUPPORT_TICKETS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const tickets = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      tickets.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      });
    });
    return { success: true, data: tickets };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get all support tickets (for admin)
export const getAllSupportTickets = async (limitCount = 100) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SUPPORT_TICKETS),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const tickets = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      tickets.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      });
    });
    return { success: true, data: tickets };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get a single support ticket
export const getSupportTicket = async (ticketId) => {
  try {
    const docRef = doc(db, COLLECTIONS.SUPPORT_TICKETS, ticketId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        data: {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
        }
      };
    } else {
      return { success: false, error: 'Ticket not found' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update support ticket
export const updateSupportTicket = async (ticketId, updateData) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.SUPPORT_TICKETS, ticketId), {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Add response to ticket
export const addTicketResponse = async (ticketId, responseData) => {
  try {
    if (!ticketId) {
      return { success: false, error: 'Ticket ID is required' };
    }

    const response = {
      ...responseData,
      createdAt: serverTimestamp()
    };
    
    // Access subcollection correctly
    const responsesRef = collection(db, COLLECTIONS.SUPPORT_TICKETS, ticketId, 'responses');
    const docRef = await addDoc(responsesRef, response);
    
    // Update ticket's updatedAt timestamp
    const ticketRef = doc(db, COLLECTIONS.SUPPORT_TICKETS, ticketId);
    await updateDoc(ticketRef, {
      updatedAt: serverTimestamp()
    });
    
    return { success: true, responseId: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get ticket responses
export const getTicketResponses = async (ticketId) => {
  try {
    // Try with orderBy first
    try {
      const q = query(
        collection(db, COLLECTIONS.SUPPORT_TICKETS, ticketId, 'responses'),
        orderBy('createdAt', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const responses = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        responses.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date())
        });
      });
      // Sort by createdAt if orderBy didn't work
      responses.sort((a, b) => {
        const aTime = a.createdAt?.getTime ? a.createdAt.getTime() : 0;
        const bTime = b.createdAt?.getTime ? b.createdAt.getTime() : 0;
        return aTime - bTime;
      });
      return { success: true, data: responses };
    } catch (orderByError) {
      // Fallback: get all responses without orderBy
      const responsesRef = collection(db, COLLECTIONS.SUPPORT_TICKETS, ticketId, 'responses');
      const querySnapshot = await getDocs(responsesRef);
      const responses = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        responses.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date())
        });
      });
      // Sort by createdAt manually
      responses.sort((a, b) => {
        const aTime = a.createdAt?.getTime ? a.createdAt.getTime() : 0;
        const bTime = b.createdAt?.getTime ? b.createdAt.getTime() : 0;
        return aTime - bTime;
      });
      return { success: true, data: responses };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Real-time listener for user tickets
export const subscribeToUserTickets = (userId, callback) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SUPPORT_TICKETS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const tickets = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        tickets.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
        });
      });
      callback({ success: true, data: tickets });
    }, (error) => {
      callback({ success: false, error: error.message });
    });
  } catch (error) {
    return null;
  }
};

// Real-time listener for all tickets (admin)
export const subscribeToAllTickets = (callback) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SUPPORT_TICKETS),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const tickets = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        tickets.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
        });
      });
      callback({ success: true, data: tickets });
    }, (error) => {
      callback({ success: false, error: error.message });
    });
  } catch (error) {
    return null;
  }
};

// ===== TEAM MEMBER MANAGEMENT FUNCTIONS =====

// Save or update team member
export const saveTeamMember = async (teamMemberData) => {
  try {
    const dataToSave = {
      name: teamMemberData.name,
      role: teamMemberData.role,
      position: teamMemberData.position,
      avatar: teamMemberData.avatar,
      color: teamMemberData.color,
      updatedAt: serverTimestamp()
    };

    if (teamMemberData.id) {
      // Update existing team member
      await updateDoc(doc(db, COLLECTIONS.TEAM_MEMBERS, teamMemberData.id), dataToSave);
      return { success: true, id: teamMemberData.id };
    } else {
      // Create new team member
      const teamMemberToSave = {
        ...dataToSave,
        createdAt: serverTimestamp(),
        order: teamMemberData.order || 0
      };
      const docRef = await addDoc(collection(db, COLLECTIONS.TEAM_MEMBERS), teamMemberToSave);
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get all team members
export const getTeamMembers = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.TEAM_MEMBERS),
      orderBy('order', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const teamMembers = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      teamMembers.push({
        id: doc.id,
        name: data.name,
        role: data.role,
        position: data.position,
        avatar: data.avatar,
        color: data.color,
        order: data.order || 0,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      });
    });
    return { success: true, data: teamMembers };
  } catch (error) {
    // If orderBy fails, try without it
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.TEAM_MEMBERS));
      const teamMembers = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        teamMembers.push({
          id: doc.id,
          name: data.name,
          role: data.role,
          position: data.position,
          avatar: data.avatar,
          color: data.color,
          order: data.order || 0,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
        });
      });
      // Sort by order client-side
      teamMembers.sort((a, b) => (a.order || 0) - (b.order || 0));
      return { success: true, data: teamMembers };
    } catch (fallbackError) {
      return { success: false, error: fallbackError.message || fallbackError };
    }
  }
};

// Delete team member
export const deleteTeamMember = async (teamMemberId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.TEAM_MEMBERS, teamMemberId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get team member by ID
export const getTeamMemberById = async (teamMemberId) => {
  try {
    const docRef = doc(db, COLLECTIONS.TEAM_MEMBERS, teamMemberId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        data: {
          id: docSnap.id,
          name: data.name,
          role: data.role,
          position: data.position,
          avatar: data.avatar,
          color: data.color,
          order: data.order || 0,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
        }
      };
    } else {
      return { success: false, error: 'Team member not found' };
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// ===== REVIEW MANAGEMENT FUNCTIONS =====

// Save review
export const saveReview = async (reviewData) => {
  try {
    const badWords = [
      'hate', 'stupid', 'idiot', 'badword1', 'badword2', 
      'worse', 'bad', 'waste', 'worst', 'garbage', 
      'scam', 'fraud', 'useless', 'horrible', 'terrible', 
      'pathetic', 'fake', 'nonsense', 'crap', 'shitty', 
      'annoying', 'dumb', 'loser', 'suck', 'sucks'
    ]; // Expanded list of negative and offensive words for moderation
    const reviewText = (reviewData.review || '').toLowerCase();
    const feedbackText = (reviewData.feedback || '').toLowerCase();
    
    const hasBadWords = badWords.some(word => 
      reviewText.includes(word) || feedbackText.includes(word)
    );

    const reviewToSave = {
      name: reviewData.name.trim(),
      exam: reviewData.exam.trim(),
      rating: reviewData.rating,
      review: reviewData.review.trim(),
      feedback: reviewData.feedback?.trim() || '',
      status: hasBadWords ? 'pending' : 'published',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.REVIEWS), reviewToSave);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get reviews with optional filters
export const getReviews = async (filters = {}) => {
  try {
    // Fetch all reviews first (this avoids Firestore index requirements)
    // Then filter and sort client-side for maximum compatibility
    const allSnapshot = await getDocs(collection(db, COLLECTIONS.REVIEWS));
    const allReviews = [];
    
    allSnapshot.forEach((doc) => {
      const data = doc.data();
      allReviews.push({
        id: doc.id,
        name: data.name,
        exam: data.exam,
        rating: data.rating,
        review: data.review,
        feedback: data.feedback || '',
        status: data.status || 'published', // Default to published if status is missing
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt ? new Date(data.updatedAt) : new Date())
      });
    });
    
    console.log('Total reviews fetched:', allReviews.length);
    
    // Filter by status client-side
    const statusFilter = filters.status || 'published';
    const filteredReviews = allReviews.filter(review => {
      const statusMatch = review.status === statusFilter;
      const examMatch = filters.exam ? review.exam === filters.exam : true;
      return statusMatch && examMatch;
    });
    
    console.log('Reviews after status filter:', filteredReviews.length);
    
    // Sort by createdAt client-side (newest first)
    filteredReviews.sort((a, b) => {
      const dateA = a.createdAt?.getTime ? a.createdAt.getTime() : 0;
      const dateB = b.createdAt?.getTime ? b.createdAt.getTime() : 0;
      return dateB - dateA;
    });
    
    // Apply limit client-side if needed
    const limitedReviews = filters.limit ? filteredReviews.slice(0, filters.limit) : filteredReviews;
    
    console.log('Final reviews count:', limitedReviews.length);
    
    return { success: true, data: limitedReviews };
  } catch (error) {
    console.error('Error in getReviews:', error);
    return { success: false, error: error.message || error, data: [] };
  }
};

// ===== TIMETABLE MANAGEMENT FUNCTIONS =====

// Save or update timetable
export const saveTimetable = async (timetableData) => {
  try {
    const dataToSave = {
      batchId: timetableData.batchId || null,
      coachingId: timetableData.coachingId || null,
      isGlobal: timetableData.isGlobal || false,
      updatedAt: serverTimestamp()
    };

    // Check if this is a PDF timetable or structured entry
    if (timetableData.pdfUrl) {
      // PDF timetable
      dataToSave.title = timetableData.title;
      dataToSave.description = timetableData.description;
      dataToSave.pdfUrl = timetableData.pdfUrl;
    } else {
      // Structured timetable entry
      dataToSave.day = timetableData.day;
      dataToSave.subject = timetableData.subject;
      dataToSave.startTime = timetableData.startTime;
      dataToSave.endTime = timetableData.endTime;
      dataToSave.teacherName = timetableData.teacherName || null;
    }

    if (timetableData.id) {
      await updateDoc(doc(db, COLLECTIONS.TIMETABLES, timetableData.id), dataToSave);
      return { success: true, id: timetableData.id };
    } else {
      const timetableToSave = {
        ...dataToSave,
        createdAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, COLLECTIONS.TIMETABLES), timetableToSave);
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get timetables
export const getTimetables = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.TIMETABLES);
    
    if (filters.batchId) {
      q = query(q, where('batchId', '==', filters.batchId));
    } else if (filters.coachingId) {
      q = query(q, where('coachingId', '==', filters.coachingId));
    } else if (filters.isGlobal) {
      q = query(q, where('isGlobal', '==', true));
    }

    q = query(q, orderBy('createdAt', 'desc'));
    
    const querySnapshot = await getDocs(q);
    const timetables = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      timetables.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      });
    });
    
    // Convert fileKeys to URLs
    const timetablesWithUrls = await convertFileKeysToUrlsInArray(timetables);
    
    return { success: true, data: timetablesWithUrls };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Delete timetable
export const deleteTimetable = async (timetableId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.TIMETABLES, timetableId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// ===== MASTER TIMETABLE MANAGEMENT FUNCTIONS =====

// Save master timetable (array of slots) with optional metadata
export const saveMasterTimetable = async (slots, coachingId = null, options = {}) => {
  try {
    const docId = coachingId ? `master_${coachingId}` : 'master_global';
    const payload = {
      type: 'master',
      slots: slots,
      coachingId: coachingId,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    };

    if (options.periods) {
      payload.periods = options.periods;
    }
    if (options.subjects) {
      payload.subjects = options.subjects;
    }

    await setDoc(doc(db, COLLECTIONS.TIMETABLES, docId), payload);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get master timetable
export const getMasterTimetable = async (coachingId = null) => {
  try {
    const docId = coachingId ? `master_${coachingId}` : 'master_global';
    const docSnap = await getDoc(doc(db, COLLECTIONS.TIMETABLES, docId));
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        data: {
          slots: data.slots || [],
          periods: data.periods || [],
          subjects: data.subjects || []
        }
      };
    } else {
      return {
        success: true,
        data: {
          slots: [],
          periods: [],
          subjects: []
        }
      };
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// ===== STUDENT RESULTS AND ADMIT CARDS FUNCTIONS =====

// Save student result or admit card
export const saveStudentDocument = async (documentData) => {
  try {
    // Normalize email (lowercase and trim)
    const normalizedEmail = documentData.studentEmail ? documentData.studentEmail.toLowerCase().trim() : '';
    
    const dataToSave = {
      studentEmail: normalizedEmail,
      studentId: documentData.studentId || null,
      studentName: documentData.studentName || '',
      documentType: documentData.documentType, // 'result' or 'admitCard'
      documentUrl: documentData.documentUrl || '',
      examName: documentData.examName || '',
      examDate: documentData.examDate || '',
      batchId: documentData.batchId || null,
      coachingId: documentData.coachingId || null,
      // Include all additional fields (for test results with scores, etc.)
      ...(documentData.testId && { testId: documentData.testId }),
      ...(documentData.testResultId && { testResultId: documentData.testResultId }),
      ...(documentData.score !== undefined && { score: documentData.score }),
      ...(documentData.totalMarks !== undefined && { totalMarks: documentData.totalMarks }),
      ...(documentData.percentageScore !== undefined && { percentageScore: documentData.percentageScore }),
      ...(documentData.questionScores && { questionScores: documentData.questionScores }),
      ...(documentData.questionsWithAnswers && { questionsWithAnswers: documentData.questionsWithAnswers }),
      ...(documentData.evaluatedAt && { evaluatedAt: documentData.evaluatedAt }),
      ...(documentData.completedAt && { completedAt: documentData.completedAt }),
      ...(documentData.sectionScores && documentData.sectionScores.length > 0 && { sectionScores: documentData.sectionScores }),
      // New result fields
      ...(documentData.testName && { testName: documentData.testName }),
      ...(documentData.exam && { exam: documentData.exam }),
      ...(documentData.marksObtained !== undefined && documentData.marksObtained !== null && !isNaN(documentData.marksObtained) && { marksObtained: documentData.marksObtained }),
      ...(documentData.percentage !== undefined && documentData.percentage !== null && !isNaN(documentData.percentage) && { percentage: documentData.percentage }),
      // Always include rank field (can be null or a number)
      rank: documentData.rank !== undefined && documentData.rank !== null && documentData.rank !== '' ? documentData.rank : null,
      ...(documentData.subjectWiseMarks && Object.keys(documentData.subjectWiseMarks).length > 0 && { subjectWiseMarks: documentData.subjectWiseMarks }),
      ...(documentData.uploadedBy && { uploadedBy: documentData.uploadedBy }),
      ...(documentData.uploadedByName && { uploadedByName: documentData.uploadedByName }),
      updatedAt: serverTimestamp()
    };

    if (documentData.id) {
      await updateDoc(doc(db, documentData.documentType === 'result' ? COLLECTIONS.STUDENT_RESULTS : COLLECTIONS.STUDENT_ADMIT_CARDS, documentData.id), dataToSave);
      return { success: true, id: documentData.id };
    } else {
      const documentToSave = {
        ...dataToSave,
        createdAt: serverTimestamp()
      };
      const collectionName = documentData.documentType === 'result' ? COLLECTIONS.STUDENT_RESULTS : COLLECTIONS.STUDENT_ADMIT_CARDS;
      const docRef = await addDoc(collection(db, collectionName), documentToSave);
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get student documents by email
export const getStudentDocuments = async (studentEmail, documentType = 'result') => {
  try {
    if (!studentEmail) {
      return { success: false, error: 'Student email is required' };
    }
    
    const collectionName = documentType === 'result' ? COLLECTIONS.STUDENT_RESULTS : COLLECTIONS.STUDENT_ADMIT_CARDS;
    const normalizedEmail = studentEmail.toLowerCase().trim();
    
    let querySnapshot;
    
    // Query by studentEmail first (for security rules to work properly)
    // Then filter client-side by batchId if needed
    try {
      const q = query(
        collection(db, collectionName),
        where('studentEmail', '==', normalizedEmail)
      );
      querySnapshot = await getDocs(q);
    } catch (queryError) {
      // If query fails, try with orderBy (might need index)
      try {
        const q = query(
          collection(db, collectionName),
          where('studentEmail', '==', normalizedEmail),
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(q);
      } catch (indexError) {
        // Both queries failed
        throw queryError;
      }
    }
    
    const documents = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Normalize email from document for comparison
      const docEmail = data.studentEmail ? data.studentEmail.toLowerCase().trim() : '';
      
      // Only include documents where email matches (case-insensitive)
      if (docEmail === normalizedEmail) {
        documents.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date()),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt ? new Date(data.updatedAt) : null)
        });
      }
    });
    
    // Sort by createdAt in memory (descending order - newest first)
    documents.sort((a, b) => {
      const dateA = a.createdAt || new Date(0);
      const dateB = b.createdAt || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
    
    // Convert fileKeys to URLs
    const documentsWithUrls = await convertFileKeysToUrlsInArray(documents);
    
    return { success: true, data: documentsWithUrls };
  } catch (error) {
    // Error fetching student documents
    return { success: false, error: error.message || error };
  }
};

// Get all student documents (admin)
export const getAllStudentDocuments = async (documentType = 'result', filters = {}) => {
  try {
    const collectionName = documentType === 'result' ? COLLECTIONS.STUDENT_RESULTS : COLLECTIONS.STUDENT_ADMIT_CARDS;
    let q = collection(db, collectionName);
    
    if (filters.batchId) {
      q = query(q, where('batchId', '==', filters.batchId));
    }
    if (filters.coachingId) {
      q = query(q, where('coachingId', '==', filters.coachingId));
    }
    
    q = query(q, orderBy('createdAt', 'desc'));
    
    const querySnapshot = await getDocs(q);
    const documents = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      documents.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
      });
    });
    
    // Convert fileKeys to URLs
    const documentsWithUrls = await convertFileKeysToUrlsInArray(documents);
    
    return { success: true, data: documentsWithUrls };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Delete student document
export const deleteStudentDocument = async (documentId, documentType = 'result') => {
  try {
    const collectionName = documentType === 'result' ? COLLECTIONS.STUDENT_RESULTS : COLLECTIONS.STUDENT_ADMIT_CARDS;
    await deleteDoc(doc(db, collectionName, documentId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// ===== BATCH CHAT FUNCTIONS =====

// Send chat message
export const sendBatchChatMessage = async (messageData) => {
  try {
    const messageToSave = {
      batchId: messageData.batchId,
      senderId: messageData.senderId,
      senderName: messageData.senderName,
      senderEmail: messageData.senderEmail,
      message: messageData.message,
      messageType: messageData.messageType || 'text', // 'text', 'image', 'file'
      fileUrl: messageData.fileUrl || null,
      fileName: messageData.fileName || null,
      replyTo: messageData.replyTo || null, // ID of the message being replied to
      replyToMessage: messageData.replyToMessage || null, // Preview of the original message
      replyToSender: messageData.replyToSender || null, // Name of the original sender
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, COLLECTIONS.BATCH_CHAT_MESSAGES), messageToSave);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get batch chat messages
export const getBatchChatMessages = async (batchId, limit = 50) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.BATCH_CHAT_MESSAGES),
      where('batchId', '==', batchId),
      orderBy('createdAt', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      messages.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
      });
    });
    // Reverse to show oldest first
    return { success: true, data: messages.reverse() };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Subscribe to batch chat messages (real-time)
export const subscribeToBatchChat = (batchId, callback) => {
  if (!batchId) {
    callback({ success: false, error: 'Batch ID is required' });
    return null;
  }

  // Helper function to process messages
  const processMessages = (snapshot) => {
    const messages = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      messages.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
      });
    });
    // Sort in memory by createdAt descending, then reverse to show oldest first
    messages.sort((a, b) => {
      const aTime = a.createdAt?.getTime ? a.createdAt.getTime() : (a.createdAt || 0);
      const bTime = b.createdAt?.getTime ? b.createdAt.getTime() : (b.createdAt || 0);
      return bTime - aTime;
    });
    callback({ success: true, data: messages.reverse() });
  };

  let unsubscribeMain = null;
  let unsubscribeFallback = null;

  // Try with orderBy first (requires index)
  const q = query(
    collection(db, COLLECTIONS.BATCH_CHAT_MESSAGES),
    where('batchId', '==', batchId),
    orderBy('createdAt', 'desc'),
    limit(100)
  );
  
  try {
    unsubscribeMain = onSnapshot(q, processMessages, (error) => {
      // If index error, try fallback query without orderBy
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        // Index not found, using fallback query
        // Unsubscribe from main query
        if (unsubscribeMain) {
          unsubscribeMain();
        }
        
        try {
          const fallbackQ = query(
            collection(db, COLLECTIONS.BATCH_CHAT_MESSAGES),
            where('batchId', '==', batchId),
            limit(100)
          );
          
          unsubscribeFallback = onSnapshot(fallbackQ, processMessages, (fallbackError) => {
            // Fallback query also failed
            callback({ success: false, error: fallbackError.message || 'Failed to load messages' });
          });
        } catch (fallbackError) {
          // Error setting up fallback query
          callback({ success: false, error: error.message || 'Failed to load messages. Please create a Firestore index for batchChatMessages with fields: batchId (Ascending), createdAt (Descending)' });
        }
      } else {
        // Query error
        callback({ success: false, error: error.message || 'Failed to load messages' });
      }
    });

    // Return unsubscribe function that cleans up both
    return () => {
      if (unsubscribeMain) unsubscribeMain();
      if (unsubscribeFallback) unsubscribeFallback();
    };
  } catch (error) {
    // Error setting up subscription
    callback({ success: false, error: error.message || 'Failed to subscribe to messages' });
    return null;
  }
};

// Update chat message
export const updateBatchChatMessage = async (messageId, updates) => {
  try {
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    await updateDoc(doc(db, COLLECTIONS.BATCH_CHAT_MESSAGES, messageId), updateData);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Delete chat message
export const deleteBatchChatMessage = async (messageId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.BATCH_CHAT_MESSAGES, messageId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// ===== DIRECT MESSAGES (ONE-ON-ONE) =====

// Send direct message
export const sendDirectMessage = async (messageData) => {
  try {
    console.log('sendDirectMessage called with:', messageData);
    
    const messageToSave = {
      batchId: messageData.batchId,
      senderId: messageData.senderId,
      senderName: messageData.senderName,
      senderEmail: messageData.senderEmail,
      receiverId: messageData.receiverId,
      receiverName: messageData.receiverName,
      receiverEmail: messageData.receiverEmail,
      message: messageData.message,
      messageType: messageData.messageType || 'text',
      fileUrl: messageData.fileUrl || null,
      fileName: messageData.fileName || null,
      replyTo: messageData.replyTo || null,
      replyToMessage: messageData.replyToMessage || null,
      replyToSender: messageData.replyToSender || null,
      read: false,
      createdAt: serverTimestamp()
    };
    
    console.log('Saving message to Firestore:', messageToSave);
    const docRef = await addDoc(collection(db, 'directMessages'), messageToSave);
    console.log('Message saved successfully with ID:', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error in sendDirectMessage:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    return { success: false, error: error.message || error };
  }
};

// Subscribe to direct messages (real-time)
export const subscribeToDirectMessages = (batchId, userId, callback) => {
  if (!batchId || !userId) {
    callback({ success: false, error: 'Batch ID and User ID are required' });
    return null;
  }

  console.log('=== subscribeToDirectMessages ===');
  console.log('batchId:', batchId);
  console.log('userId:', userId);

  // Simple query - just filter by batchId, sort in memory
  const q = query(
    collection(db, 'directMessages'),
    where('batchId', '==', batchId)
  );
  
  try {
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('=== Direct Messages Snapshot ===');
      console.log('Total docs:', snapshot.docs.length);
      
      // Filter messages where user is sender or receiver
      const filteredDocs = snapshot.docs.filter(doc => {
        const data = doc.data();
        return data.senderId === userId || data.receiverId === userId;
      });
      
      console.log('Filtered docs:', filteredDocs.length);
      
      const messages = filteredDocs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
        };
      });
      
      // Sort in memory by createdAt
      messages.sort((a, b) => {
        const aTime = a.createdAt?.getTime?.() || 0;
        const bTime = b.createdAt?.getTime?.() || 0;
        return aTime - bTime;
      });
      
      console.log('Processed and sorted messages:', messages.length);
      callback({ success: true, data: messages });
    }, (error) => {
      console.error('=== Direct Messages Error ===');
      console.error('Error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      callback({ success: false, error: error.message || 'Failed to load messages' });
    });

    return unsubscribe;
  } catch (error) {
    console.error('=== Direct Messages Subscribe Error ===');
    console.error('Error:', error);
    callback({ success: false, error: error.message || 'Failed to subscribe to messages' });
    return null;
  }
};

// Update direct message
export const updateDirectMessage = async (messageId, updates) => {
  try {
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    await updateDoc(doc(db, 'directMessages', messageId), updateData);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Delete direct message
export const deleteDirectMessage = async (messageId) => {
  try {
    await deleteDoc(doc(db, 'directMessages', messageId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Mark message as read
export const markMessageAsRead = async (messageId) => {
  try {
    await updateDoc(doc(db, 'directMessages', messageId), {
      read: true,
      readAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get batch teachers
export const getBatchTeachers = async (batchId) => {
  try {
    // Get batch to find teachers
    const batchDoc = await getDoc(doc(db, COLLECTIONS.BATCHES, batchId));
    if (!batchDoc.exists()) {
      return { success: false, error: 'Batch not found' };
    }
    
    const batchData = batchDoc.data();
    const teachers = [];
    
    // Add main teacher
    if (batchData.teacherId) {
      const teacherDoc = await getDoc(doc(db, COLLECTIONS.USERS, batchData.teacherId));
      if (teacherDoc.exists()) {
        teachers.push({ id: teacherDoc.id, uid: teacherDoc.id, ...teacherDoc.data() });
      }
    }
    
    // Add additional teachers if any
    if (batchData.additionalTeachers && Array.isArray(batchData.additionalTeachers)) {
      for (const teacherId of batchData.additionalTeachers) {
        const teacherDoc = await getDoc(doc(db, COLLECTIONS.USERS, teacherId));
        if (teacherDoc.exists()) {
          teachers.push({ id: teacherDoc.id, uid: teacherDoc.id, ...teacherDoc.data() });
        }
      }
    }
    
    return { success: true, data: teachers };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get batch students
export const getBatchStudents = async (batchId) => {
  try {
    const enrollmentsQuery = query(
      collection(db, COLLECTIONS.BATCH_ENROLLMENTS),
      where('batchId', '==', batchId)
    );
    
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    const students = [];
    
    for (const enrollmentDoc of enrollmentsSnapshot.docs) {
      const enrollmentData = enrollmentDoc.data();
      if (enrollmentData.userId) {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, enrollmentData.userId));
        if (userDoc.exists()) {
          students.push({ id: userDoc.id, uid: userDoc.id, ...userDoc.data() });
        }
      }
    }
    
    return { success: true, data: students };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get all teachers from the institute (coaching)
export const getInstituteTeachers = async (batchId) => {
  try {
    // First get the batch to find the coachingId
    const batchDoc = await getDoc(doc(db, COLLECTIONS.BATCHES, batchId));
    if (!batchDoc.exists()) {
      return { success: false, error: 'Batch not found' };
    }
    
    const batchData = batchDoc.data();
    const coachingId = batchData.coachingId;
    
    if (!coachingId) {
      return { success: false, error: 'Coaching ID not found' };
    }
    
    // Get all users with role 'teacher' or 'admin' from this coaching
    const teachersQuery = query(
      collection(db, COLLECTIONS.USERS),
      where('coachingId', '==', coachingId)
    );
    
    const teachersSnapshot = await getDocs(teachersQuery);
    const teachers = [];
    
    teachersSnapshot.forEach((doc) => {
      const userData = doc.data();
      // Only include teachers and admins
      if (userData.role === 'teacher' || userData.role === 'admin') {
        teachers.push({ 
          id: doc.id, 
          uid: doc.id, 
          ...userData 
        });
      }
    });
    
    // Sort by name
    teachers.sort((a, b) => {
      const nameA = (a.name || a.displayName || '').toLowerCase();
      const nameB = (b.name || b.displayName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return { success: true, data: teachers };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// ===== ADMIN PERMISSIONS MANAGEMENT =====

// Get admin permissions by email
export const getAdminPermissions = async (adminEmail) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ADMIN_PERMISSIONS),
      where('email', '==', adminEmail)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: true, data: null };
    }
    
    const docSnap = querySnapshot.docs[0];
    return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get coaching admin permissions by email (for coaching admin panel)
export const getCoachingAdminPermissions = async (coachingAdminEmail) => {
  try {
    // Normalize email to lowercase for consistent querying
    const normalizedEmail = coachingAdminEmail?.toLowerCase();
    
    const q = query(
      collection(db, COLLECTIONS.COACHING_ADMIN_PERMISSIONS),
      where('email', '==', normalizedEmail)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: true, data: null };
    }
    
    const docSnap = querySnapshot.docs[0];
    return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get all admin permissions
export const getAllAdminPermissions = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ADMIN_PERMISSIONS),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const permissions = [];
    
    querySnapshot.forEach((doc) => {
      permissions.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: permissions };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get all coaching admin permissions
export const getAllCoachingAdminPermissions = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COACHING_ADMIN_PERMISSIONS),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const permissions = [];
    
    querySnapshot.forEach((doc) => {
      permissions.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: permissions };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Create or update admin permissions
export const setAdminPermissions = async (adminEmail, permissions) => {
  try {
    // Check if permissions already exist
    const existing = await getAdminPermissions(adminEmail);
    
    const permissionData = {
      email: adminEmail,
      permissions: permissions.allowedSections || [],
      isActive: permissions.isActive !== undefined ? permissions.isActive : true,
      createdAt: existing.data?.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: permissions.createdBy || 'founder',
      notes: permissions.notes || ''
    };
    
    if (existing.data) {
      // Update existing
      await updateDoc(doc(db, COLLECTIONS.ADMIN_PERMISSIONS, existing.data.id), permissionData);
      return { success: true, data: { id: existing.data.id, ...permissionData } };
    } else {
      // Create new
      const docRef = await addDoc(collection(db, COLLECTIONS.ADMIN_PERMISSIONS), permissionData);
      return { success: true, data: { id: docRef.id, ...permissionData } };
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Create or update coaching admin permissions
export const setCoachingAdminPermissions = async (coachingAdminEmail, permissions) => {
  try {
    // Normalize email to lowercase for consistent storage
    const normalizedEmail = coachingAdminEmail?.toLowerCase();
    
    // Check if permissions already exist
    const existing = await getCoachingAdminPermissions(normalizedEmail);
    
    const permissionData = {
      email: normalizedEmail,
      permissions: permissions.allowedSections || [],
      isActive: permissions.isActive !== undefined ? permissions.isActive : true,
      createdAt: existing.data?.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: permissions.createdBy || 'admin',
      notes: permissions.notes || ''
    };
    
    if (existing.data) {
      // Update existing
      await updateDoc(doc(db, COLLECTIONS.COACHING_ADMIN_PERMISSIONS, existing.data.id), permissionData);
      return { success: true, data: { id: existing.data.id, ...permissionData } };
    } else {
      // Create new
      const docRef = await addDoc(collection(db, COLLECTIONS.COACHING_ADMIN_PERMISSIONS), permissionData);
      return { success: true, data: { id: docRef.id, ...permissionData } };
    }
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Delete admin permissions
export const deleteAdminPermissions = async (adminEmail) => {
  try {
    const existing = await getAdminPermissions(adminEmail);
    if (!existing.data) {
      return { success: false, error: 'Permissions not found' };
    }
    
    await deleteDoc(doc(db, COLLECTIONS.ADMIN_PERMISSIONS, existing.data.id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Delete coaching admin permissions
export const deleteCoachingAdminPermissions = async (coachingAdminEmail) => {
  try {
    const existing = await getCoachingAdminPermissions(coachingAdminEmail);
    if (!existing.data) {
      return { success: false, error: 'Permissions not found' };
    }
    
    await deleteDoc(doc(db, COLLECTIONS.COACHING_ADMIN_PERMISSIONS, existing.data.id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get admin logs by email and date
export const getAdminLogs = async (adminEmail = null, date = null) => {
  try {
    let q;
    
    if (adminEmail) {
      // Get admin user ID from email
      const usersQuery = query(
        collection(db, COLLECTIONS.USERS),
        where('email', '==', adminEmail)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      if (!usersSnapshot.empty) {
        const userId = usersSnapshot.docs[0].id;
        try {
          q = query(
            collection(db, COLLECTIONS.ADMIN_LOGS),
            where('adminId', '==', userId),
            orderBy('timestamp', 'desc'),
            limit(1000)
          );
        } catch (error) {
          // If index doesn't exist, try without orderBy
          q = query(
            collection(db, COLLECTIONS.ADMIN_LOGS),
            where('adminId', '==', userId),
            limit(1000)
          );
        }
      } else {
        return { success: true, data: [] };
      }
    } else {
      try {
        q = query(
          collection(db, COLLECTIONS.ADMIN_LOGS),
          orderBy('timestamp', 'desc'),
          limit(1000)
        );
      } catch (error) {
        // If index doesn't exist, try without orderBy
        q = query(
          collection(db, COLLECTIONS.ADMIN_LOGS),
          limit(1000)
        );
      }
    }
    
    const querySnapshot = await getDocs(q);
    const logs = [];
    
    querySnapshot.forEach((doc) => {
      const logData = doc.data();
      const logDate = logData.timestamp?.toDate ? logData.timestamp.toDate() : new Date(logData.timestamp || Date.now());
      
      // Filter by date if provided
      if (date) {
        const targetDate = new Date(date);
        const isSameDay = logDate.toDateString() === targetDate.toDateString();
        if (!isSameDay) return;
      }
      
      logs.push({
        id: doc.id,
        ...logData,
        timestamp: logDate
      });
    });
    
    // Sort by timestamp if not already sorted
    logs.sort((a, b) => {
      const aTime = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
      const bTime = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
      return bTime - aTime; // Descending order
    });
    
    return { success: true, data: logs };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get all admins (users with admin role)
export const getAllAdmins = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      where('role', 'in', ['admin', 'superadmin', 'founder'])
    );
    const querySnapshot = await getDocs(q);
    const admins = [];
    
    querySnapshot.forEach((doc) => {
      admins.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: admins };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// ==================== FEE MANAGEMENT FUNCTIONS ====================

// Create or update student fee structure
export const saveStudentFee = async (feeData) => {
  try {
    const { 
      studentId, coachingId, batchId, feeType, amount, dueDate, academicYear, term, description,
      lateFeeEnabled, lateFeeType, lateFeeAmount, lateFeePercentage, lateFeeSlabs
    } = feeData;
    
    if (!studentId || !coachingId) {
      return { success: false, error: 'Student ID and Coaching ID are required' };
    }

    const feeRef = doc(collection(db, COLLECTIONS.STUDENT_FEES));
    const feeId = feeRef.id;

    await setDoc(feeRef, {
      id: feeId,
      studentId,
      coachingId,
      batchId: batchId || null,
      feeType: feeType || 'tuition', // tuition, exam, library, transport, etc.
      amount: parseFloat(amount) || 0,
      dueDate: dueDate ? (dueDate.toDate ? dueDate : new Date(dueDate)) : null,
      academicYear: academicYear || new Date().getFullYear(),
      term: term || null, // term1, term2, annual, etc.
      description: description || '',
      status: 'pending', // pending, paid, overdue, waived
      paidAmount: 0,
      outstandingAmount: parseFloat(amount) || 0,
      // Late fee configuration
      lateFeeEnabled: lateFeeEnabled || false,
      lateFeeType: lateFeeEnabled ? (lateFeeType || 'fixed') : null, // fixed, perDay, percentage, slab
      lateFeeAmount: lateFeeEnabled && (lateFeeType === 'fixed' || lateFeeType === 'perDay') 
        ? (parseFloat(lateFeeAmount) || 0) : null,
      lateFeePercentage: lateFeeEnabled && lateFeeType === 'percentage' 
        ? (parseFloat(lateFeePercentage) || 0) : null,
      lateFeeSlabs: lateFeeEnabled && lateFeeType === 'slab' && lateFeeSlabs 
        ? lateFeeSlabs : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true, feeId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get student fees
export const getStudentFees = async (coachingId, filters = {}) => {
  try {
    let q = query(collection(db, COLLECTIONS.STUDENT_FEES));

    // Filter by studentId first (for security rules to work properly)
    if (filters.studentId) {
      q = query(q, where('studentId', '==', filters.studentId));
    }
    
    // Then filter by coachingId (batchId filtering done client-side to avoid index issues)
    if (coachingId) {
      q = query(q, where('coachingId', '==', coachingId));
    }
    
    // Note: batchId filtering is done client-side to avoid composite index requirements
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.academicYear) {
      q = query(q, where('academicYear', '==', filters.academicYear));
    }

    // Try with orderBy first, fallback if index doesn't exist
    let querySnapshot;
    try {
      const qWithOrder = query(q, orderBy('createdAt', 'desc'));
      querySnapshot = await getDocs(qWithOrder);
    } catch (orderError) {
      // Index not available, try without orderBy
      console.log('OrderBy index not available for student fees, using fallback');
      querySnapshot = await getDocs(q);
    }
    const fees = [];
    
    for (const docSnap of querySnapshot.docs) {
      const feeData = { id: docSnap.id, ...docSnap.data() };
      
      // Filter by batchId client-side if provided
      if (filters.batchId && feeData.batchId !== filters.batchId && feeData.batchId !== null) {
        continue; // Skip documents that don't match batchId
      }
      
      // Get student details
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, feeData.studentId));
        if (userDoc.exists()) {
          feeData.studentData = userDoc.data();
        }
      } catch (err) {
        // Student not found
      }
      fees.push(feeData);
    }

    // Sort client-side if orderBy wasn't used
    if (fees.length > 0) {
      fees.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
    }

    return { success: true, data: fees };
  } catch (error) {
    console.error('Error getting student fees:', error);
    return { success: false, error: error.message || error.code || 'Failed to load fees' };
  }
};

// Record fee payment
export const recordFeePayment = async (paymentData) => {
  try {
    const { 
      feeId, studentId, coachingId, batchId, amount, paymentMethod, 
      paymentId, transactionId, notes,
      isRoutePayment, platformFee, schoolAmount,
      lateFeeApplied, lateFeeIncluded
    } = paymentData;
    
    console.log('Recording fee payment:', { feeId, studentId, coachingId, amount });
    
    if (!feeId || !studentId || !coachingId || !amount) {
      console.error('Missing required payment data:', { feeId, studentId, coachingId, amount });
      return { success: false, error: 'Missing required payment data' };
    }

    // Get the fee document
    const feeDoc = await getDoc(doc(db, COLLECTIONS.STUDENT_FEES, feeId));
    if (!feeDoc.exists()) {
      console.error('Fee record not found:', feeId);
      return { success: false, error: 'Fee record not found' };
    }

    const feeData = feeDoc.data();
    console.log('Current fee data:', { 
      amount: feeData.amount, 
      paidAmount: feeData.paidAmount, 
      outstandingAmount: feeData.outstandingAmount,
      status: feeData.status 
    });
    
    // Calculate amounts - if late fee is included, separate it from the base payment
    const totalPayment = parseFloat(amount);
    const lateFeeAmount = lateFeeIncluded ? (lateFeeApplied || 0) : 0;
    const baseFeePayment = totalPayment - lateFeeAmount;
    
    const newPaidAmount = (feeData.paidAmount || 0) + baseFeePayment;
    const newOutstandingAmount = Math.max(0, (feeData.amount || 0) - newPaidAmount);
    const newStatus = newOutstandingAmount === 0 ? 'paid' : (newPaidAmount > 0 ? 'partial' : feeData.status);

    console.log('Updating fee:', { newPaidAmount, newOutstandingAmount, newStatus, lateFeeAmount });

    // Update fee record
    const feeUpdateData = {
      paidAmount: newPaidAmount,
      outstandingAmount: newOutstandingAmount,
      status: newStatus,
      updatedAt: serverTimestamp()
    };
    
    // Track late fee paid if applicable
    if (lateFeeAmount > 0) {
      feeUpdateData.lateFeePaid = (feeData.lateFeePaid || 0) + lateFeeAmount;
    }
    
    await updateDoc(doc(db, COLLECTIONS.STUDENT_FEES, feeId), feeUpdateData);
    
    console.log('Fee record updated successfully');

    // Create payment record
    const paymentRef = doc(collection(db, COLLECTIONS.FEE_PAYMENTS));
    await setDoc(paymentRef, {
      id: paymentRef.id,
      feeId,
      studentId,
      coachingId,
      batchId: batchId || feeData.batchId || null,
      amount: totalPayment,
      baseFeeAmount: baseFeePayment,
      lateFeeAmount: lateFeeAmount,
      paymentMethod: paymentMethod || 'cash', // cash, online, cheque, bank_transfer, razorpay
      paymentId: paymentId || null,
      transactionId: transactionId || null,
      notes: notes || '',
      status: 'completed',
      // Route payment details
      isRoutePayment: isRoutePayment || false,
      platformFee: platformFee || 0,
      schoolAmount: schoolAmount || totalPayment,
      // Late fee tracking
      lateFeeApplied: lateFeeApplied || 0,
      lateFeeIncluded: lateFeeIncluded || false,
      paidAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    // Generate receipt
    const receiptId = await generateFeeReceipt(paymentRef.id, feeId, studentId, coachingId);

    return { success: true, paymentId: paymentRef.id, receiptId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Generate fee receipt
const generateFeeReceipt = async (paymentId, feeId, studentId, coachingId) => {
  try {
    const receiptRef = doc(collection(db, COLLECTIONS.FEE_RECEIPTS));
    const receiptId = receiptRef.id;

    // Get payment and fee data
    const paymentDoc = await getDoc(doc(db, COLLECTIONS.FEE_PAYMENTS, paymentId));
    const feeDoc = await getDoc(doc(db, COLLECTIONS.STUDENT_FEES, feeId));
    const studentDoc = await getDoc(doc(db, COLLECTIONS.USERS, studentId));
    
    // Get coaching data - query by coachingId field, not document ID
    let coachingData = null;
    try {
      const coachingQuery = query(
        collection(db, COLLECTIONS.USERS),
        where('coachingId', '==', coachingId),
        where('role', '==', 'coaching_admin'),
        limit(1)
      );
      const coachingSnapshot = await getDocs(coachingQuery);
      if (!coachingSnapshot.empty) {
        coachingData = coachingSnapshot.docs[0].data();
      }
    } catch (err) {
      console.log('Could not fetch coaching data for receipt:', err);
    }

    const paymentData = paymentDoc.data();
    const feeData = feeDoc.data();
    const studentData = studentDoc.data();

    await setDoc(receiptRef, {
      id: receiptId,
      receiptNumber: `REC-${Date.now()}`,
      paymentId,
      feeId,
      studentId,
      coachingId,
      studentName: studentData?.name || studentData?.displayName || 'Student',
      coachingName: coachingData?.coachingName || coachingData?.name || 'School',
      amount: paymentData.amount,
      paymentMethod: paymentData.paymentMethod,
      transactionId: paymentData.transactionId,
      feeType: feeData.feeType,
      academicYear: feeData.academicYear,
      term: feeData.term,
      issuedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    return receiptId;
  } catch (error) {
    console.error('Error generating receipt:', error);
    return null;
  }
};

// Get fee payments
export const getFeePayments = async (coachingId, filters = {}) => {
  try {
    let q = query(
      collection(db, COLLECTIONS.FEE_PAYMENTS),
      where('coachingId', '==', coachingId),
      orderBy('paidAt', 'desc')
    );

    if (filters.studentId) {
      q = query(q, where('studentId', '==', filters.studentId));
    }
    if (filters.feeId) {
      q = query(q, where('feeId', '==', filters.feeId));
    }

    const querySnapshot = await getDocs(q);
    const payments = [];
    
    for (const docSnap of querySnapshot.docs) {
      const paymentData = { id: docSnap.id, ...docSnap.data() };
      // Get student details
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, paymentData.studentId));
        if (userDoc.exists()) {
          paymentData.studentData = userDoc.data();
        }
      } catch (err) {
        // Student not found
      }
      payments.push(paymentData);
    }

    return { success: true, data: payments };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get outstanding fees dashboard
export const getOutstandingFees = async (coachingId, filters = {}) => {
  try {
    const feesResult = await getStudentFees(coachingId, { ...filters, status: 'pending' });
    if (!feesResult.success) {
      return feesResult;
    }

    const fees = feesResult.data || [];
    const now = new Date();
    
    const outstanding = fees
      .filter(fee => {
        const dueDate = fee.dueDate?.toDate ? fee.dueDate.toDate() : new Date(fee.dueDate);
        return fee.outstandingAmount > 0 && dueDate < now;
      })
      .map(fee => ({
        ...fee,
        daysOverdue: Math.floor((now - (fee.dueDate?.toDate ? fee.dueDate.toDate() : new Date(fee.dueDate))) / (1000 * 60 * 60 * 24))
      }));

    const totalOutstanding = outstanding.reduce((sum, fee) => sum + (fee.outstandingAmount || 0), 0);

    return {
      success: true,
      data: outstanding,
      summary: {
        totalOutstanding,
        count: outstanding.length
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get fee receipt by receipt ID
export const getFeeReceipt = async (receiptId) => {
  try {
    const receiptDoc = await getDoc(doc(db, COLLECTIONS.FEE_RECEIPTS, receiptId));
    if (!receiptDoc.exists()) {
      return { success: false, error: 'Receipt not found' };
    }

    return { success: true, data: { id: receiptDoc.id, ...receiptDoc.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get fee receipt by payment ID
export const getFeeReceiptByPaymentId = async (paymentId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.FEE_RECEIPTS),
      where('paymentId', '==', paymentId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, error: 'Receipt not found' };
    }

    const receiptData = { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
    return { success: true, data: receiptData };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get fee receipts for a student
export const getFeeReceipts = async (coachingId, filters = {}) => {
  try {
    let q = query(
      collection(db, COLLECTIONS.FEE_RECEIPTS)
    );

    // Filter by studentId first (for security rules to work properly)
    if (filters.studentId) {
      q = query(q, where('studentId', '==', filters.studentId));
    }
    
    // Then filter by coachingId
    if (coachingId) {
      q = query(q, where('coachingId', '==', coachingId));
    }
    
    // Then filter by batchId if provided
    if (filters.batchId) {
      q = query(q, where('batchId', '==', filters.batchId));
    }

    // Try with orderBy, fallback if index doesn't exist
    try {
      q = query(q, orderBy('issuedAt', 'desc'));
    } catch (orderError) {
      // Index not available, continue without orderBy
      console.log('OrderBy index not available for fee receipts');
    }

    const querySnapshot = await getDocs(q);
    const receipts = [];
    
    for (const docSnap of querySnapshot.docs) {
      const receiptData = { id: docSnap.id, ...docSnap.data() };
      // Get student details
      try {
        if (receiptData.studentId) {
          const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, receiptData.studentId));
          if (userDoc.exists()) {
            receiptData.studentData = userDoc.data();
          }
        }
      } catch (err) {
        // Student not found
      }
      receipts.push(receiptData);
    }

    // Sort client-side if orderBy wasn't used
    if (receipts.length > 0 && !receipts[0].issuedAt) {
      receipts.sort((a, b) => {
        const dateA = a.issuedAt?.toDate ? a.issuedAt.toDate() : new Date(a.issuedAt || 0);
        const dateB = b.issuedAt?.toDate ? b.issuedAt.toDate() : new Date(b.issuedAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
    }

    return { success: true, data: receipts };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Create fee reminder
export const createFeeReminder = async (reminderData) => {
  try {
    const { feeId, studentId, coachingId, reminderType, message } = reminderData;
    
    const reminderRef = doc(collection(db, COLLECTIONS.FEE_REMINDERS));
    await setDoc(reminderRef, {
      id: reminderRef.id,
      feeId,
      studentId,
      coachingId,
      reminderType: reminderType || 'email', // email, sms, push
      message: message || '',
      status: 'pending', // pending, sent, failed
      createdAt: serverTimestamp(),
      sentAt: null
    });

    return { success: true, reminderId: reminderRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== REPORT CARDS & CERTIFICATES ====================

// Generate report card
export const generateReportCard = async (reportCardData) => {
  try {
    const { 
      studentId, 
      coachingId, 
      batchId, 
      academicYear, 
      term, 
      subjects, 
      subjectDetails, 
      grades, 
      remarks,
      templateId,
      teacherName,
      principalName,
      rollNo,
      attendance
    } = reportCardData;
    
    if (!studentId || !coachingId) {
      return { success: false, error: 'Student ID and Coaching ID are required' };
    }

    const reportCardRef = doc(collection(db, COLLECTIONS.REPORT_CARDS));
    const reportCardId = reportCardRef.id;

    await setDoc(reportCardRef, {
      id: reportCardId,
      studentId,
      coachingId,
      batchId: batchId || null,
      academicYear: academicYear || new Date().getFullYear(),
      term: term || null,
      subjects: subjects || [],
      subjectDetails: subjectDetails || [], // Store detailed subject information with marks
      grades: grades || {},
      totalMarks: reportCardData.totalMarks || 0,
      obtainedMarks: reportCardData.obtainedMarks || 0,
      percentage: reportCardData.percentage || 0,
      grade: reportCardData.grade || '',
      rank: reportCardData.rank || null,
      remarks: remarks || '',
      templateId: templateId || 'modern-clean',
      teacherName: teacherName || '',
      principalName: principalName || '',
      rollNo: rollNo || '',
      attendance: attendance || '',
      reportCardUrl: reportCardData.reportCardUrl || null,
      generatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    return { success: true, reportCardId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update report card
export const updateReportCard = async (reportCardId, reportCardData) => {
  try {
    const reportCardRef = doc(db, COLLECTIONS.REPORT_CARDS, reportCardId);
    await updateDoc(reportCardRef, {
      ...reportCardData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating report card:', error);
    return { success: false, error: error.message || 'Failed to update report card' };
  }
};

// Delete report card
export const deleteReportCard = async (reportCardId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.REPORT_CARDS, reportCardId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting report card:', error);
    return { success: false, error: error.message || 'Failed to delete report card' };
  }
};

// Get report cards
export const getReportCards = async (coachingId, filters = {}) => {
  try {
    let q = query(collection(db, COLLECTIONS.REPORT_CARDS));

    // Filter by studentId first (for security rules to work properly)
    if (filters.studentId) {
      q = query(q, where('studentId', '==', filters.studentId));
    }
    
    // Then filter by coachingId (batchId filtering done client-side to avoid index issues)
    if (coachingId) {
      q = query(q, where('coachingId', '==', coachingId));
    }
    
    // Note: batchId filtering is done client-side to avoid composite index requirements
    
    if (filters.academicYear) {
      q = query(q, where('academicYear', '==', filters.academicYear));
    }

    // Try with orderBy first, fallback if index doesn't exist
    let querySnapshot;
    try {
      const qWithOrder = query(q, orderBy('generatedAt', 'desc'));
      querySnapshot = await getDocs(qWithOrder);
    } catch (orderError) {
      // Index not available, try without orderBy
      console.log('OrderBy index not available for report cards, using fallback');
      querySnapshot = await getDocs(q);
    }
    const reportCards = [];
    
    for (const docSnap of querySnapshot.docs) {
      const cardData = { id: docSnap.id, ...docSnap.data() };
      
      // Filter by batchId client-side if provided
      if (filters.batchId && cardData.batchId !== filters.batchId && cardData.batchId !== null) {
        continue; // Skip documents that don't match batchId
      }
      
      // Get student details
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, cardData.studentId));
        if (userDoc.exists()) {
          cardData.studentData = userDoc.data();
        }
      } catch (err) {
        // Student not found
      }
      reportCards.push(cardData);
    }

    // Sort client-side if orderBy wasn't used
    if (reportCards.length > 0) {
      reportCards.sort((a, b) => {
        const dateA = a.generatedAt?.toDate ? a.generatedAt.toDate() : new Date(a.generatedAt || 0);
        const dateB = b.generatedAt?.toDate ? b.generatedAt.toDate() : new Date(b.generatedAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
    }

    return { success: true, data: reportCards };
  } catch (error) {
    console.error('Error getting report cards:', error);
    return { success: false, error: error.message || error.code || 'Failed to load report cards' };
  }
};

// Generate certificate
export const generateCertificate = async (certificateData) => {
  try {
    const { 
      studentId, 
      coachingId, 
      batchId, 
      certificateType, 
      title, 
      description, 
      achievementDate,
      templateId,
      issuerName,
      issuerTitle,
      venue
    } = certificateData;
    
    if (!studentId || !coachingId || !certificateType) {
      return { success: false, error: 'Missing required certificate data' };
    }

    const certificateRef = doc(collection(db, COLLECTIONS.CERTIFICATES));
    const certificateId = certificateRef.id;

    await setDoc(certificateRef, {
      id: certificateId,
      studentId,
      coachingId,
      batchId: batchId || null,
      certificateType: certificateType, // achievement, completion, excellence, participation
      title: title || 'Certificate of Achievement',
      description: description || '',
      achievementDate: achievementDate ? (achievementDate.toDate ? achievementDate : new Date(achievementDate)) : serverTimestamp(),
      templateId: templateId || 'classic-gold',
      issuerName: issuerName || '',
      issuerTitle: issuerTitle || '',
      venue: venue || '',
      certificateUrl: certificateData.certificateUrl || null,
      generatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    return { success: true, certificateId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update certificate
export const updateCertificate = async (certificateId, certificateData) => {
  try {
    const certificateRef = doc(db, COLLECTIONS.CERTIFICATES, certificateId);
    await updateDoc(certificateRef, {
      ...certificateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating certificate:', error);
    return { success: false, error: error.message || 'Failed to update certificate' };
  }
};

// Delete certificate
export const deleteCertificate = async (certificateId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.CERTIFICATES, certificateId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting certificate:', error);
    return { success: false, error: error.message || 'Failed to delete certificate' };
  }
};

// Get certificates
export const getCertificates = async (coachingId, filters = {}) => {
  try {
    let q = query(collection(db, COLLECTIONS.CERTIFICATES));

    // Filter by studentId first (for security rules to work properly)
    if (filters.studentId) {
      q = query(q, where('studentId', '==', filters.studentId));
    }
    
    // Then filter by coachingId (batchId filtering done client-side to avoid index issues)
    if (coachingId) {
      q = query(q, where('coachingId', '==', coachingId));
    }
    
    // Note: batchId filtering is done client-side to avoid composite index requirements
    
    if (filters.certificateType) {
      q = query(q, where('certificateType', '==', filters.certificateType));
    }

    // Try with orderBy first, fallback if index doesn't exist
    let querySnapshot;
    try {
      const qWithOrder = query(q, orderBy('generatedAt', 'desc'));
      querySnapshot = await getDocs(qWithOrder);
    } catch (orderError) {
      // Index not available, try without orderBy
      console.log('OrderBy index not available for certificates, using fallback');
      querySnapshot = await getDocs(q);
    }
    const certificates = [];
    
    for (const docSnap of querySnapshot.docs) {
      const certData = { id: docSnap.id, ...docSnap.data() };
      
      // Filter by batchId client-side if provided
      if (filters.batchId && certData.batchId !== filters.batchId && certData.batchId !== null) {
        continue; // Skip documents that don't match batchId
      }
      
      // Get student details
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, certData.studentId));
        if (userDoc.exists()) {
          certData.studentData = userDoc.data();
        }
      } catch (err) {
        // Student not found
      }
      certificates.push(certData);
    }

    // Sort client-side if orderBy wasn't used
    if (certificates.length > 0) {
      certificates.sort((a, b) => {
        const dateA = a.generatedAt?.toDate ? a.generatedAt.toDate() : new Date(a.generatedAt || 0);
        const dateB = b.generatedAt?.toDate ? b.generatedAt.toDate() : new Date(b.generatedAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
    }

    return { success: true, data: certificates };
  } catch (error) {
    console.error('Error getting certificates:', error);
    return { success: false, error: error.message || error.code || 'Failed to load certificates' };
  }
};

// Blog Management Functions
export const createBlog = async (blogData) => {
  try {
    const blogRef = doc(collection(db, COLLECTIONS.BLOGS));
    const blogId = blogRef.id;
    
    const blog = {
      ...blogData,
      id: blogId,
      slug: blogData.slug || generateSlug(blogData.title),
      status: blogData.status || 'draft',
      views: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(blogRef, blog);
    return { success: true, data: { id: blogId, ...blog } };
  } catch (error) {
    console.error('Error creating blog:', error);
    return { success: false, error: error.message || 'Failed to create blog' };
  }
};

export const updateBlog = async (blogId, blogData) => {
  try {
    const blogRef = doc(db, COLLECTIONS.BLOGS, blogId);
    const updateData = {
      ...blogData,
      updatedAt: serverTimestamp()
    };
    
    // Generate slug if title changed
    if (blogData.title) {
      updateData.slug = blogData.slug || generateSlug(blogData.title);
    }
    
    await updateDoc(blogRef, updateData);
    return { success: true };
  } catch (error) {
    console.error('Error updating blog:', error);
    return { success: false, error: error.message || 'Failed to update blog' };
  }
};

export const deleteBlog = async (blogId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.BLOGS, blogId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting blog:', error);
    return { success: false, error: error.message || 'Failed to delete blog' };
  }
};

export const getBlog = async (blogId) => {
  try {
    const blogDoc = await getDoc(doc(db, COLLECTIONS.BLOGS, blogId));
    if (blogDoc.exists()) {
      return { success: true, data: { id: blogDoc.id, ...blogDoc.data() } };
    }
    return { success: false, error: 'Blog not found' };
  } catch (error) {
    console.error('Error getting blog:', error);
    return { success: false, error: error.message || 'Failed to get blog' };
  }
};

export const getBlogBySlug = async (slug) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.BLOGS),
      where('slug', '==', slug),
      where('status', '==', 'published'),
      limit(1)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { success: true, data: { id: doc.id, ...doc.data() } };
    }
    return { success: false, error: 'Blog not found' };
  } catch (error) {
    console.error('Error getting blog by slug:', error);
    return { success: false, error: error.message || 'Failed to get blog' };
  }
};

export const getBlogs = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.BLOGS);
    const constraints = [];
    
    // Apply filters
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters.authorId) {
      constraints.push(where('authorId', '==', filters.authorId));
    }
    
    // Order by
    if (filters.orderBy) {
      constraints.push(orderBy(filters.orderBy, filters.orderDirection || 'desc'));
    } else {
      constraints.push(orderBy('createdAt', 'desc'));
    }
    
    // Limit
    if (filters.limit) {
      constraints.push(limit(filters.limit));
    }
    
    q = query(q, ...constraints);
    const querySnapshot = await getDocs(q);
    
    const blogs = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, data: blogs };
  } catch (error) {
    console.error('Error getting blogs:', error);
    return { success: false, error: error.message || 'Failed to get blogs' };
  }
};

export const subscribeToBlogs = (callback, filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.BLOGS);
    const constraints = [];
    
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    
    if (filters.orderBy) {
      constraints.push(orderBy(filters.orderBy, filters.orderDirection || 'desc'));
    } else {
      constraints.push(orderBy('createdAt', 'desc'));
    }
    
    if (filters.limit) {
      constraints.push(limit(filters.limit));
    }
    
    q = query(q, ...constraints);
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const blogs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback({ success: true, data: blogs });
    }, (error) => {
      console.error('Error subscribing to blogs:', error);
      callback({ success: false, error: error.message });
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up blog subscription:', error);
    callback({ success: false, error: error.message });
    return () => {};
  }
};

export const incrementBlogViews = async (blogId) => {
  try {
    const blogRef = doc(db, COLLECTIONS.BLOGS, blogId);
    await updateDoc(blogRef, {
      views: increment(1)
    });
    return { success: true };
  } catch (error) {
    console.error('Error incrementing blog views:', error);
    return { success: false, error: error.message };
  }
};

// Helper function to generate URL-friendly slug
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};





// ===== ID CARD MANAGEMENT FUNCTIONS =====

/**
 * Get ID cards for a coaching institute
 */
export const getIDCards = async (coachingId, filters = {}) => {
  try {
    // Start with basic query - avoid orderBy to prevent composite index requirement
    let q = query(
      collection(db, COLLECTIONS.ID_CARDS),
      where('coachingId', '==', coachingId)
    );

    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }

    if (filters.batchId) {
      q = query(q, where('batchId', '==', filters.batchId));
    }

    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    const snapshot = await getDocs(q);
    const cards = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort in memory by createdAt (newest first)
    cards.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
      const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
      return bTime - aTime;
    });

    return { success: true, data: cards };
  } catch (error) {
    console.error('Error getting ID cards:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single ID card by ID
 */
export const getIDCardById = async (cardId) => {
  try {
    const docRef = doc(db, COLLECTIONS.ID_CARDS, cardId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { 
        success: true, 
        data: { id: docSnap.id, ...docSnap.data() } 
      };
    } else {
      return { success: false, error: 'ID card not found' };
    }
  } catch (error) {
    console.error('Error getting ID card:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get ID card by card number
 */
export const getIDCardByNumber = async (cardNumber, coachingId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ID_CARDS),
      where('cardNumber', '==', cardNumber),
      where('coachingId', '==', coachingId),
      limit(1)
    );

    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { 
        success: true, 
        data: { id: doc.id, ...doc.data() } 
      };
    } else {
      return { success: false, error: 'ID card not found' };
    }
  } catch (error) {
    console.error('Error getting ID card by number:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Save or update an ID card
 */
export const saveIDCard = async (cardData) => {
  try {
    if (cardData.id) {
      // Update existing card
      const docRef = doc(db, COLLECTIONS.ID_CARDS, cardData.id);
      await updateDoc(docRef, {
        ...cardData,
        updatedAt: serverTimestamp()
      });
      return { success: true, data: { id: cardData.id, ...cardData } };
    } else {
      // Create new card
      const docRef = await addDoc(collection(db, COLLECTIONS.ID_CARDS), {
        ...cardData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, data: { id: docRef.id, ...cardData } };
    }
  } catch (error) {
    console.error('Error saving ID card:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Bulk generate ID cards
 */
export const bulkGenerateIDCards = async (cardsData) => {
  try {
    const batch = writeBatch(db);
    const generatedCards = [];

    cardsData.forEach(cardData => {
      const docRef = doc(collection(db, COLLECTIONS.ID_CARDS));
      batch.set(docRef, {
        ...cardData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      generatedCards.push({ id: docRef.id, ...cardData });
    });

    await batch.commit();
    return { success: true, data: generatedCards };
  } catch (error) {
    console.error('Error bulk generating ID cards:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete an ID card
 */
export const deleteIDCard = async (cardId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.ID_CARDS, cardId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting ID card:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update ID card status
 */
export const updateIDCardStatus = async (cardId, status) => {
  try {
    const docRef = doc(db, COLLECTIONS.ID_CARDS, cardId);
    await updateDoc(docRef, {
      status,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating ID card status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get ID cards for a specific student
 */
export const getStudentIDCards = async (studentId, coachingId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ID_CARDS),
      where('studentId', '==', studentId),
      where('coachingId', '==', coachingId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const cards = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: cards };
  } catch (error) {
    console.error('Error getting student ID cards:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Verify ID card by scanning QR code
 */
export const verifyIDCard = async (qrData) => {
  try {
    const { id, institute } = JSON.parse(qrData);
    
    const result = await getIDCardByNumber(id, institute);
    
    if (result.success && result.data) {
      const card = result.data;
      const now = new Date();
      const validUntil = new Date(card.validUntil);
      
      if (card.status === 'active' && validUntil > now) {
        return { 
          success: true, 
          valid: true,
          data: card,
          message: 'ID card is valid'
        };
      } else {
        return { 
          success: true, 
          valid: false,
          data: card,
          message: card.status !== 'active' ? 'ID card is inactive' : 'ID card has expired'
        };
      }
    } else {
      return { 
        success: false, 
        valid: false,
        message: 'ID card not found'
      };
    }
  } catch (error) {
    console.error('Error verifying ID card:', error);
    return { success: false, valid: false, error: error.message };
  }
};
